<template>
  <div class="bo">

    <!-- ─────────────────────────────────────────
         TOASTS
    ───────────────────────────────────────── -->
    <transition-group
      name="bo-toast"
      tag="div"
      :class="['bo-toasts', { 'bo-toasts--raised': selected.size > 0 }]"
    >
        <div
          v-for="t in toasts"
          :key="t.id"
          :class="['bo-toast', `bo-toast--${t.type}`]"
        >
          <span class="bo-toast__icon">
            {{ t.type === 'success' ? '✓' : t.type === 'error' ? '✕' : 'ℹ' }}
          </span>
          {{ t.message }}
        </div>
    </transition-group>

    <!-- ─────────────────────────────────────────
         MAIN
    ───────────────────────────────────────── -->
    <main class="bo-main">

      <!-- Navegación de vistas: Panel (activos) / Sin aprobar / Eliminados / Datos.
           Las tres primeras usan el mismo listado con otro `estado`: al cambiar
           se corta lo que esté cargando y se vacía la lista, así en memoria hay
           una sola sección por vez. "Datos" queda restringido a canAccessData. -->
      <div class="bo-container">
      <nav class="bo-viewnav" role="tablist" aria-label="Vistas del BackOffice">
        <button
          v-for="v in VISTAS"
          :key="v.key"
          type="button"
          role="tab"
          :aria-selected="activeView === v.key"
          :class="['bo-viewnav__tab', { 'bo-viewnav__tab--active': activeView === v.key }]"
          @click="cambiarVista(v.key)"
        >{{ v.label }}</button>
      </nav>
      </div>

      <!-- ═══════ VISTA: LISTADO (Panel / Sin aprobar / Eliminados) ═══════ -->
      <!-- v-if y no v-show: en "Datos" la tabla no queda dibujada de fondo. -->
      <div v-if="activeView !== 'data'" class="bo-view bo-view--dashboard">

      <!-- Cabecera (filtros + resumen): centrada a 1280px.
           El listado, en cambio, usa todo el ancho disponible. -->
      <div class="bo-container">

      <!-- ── DISPONIBILIDAD: abre el popup del rango de fechas ── -->
      <div class="bo-toolbar">
        <!-- Disponibilidad solo en Panel: un alojamiento eliminado o sin
             aprobar no se puede reservar, así que el cálculo no aporta nada. -->
        <button
          v-if="estadoActual === 'activos'"
          type="button"
          :class="['bo-availbtn', { 'bo-availbtn--on': isApplied }]"
          @click="openAvailModal"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="3" y="4.5" width="18" height="16" rx="2.5" />
            <path d="M3 9.5h18M8 2.5v4M16 2.5v4" />
          </svg>

          <span class="bo-availbtn__label">Disponibilidad</span>

          <span v-if="isApplied" class="bo-availbtn__range">{{ appliedRangeLabel }}</span>
          <span v-if="isApplied" class="bo-availbtn__count">
            {{ formatInt(availableTotal) }}/{{ formatInt(appliedScopeSize) }}
          </span>
          <span v-if="availabilityLoading" class="bo-spinner bo-spinner--dark"></span>
        </button>

        <button
          v-if="isApplied"
          type="button"
          class="bo-availbtn__clear"
          title="Quitar el filtro de fechas"
          @click="clearDateRange"
        >×</button>

        <!-- Los filtros cambiaron después de aplicar las fechas: lo que entró
             recién nunca se evaluó y muestra "-". El aviso vivía solo adentro
             del popup, o sea justo donde no se está mirando cuando pasa. -->
        <button
          v-if="isApplied && outOfScopeCount"
          type="button"
          class="bo-availbtn__redo"
          :disabled="availabilityLoading"
          :title="`Volver a calcular la disponibilidad de ${formatInt(outOfScopeCount)} unidades que entraron después`"
          @click="applyDateRange"
        >
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-2.6-6.4" /><path d="M21 3v6h-6" />
          </svg>
          {{ formatInt(outOfScopeCount) }} sin evaluar · Reaplicar
        </button>

        <!-- ── BUSCADOR GLOBAL: cruza el texto de todas las columnas ── -->
        <div :class="['bo-search', { 'bo-search--on': searchTokens.length }]">
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.6-3.6" />
          </svg>
          <input
            v-model="searchInput"
            type="text"
            class="bo-search__input"
            placeholder="Buscar en todas las columnas…"
            aria-label="Buscar en todas las columnas"
          />
          <span v-if="searchTokens.length" class="bo-search__count">
            {{ formatInt(filteredRows.length) }}
          </span>
          <button
            v-if="searchInput"
            type="button"
            class="bo-search__clear"
            title="Limpiar la búsqueda"
            @click="clearSearch"
          >×</button>
        </div>

        <!-- ── EXPORTAR: trabaja sobre lo que se está viendo ── -->
        <div class="bo-tools">
          <button
            type="button"
            :class="['bo-toolbtn', { 'bo-toolbtn--busy': exportando }]"
            :disabled="exportando || apiLoading || !serverTotal"
            :title="exportTitle"
            @click="abrirExportModal('filtro')"
          >
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span v-if="!exportando">Exportar Excel</span>
            <span v-else>Exportando… {{ exportPorcentaje }} %</span>
            <span v-if="!exportando && serverTotal" class="bo-toolbtn__count">{{ formatInt(serverTotal) }}</span>
            <span v-if="exportando" class="bo-spinner bo-spinner--dark"></span>
          </button>
          <button
            v-if="exportando"
            type="button"
            class="bo-availbtn__clear"
            title="Cancelar la exportación"
            aria-label="Cancelar la exportación"
            @click="cancelarExport"
          >×</button>
        </div>
      </div>

      <!-- ── RESUMEN ── -->
      <section class="bo-stats">
        <div class="bo-stat">
          <p class="bo-stat__label">Resultados</p>
          <p class="bo-stat__value">{{ formatInt(stats.resultados) }}</p>
        </div>
        <div class="bo-stat">
          <p class="bo-stat__label">Hosts distintos</p>
          <p class="bo-stat__value">{{ formatInt(stats.hosts) }}</p>
        </div>
        <div class="bo-stat">
          <p class="bo-stat__label">Precio promedio ({{ displayCurrency }})</p>
          <p class="bo-stat__value">{{ formatInt(stats.promedio) }}</p>
        </div>
        <div class="bo-stat">
          <p class="bo-stat__label">Precio más alto</p>
          <p class="bo-stat__value bo-stat__value--money" :title="maxPriceTitle">{{ maxPriceLabel }}</p>
        </div>
        <div v-if="isApplied" class="bo-stat">
          <p class="bo-stat__label">Disponibles en las fechas</p>
          <p class="bo-stat__value bo-stat__value--ok">{{ formatInt(stats.disponibles) }}</p>
        </div>
      </section>

      </div>
      <!-- ── FIN CABECERA ── -->

      <!-- ── TABLA DE ALOJAMIENTOS ── -->
      <section class="bo-grid" :class="{ 'bo-grid--with-bar': selected.size > 0 }">

        <div
          ref="gridScrollRef"
          class="bo-grid__scroll"
          @mousemove="onGridMouseMove"
          @mouseleave="stopEdgeScroll"
        >
          <table class="bo-grid__table" :style="{ minWidth: tableMinWidth + 'px' }">
            <colgroup>
              <col style="width: 48px" />
              <col v-for="c in COLUMNS" :key="c.key" :style="{ width: colWidth(c) + 'px' }" />
            </colgroup>
            <thead>
              <tr>
                <th class="bo-grid__check">
                  <input
                    type="checkbox"
                    :checked="allSelected"
                    :indeterminate.prop="someSelected && !allSelected"
                    @change="toggleAll"
                  />
                </th>
                <th
                  v-for="c in COLUMNS"
                  :key="c.key"
                  :class="[`bo-grid__th--${c.key}`, { 'bo-grid__th--num': c.numeric }]"
                  @mouseenter="onColEnter(c)"
                  @mouseleave="onColLeave(c)"
                >
                  <span class="bo-grid__th-inner">
                    <span class="bo-grid__th-label">{{ c.label }}</span>
                    <button
                      type="button"
                      :class="['bo-funnel', { 'bo-funnel--on': isColumnActive(c.key) }]"
                      :title="`Filtrar / ordenar por ${c.label}`"
                      @click.stop="openColumnMenu(c, $event)"
                    >
                      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                      </svg>
                    </button>
                    <span v-if="sort.key === c.key" class="bo-grid__sortmark">{{ sort.dir === 'asc' ? '↑' : '↓' }}</span>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="acc in visibleRows"
                :key="acc.id"
                :class="['bo-grid__row', { 'bo-grid__row--selected': selected.has(acc.id) }]"
              >
                <td class="bo-grid__check" @click.stop="toggleSelection(acc.id)">
                  <input type="checkbox" :checked="selected.has(acc.id)" @click.stop @change="toggleSelection(acc.id)" />
                </td>
                <td
                  v-for="c in COLUMNS"
                  :key="c.key"
                  :class="['bo-grid__td', `bo-grid__td--${c.key}`, { 'bo-grid__td--expanded': c.hoverWidth && hoveredCol === c.key }]"
                  @click="onCellClick(c, acc)"
                  @mouseenter="onColEnter(c)"
                  @mouseleave="onColLeave(c)"
                >
                  <!-- ID: link al alojamiento -->
                  <a
                    v-if="c.key === 'id'"
                    :href="`${siteUrl}/alojamiento/${acc.id}`"
                    target="_blank"
                    class="bo-grid__link"
                  >{{ acc.id }}</a>

                  <!-- Host: abre el perfil -->
                  <button
                    v-else-if="c.key === 'host'"
                    class="bo-grid__hostbtn"
                    :disabled="!acc.usuario_id"
                    :title="colText(c, acc)"
                  >{{ colText(c, acc) }}</button>

                  <!-- Segmento: pill B2B / B2C -->
                  <span
                    v-else-if="c.key === 'segmento'"
                    :class="['bo-seg', `bo-seg--${accSegment(acc)}`]"
                  >{{ colText(c, acc) }}</span>

                  <!-- Whats: logo -->
                  <span
                    v-else-if="c.key === 'whats'"
                    class="bo-whats"
                    :class="{ 'bo-whats--on': hostTieneGrupoWhatsapp(acc) }"
                    :title="hostTieneGrupoWhatsapp(acc) ? 'En grupo de WhatsApp' : 'Sin grupo de WhatsApp'"
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </span>

                  <!-- iCal -->
                  <span
                    v-else-if="c.key === 'ical'"
                    :class="['bo-ical', acc.ical ? 'bo-ical--yes' : 'bo-ical--no']"
                  >{{ acc.ical ? '✓ iCal' : '✕ Sin iCal' }}</span>

                  <!-- Proveedor del que viene la unidad -->
                  <span
                    v-else-if="c.key === 'proveedor'"
                    :class="['bo-provpill', `bo-provpill--${acc.proveedor || 'propio'}`]"
                  >{{ colText(c, acc) }}</span>

                  <!-- Disponibilidad en el rango de fechas elegido -->
                  <span
                    v-else-if="c.key === 'disponible'"
                    :class="[
                      acc.disponible == null
                        ? 'bo-grid__muted'
                        : ['bo-avpill', acc.disponible ? 'bo-avpill--yes' : 'bo-avpill--no'],
                      availabilityIsStale(acc) ? 'bo-avpill--stale' : ''
                    ]"
                    :title="availabilityTitle(acc)"
                  >{{ colText(c, acc) }}<template v-if="availabilityIsStale(acc)"> ⚠</template></span>

                  <!-- Actividad del host -->
                  <span
                    v-else-if="c.key === 'actividad'"
                    :class="hostClasificacion(acc) ? ['bo-pill', `bo-pill--${hostClasificacion(acc)}`] : 'bo-grid__muted'"
                  >{{ colText(c, acc) }}</span>

                  <!-- Resto: texto -->
                  <span v-else :title="colText(c, acc)">{{ colDisplay(c, acc) }}</span>
                </td>
              </tr>
              <tr v-if="!visibleRows.length">
                <td :colspan="COLUMNS.length + 1" class="bo-grid__empty">
                  {{ apiLoading ? 'Cargando alojamientos…' : 'Sin resultados' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Centinela de scroll infinito: al entrar en pantalla pide otra página -->
        <div ref="sentinelRef" class="bo-grid__sentinel">
          <span v-if="hasMore">
            Cargando más… ({{ formatInt(visibleRows.length) }} de {{ formatInt(serverTotal) }})
          </span>
          <span v-else-if="serverTotal">
            {{ formatInt(serverTotal) }} alojamiento{{ serverTotal !== 1 ? 's' : '' }}
          </span>
        </div>
      </section>

      </div>
      <!-- ═══════ FIN VISTA PANEL PRINCIPAL ═══════ -->

      <!-- ═══════ VISTA: DESCARGAR DATOS (ManageData integrado como sección interna) ═══════ -->
      <section v-if="activeView === 'data' && canAccessData" class="bo-view bo-view--data bo-container">
        <ManageData />
      </section>

    </main>

    <!-- ─────────────────────────────────────────
         POPUP EXPORTAR (elegir columnas)
         Mismo armazón que el de disponibilidad. La elección queda guardada
         en este navegador para la próxima exportación.
    ───────────────────────────────────────── -->
    <Transition name="bo-fade">
      <div v-if="exportModal.show" class="bo-modal-overlay bo-avail-overlay" @click.self="cerrarExportModal">
        <div class="bo-availmodal bo-exportmodal" role="dialog" aria-modal="true" aria-labelledby="bo-export-title">

          <div class="bo-availmodal__head">
            <span id="bo-export-title" class="bo-avail__eyebrow">Exportar a Excel</span>
            <button class="bo-modal__close" @click="cerrarExportModal" aria-label="Cerrar">×</button>
          </div>

          <div class="bo-availmodal__body">
            <p class="bo-exportmodal__intro">{{ exportModalResumen }}</p>

            <div class="bo-exportmodal__bar">
              <span class="bo-exportmodal__count">
                {{ exportColsSel.size }} de {{ opcionesExport.length }} columnas
              </span>
              <button type="button" class="bo-exportmodal__link" @click="marcarTodasExport(true)">Marcar todas</button>
              <button type="button" class="bo-exportmodal__link" @click="marcarTodasExport(false)">Desmarcar todas</button>
            </div>

            <fieldset class="bo-exportmodal__cols">
              <legend class="bo-sr-only">Columnas a exportar</legend>
              <label
                v-for="o in opcionesExport"
                :key="o.key"
                :class="['bo-exportmodal__col', { 'bo-exportmodal__col--on': exportColsSel.has(o.key) }]"
              >
                <input type="checkbox" :checked="exportColsSel.has(o.key)" @change="toggleColExport(o.key)" />
                <span>{{ o.label }}</span>
              </label>
            </fieldset>
          </div>

          <div class="bo-availmodal__footer">
            <button
              type="button"
              class="bo-avail__btn bo-avail__btn--ghost"
              @click="confirmarExport(true)"
            >Exportar todo</button>
            <button
              type="button"
              class="bo-avail__btn bo-avail__btn--apply"
              :disabled="!exportColsSel.size"
              @click="confirmarExport(false)"
            >Exportar {{ exportColsSel.size }} columna{{ exportColsSel.size !== 1 ? 's' : '' }}</button>
          </div>

        </div>
      </div>
    </Transition>
    <!-- ─────────────────────────────────────────
         POPUP DISPONIBILIDAD (rango de fechas)
         El rango se calcula sólo sobre las unidades que dejaron pasar el
         resto de los filtros: se aplica con el botón, no al tipear.
    ───────────────────────────────────────── -->
    <Transition name="bo-fade">
      <div v-if="availModal" class="bo-modal-overlay bo-avail-overlay" @click.self="closeAvailModal">
        <div class="bo-availmodal" role="dialog" aria-modal="true" aria-label="Disponibilidad por fechas">

          <div class="bo-availmodal__head">
            <span class="bo-avail__eyebrow">Disponibilidad por fechas</span>
            <button class="bo-modal__close" @click="closeAvailModal" aria-label="Cerrar">×</button>
          </div>

          <div class="bo-availmodal__body">
            <div class="bo-avail__row">
              <div class="bo-avail__field">
                <label class="bo-avail__label" for="bo-date-start">Desde</label>
                <input
                  id="bo-date-start"
                  type="date"
                  class="bo-avail__input"
                  :max="dateRange.end || null"
                  v-model="dateRange.start"
                  @keyup.enter="applyDateRange"
                />
              </div>

              <span class="bo-avail__arrow" aria-hidden="true">→</span>

              <div class="bo-avail__field">
                <label class="bo-avail__label" for="bo-date-end">Hasta</label>
                <input
                  id="bo-date-end"
                  type="date"
                  class="bo-avail__input"
                  :min="dateRange.start || null"
                  v-model="dateRange.end"
                  @keyup.enter="applyDateRange"
                />
              </div>
            </div>

            <div class="bo-availmodal__state">
              <span v-if="availabilityLoading" class="bo-avail__chip bo-avail__chip--wait">
                <span class="bo-spinner bo-spinner--dark"></span> Calculando…
              </span>
              <span v-else-if="pendingApply" class="bo-avail__chip bo-avail__chip--pending">Sin aplicar</span>
              <span v-else-if="isApplied" class="bo-avail__chip bo-avail__chip--ok">
                {{ formatInt(availableTotal) }} disponible{{ availableTotal !== 1 ? 's' : '' }}
                de {{ formatInt(appliedScopeSize) }}
              </span>

              <label v-if="isApplied" class="bo-avail__toggle">
                <input type="checkbox" v-model="onlyAvailable" />
                Sólo disponibles
              </label>
            </div>

            <p class="bo-avail__note">
              <template v-if="isApplied">
                Aplicado a {{ formatInt(appliedScopeSize) }} unidad{{ appliedScopeSize !== 1 ? 'es' : '' }}
                · {{ appliedRangeLabel }} · {{ formatInt(appliedNights) }} noche{{ appliedNights !== 1 ? 's' : '' }}
                <span v-if="pendingApply" class="bo-avail__warn">
                  — cambiaste el rango, aplicá para actualizar
                </span>
                <span v-else-if="outOfScopeCount" class="bo-avail__warn">
                  — los filtros cambiaron: {{ formatInt(outOfScopeCount) }} unidad{{ outOfScopeCount !== 1 ? 'es' : '' }}
                  sin evaluar, reaplicá para incluirlas
                </span>
              </template>
              <template v-else-if="hasDateRange">
                Se aplicará a las {{ formatInt(scopeSize) }} unidad{{ scopeSize !== 1 ? 'es' : '' }} del filtro actual
                · {{ formatInt(rangeNights) }} noche{{ rangeNights !== 1 ? 's' : '' }}
              </template>
              <template v-else>
                Elegí un rango y aplicá: la disponibilidad se calcula sobre las unidades que quedaron en el filtro,
                no sobre todo el inventario.
              </template>
            </p>
          </div>

          <div class="bo-availmodal__footer">
            <button
              v-if="dateRange.start || dateRange.end || isApplied"
              type="button"
              class="bo-avail__btn bo-avail__btn--ghost"
              :disabled="availabilityLoading"
              @click="clearDateRange"
            >Limpiar</button>

            <button
              type="button"
              class="bo-avail__btn bo-avail__btn--apply"
              :disabled="!hasDateRange || availabilityLoading"
              @click="applyDateRange"
            >{{ isApplied && !pendingApply ? 'Reaplicar' : 'Aplicar' }}</button>
          </div>

        </div>
      </div>
    </Transition>

    <!-- ─────────────────────────────────────────
         OVERLAY DE CARGA (disponibilidad)
         Aplicar un rango vuelve a pedir el inventario entero y lo reprocesa:
         son varios segundos en los que antes la pantalla parecía colgada y la
         gente seguía clickeando encima. El overlay tapa la vista, cuenta el
         tiempo y deja salir sin recargar la página.
    ───────────────────────────────────────── -->
    <Transition name="bo-fade">
      <div v-if="availabilityLoading" class="bo-loading-overlay" aria-busy="true">
        <div class="bo-loading-card" role="dialog" aria-modal="true" aria-label="Calculando disponibilidad">
          <span class="bo-loading-ring" aria-hidden="true"></span>

          <p class="bo-loading-title">Calculando disponibilidad</p>
          <!-- Sólo la etapa se anuncia: el contador de segundos como live region
               haría que el lector de pantalla hablara una vez por segundo. -->
          <p class="bo-loading-stage" role="status" aria-live="polite">{{ loadingStage }}</p>

          <div :class="['bo-loading-bar', { 'bo-loading-bar--indef': loadingProgress === null }]">
            <div
              class="bo-loading-bar__fill"
              :style="loadingProgress === null ? null : { width: loadingProgress + '%' }"
            ></div>
          </div>

          <p class="bo-loading-hint" aria-hidden="true">
            {{ loadingElapsed }} s transcurrido{{ loadingElapsed !== 1 ? 's' : '' }}
            <template v-if="loadingElapsed >= AVISO_LENTO_S">
              — con el inventario completo la consulta es lenta; acotá los filtros antes de aplicar las fechas.
            </template>
            <template v-else>
              — no cierres la pestaña.
            </template>
          </p>

          <button type="button" class="bo-loading-cancel" @click="cancelAvailability">
            Cancelar
          </button>
        </div>
      </div>
    </Transition>

    <!-- ─────────────────────────────────────────
         MODAL PERFIL DEL HOST
    ───────────────────────────────────────── -->
    <Transition name="bo-fade">
      <div v-if="hostProfileModal.show && hostProfileModal.host" class="bo-modal-overlay bo-host-overlay" @click.self="closeHostProfile">
        <div class="bo-host-modal" role="dialog" aria-modal="true">

          <!-- Header -->
          <div class="bo-host-modal__header">
            <div class="bo-host-modal__header-left">
              <div class="bo-host-avatar">
                <span class="bo-host-avatar__initials">
                  {{ (hostProfileModal.host.nombre || '?')[0].toUpperCase() }}{{ hostProfileModal.host.apellido ? hostProfileModal.host.apellido[0].toUpperCase() : '' }}
                </span>
              </div>
              <div>
                <h3 class="bo-host-modal__title">{{ hostProfileModal.host.nombre }} {{ hostProfileModal.host.apellido || '' }}</h3>
                <div class="bo-host-modal__header-meta">
                  <span :class="['bo-pill', `bo-pill--${hostProfileModal.host.clasificacion}`]">{{ hostProfileModal.host.clasificacion }}</span>
                  <span class="bo-host-modal__id-badge">#{{ hostProfileModal.host.id }}</span>
                </div>
              </div>
            </div>
            <button class="bo-modal__close" @click="closeHostProfile" aria-label="Cerrar">×</button>
          </div>

          <!-- Body -->
          <div class="bo-host-modal__body">

            <!-- ── IZQUIERDA: datos del host ── -->
            <div class="bo-host-modal__left">
              <div class="bo-host-modal__section-head">
                <p class="bo-host-modal__section-label">Información del host</p>
                <button
                  v-if="canEditHost && !hostProfileModal.editingProfile"
                  type="button"
                  class="bo-host-edit-btn"
                  @click="startEditProfile"
                >✎ Editar</button>
              </div>
              <div v-if="!hostProfileModal.editingProfile" class="bo-host-card__info">
                <div class="bo-host-card__row" v-if="hostProfileModal.host.email">
                  <span class="bo-host-card__label">Email</span>
                  <span class="bo-host-card__value bo-host-card__value--email">{{ hostProfileModal.host.email }}</span>
                </div>
                <div class="bo-host-card__row" v-if="hostProfileModal.host.telefono">
                  <span class="bo-host-card__label">Teléfono</span>
                  <span class="bo-host-card__value">{{ hostProfileModal.host.telefono }}</span>
                </div>
                <div class="bo-host-card__row" v-if="hostProfileModal.host.pais">
                  <span class="bo-host-card__label">País</span>
                  <span class="bo-host-card__value">{{ hostProfileModal.host.pais }}</span>
                </div>
                <div class="bo-host-card__row" v-if="hostProfileModal.host.fecha_registro">
                  <span class="bo-host-card__label">Registro</span>
                  <span class="bo-host-card__value">{{ new Date(hostProfileModal.host.fecha_registro).toLocaleDateString('es-CL') }}</span>
                </div>
                <div class="bo-host-card__row" v-if="hostProfileModal.host.ultimo_login">
                  <span class="bo-host-card__label">Último acceso</span>
                  <span class="bo-host-card__value">{{ new Date(hostProfileModal.host.ultimo_login).toLocaleDateString('es-CL') }}</span>
                </div>
                <div class="bo-host-card__row">
                  <span class="bo-host-card__label">Estado</span>
                  <span :class="['bo-pill', `bo-pill--${hostProfileModal.host.clasificacion}`]">{{ hostProfileModal.host.clasificacion }}</span>
                </div>
                <div class="bo-host-card__row">
                  <span class="bo-host-card__label">Alojamientos</span>
                  <span class="bo-host-card__value--count">{{ hostProfileModal.accommodations.length }}</span>
                </div>
              </div>

              <!-- Formulario de edición del perfil (solo superadmins) -->
              <div v-if="hostProfileModal.editingProfile" class="bo-host-edit-form">
                <div class="bo-edit-row bo-edit-row--2">
                  <label class="bo-edit-field">
                    <span class="bo-edit-label">Nombre</span>
                    <input v-model="hostProfileModal.profileForm.nombre" type="text" class="bo-edit-input" />
                  </label>
                  <label class="bo-edit-field">
                    <span class="bo-edit-label">Apellido</span>
                    <input v-model="hostProfileModal.profileForm.apellido" type="text" class="bo-edit-input" />
                  </label>
                </div>
                <div class="bo-edit-row">
                  <label class="bo-edit-field">
                    <span class="bo-edit-label">Email (no editable)</span>
                    <input :value="hostProfileModal.host.email" type="text" class="bo-edit-input" disabled />
                  </label>
                </div>
                <div class="bo-edit-row bo-edit-row--2">
                  <label class="bo-edit-field">
                    <span class="bo-edit-label">Teléfono</span>
                    <input v-model="hostProfileModal.profileForm.telefono" type="text" class="bo-edit-input" />
                  </label>
                  <label class="bo-edit-field">
                    <span class="bo-edit-label">Fecha de nacimiento</span>
                    <input v-model="hostProfileModal.profileForm.fecha_nacimiento" type="date" class="bo-edit-input" />
                  </label>
                </div>
                <div class="bo-edit-row bo-edit-row--2">
                  <label class="bo-edit-field">
                    <span class="bo-edit-label">País</span>
                    <select v-model="hostProfileModal.profileForm.pais_idpais" class="bo-edit-input">
                      <option value="" disabled>Seleccionar…</option>
                      <option v-for="p in countriesList" :key="p.idpais" :value="p.idpais">{{ p.nombre }}</option>
                    </select>
                  </label>
                  <label class="bo-edit-field">
                    <span class="bo-edit-label">Sexo</span>
                    <select v-model="hostProfileModal.profileForm.sexo" class="bo-edit-input">
                      <option value="" disabled>Seleccionar…</option>
                      <option value="m">Masculino</option>
                      <option value="f">Femenino</option>
                    </select>
                  </label>
                </div>
                <div class="bo-edit-row">
                  <label class="bo-edit-field">
                    <span class="bo-edit-label">Biografía</span>
                    <textarea v-model="hostProfileModal.profileForm.biografia" rows="3" class="bo-edit-input bo-edit-textarea"></textarea>
                  </label>
                </div>
                <div class="bo-edit-actions">
                  <button type="button" class="bo-edit-btn bo-edit-btn--ghost" :disabled="hostProfileModal.savingProfile" @click="cancelEditProfile">Cancelar</button>
                  <button type="button" class="bo-edit-btn bo-edit-btn--primary" :disabled="hostProfileModal.savingProfile" @click="saveHostProfile">
                    {{ hostProfileModal.savingProfile ? 'Guardando…' : 'Guardar perfil' }}
                  </button>
                </div>
              </div>

              <div class="bo-host-stat-grid">
                <div class="bo-host-stat">
                  <span class="bo-host-stat__num">{{ hostProfileModal.accommodations.filter(a => a.ical).length }}</span>
                  <span class="bo-host-stat__label">Con iCal</span>
                </div>
                <div class="bo-host-stat">
                  <span class="bo-host-stat__num">{{ hostProfileModal.accommodations.filter(a => a.quality === 'bueno').length }}</span>
                  <span class="bo-host-stat__label">Buen inventario</span>
                </div>
                <div class="bo-host-stat">
                  <span class="bo-host-stat__num bo-host-stat__num--warn">{{ hostProfileModal.accommodations.filter(a => a.quality === 'malo').length }}</span>
                  <span class="bo-host-stat__label">Mal inventario</span>
                </div>
              </div>

              <!-- Puntuación del anfitrión -->
              <p class="bo-host-modal__section-label" style="margin-top:4px;">Puntuación</p>
              <div class="bo-host-rating">
                <button
                  v-for="n in 4"
                  :key="n"
                  type="button"
                  :class="['bo-host-rating__star', { 'bo-host-rating__star--on': n <= hostProfileModal.rating }]"
                  :disabled="hostProfileModal.savingRating"
                  :aria-label="`${n} estrella${n > 1 ? 's' : ''}`"
                  @click="setHostRating(n)"
                >★</button>
                <span v-if="hostProfileModal.rating > 0" class="bo-host-rating__value">{{ hostProfileModal.rating }}/4</span>
                <span v-else class="bo-host-rating__value bo-host-rating__value--empty">Sin puntuar</span>
              </div>

              <!-- Grupo de WhatsApp: logo en gris; al hacer clic pasa a color = tiene grupo -->
              <div class="bo-host-wa">
                <button
                  type="button"
                  :class="['bo-host-wa__btn', { 'bo-host-wa__btn--on': hostProfileModal.tieneGrupoWhatsapp }]"
                  :disabled="hostProfileModal.savingWhatsapp"
                  :aria-pressed="hostProfileModal.tieneGrupoWhatsapp ? 'true' : 'false'"
                  :title="hostProfileModal.tieneGrupoWhatsapp ? 'Está en un grupo de WhatsApp' : 'Sin grupo de WhatsApp'"
                  @click="toggleHostWhatsappGroup"
                >
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </button>
                <span class="bo-host-wa__label">
                  {{ hostProfileModal.tieneGrupoWhatsapp ? 'En grupo de WhatsApp' : 'Sin grupo de WhatsApp' }}
                </span>
              </div>

              <!-- Datos bancarios -->
              <div class="bo-host-modal__section-head" style="margin-top:4px;">
                <p class="bo-host-modal__section-label">Datos bancarios</p>
                <button
                  v-if="canEditHost && !hostProfileModal.editingBank"
                  type="button"
                  class="bo-host-edit-btn"
                  @click="startEditBank"
                >✎ Editar</button>
              </div>
              <div v-if="!hostProfileModal.editingBank && hostHasBankData(hostProfileModal.host)" class="bo-host-card__info">
                <div class="bo-host-card__row" v-if="hostProfileModal.host.bank_account.bank_name">
                  <span class="bo-host-card__label">Banco</span>
                  <span class="bo-host-card__value">{{ hostProfileModal.host.bank_account.bank_name }}</span>
                </div>
                <div class="bo-host-card__row" v-if="hostProfileModal.host.bank_account.first_name || hostProfileModal.host.bank_account.last_name">
                  <span class="bo-host-card__label">Titular</span>
                  <span class="bo-host-card__value">{{ [hostProfileModal.host.bank_account.first_name, hostProfileModal.host.bank_account.last_name].filter(Boolean).join(' ') }}</span>
                </div>
                <div class="bo-host-card__row" v-if="hostProfileModal.host.bank_account.bank_account_number">
                  <span class="bo-host-card__label">Nº Cuenta</span>
                  <span class="bo-host-card__value bo-host-card__value--mono">{{ hostProfileModal.host.bank_account.bank_account_number }}</span>
                </div>
                <div class="bo-host-card__row" v-if="hostProfileModal.host.bank_account.bank_code">
                  <span class="bo-host-card__label">Código</span>
                  <span class="bo-host-card__value bo-host-card__value--mono">{{ hostProfileModal.host.bank_account.bank_code }}</span>
                </div>
                <div class="bo-host-card__row" v-if="hostProfileModal.host.bank_account.branch_code">
                  <span class="bo-host-card__label">Sucursal</span>
                  <span class="bo-host-card__value bo-host-card__value--mono">{{ hostProfileModal.host.bank_account.branch_code }}</span>
                </div>
                <div class="bo-host-card__row" v-if="hostProfileModal.host.bank_account.government_id">
                  <span class="bo-host-card__label">ID Gov.</span>
                  <span class="bo-host-card__value">{{ hostProfileModal.host.bank_account.government_id }}</span>
                </div>
                <div class="bo-host-card__row" v-if="hostProfileModal.host.bank_account.bank_address">
                  <span class="bo-host-card__label">Dirección</span>
                  <span class="bo-host-card__value">{{ hostProfileModal.host.bank_account.bank_address }}</span>
                </div>
              </div>
              <div v-else-if="!hostProfileModal.editingBank" class="bo-host-bank-empty">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                <span>Sin datos bancarios</span>
              </div>

              <!-- Formulario de edición de datos bancarios (solo superadmins) -->
              <div v-if="hostProfileModal.editingBank" class="bo-host-edit-form">
                <div class="bo-edit-row bo-edit-row--2">
                  <label class="bo-edit-field">
                    <span class="bo-edit-label">Nombre titular</span>
                    <input v-model="hostProfileModal.bankForm.first_name" type="text" class="bo-edit-input" />
                  </label>
                  <label class="bo-edit-field">
                    <span class="bo-edit-label">Apellido titular</span>
                    <input v-model="hostProfileModal.bankForm.last_name" type="text" class="bo-edit-input" />
                  </label>
                </div>
                <div class="bo-edit-row bo-edit-row--2">
                  <label class="bo-edit-field">
                    <span class="bo-edit-label">ID Gobierno</span>
                    <input v-model="hostProfileModal.bankForm.government_id" type="text" class="bo-edit-input" />
                  </label>
                  <label class="bo-edit-field">
                    <span class="bo-edit-label">Teléfono</span>
                    <input v-model="hostProfileModal.bankForm.phone" type="text" class="bo-edit-input" />
                  </label>
                </div>
                <div class="bo-edit-row">
                  <label class="bo-edit-field">
                    <span class="bo-edit-label">Email</span>
                    <input v-model="hostProfileModal.bankForm.email" type="text" class="bo-edit-input" />
                  </label>
                </div>
                <div class="bo-edit-row bo-edit-row--2">
                  <label class="bo-edit-field">
                    <span class="bo-edit-label">Nº Cuenta</span>
                    <input v-model="hostProfileModal.bankForm.bank_account_number" type="text" class="bo-edit-input" />
                  </label>
                  <label class="bo-edit-field">
                    <span class="bo-edit-label">Código banco</span>
                    <input v-model="hostProfileModal.bankForm.bank_code" type="text" class="bo-edit-input" />
                  </label>
                </div>
                <div class="bo-edit-row bo-edit-row--2">
                  <label class="bo-edit-field">
                    <span class="bo-edit-label">Nombre banco</span>
                    <input v-model="hostProfileModal.bankForm.bank_name" type="text" class="bo-edit-input" />
                  </label>
                  <label class="bo-edit-field">
                    <span class="bo-edit-label">Sucursal</span>
                    <input v-model="hostProfileModal.bankForm.branch_code" type="text" class="bo-edit-input" />
                  </label>
                </div>
                <div class="bo-edit-row">
                  <label class="bo-edit-field">
                    <span class="bo-edit-label">Dirección banco</span>
                    <input v-model="hostProfileModal.bankForm.bank_address" type="text" class="bo-edit-input" />
                  </label>
                </div>
                <div class="bo-edit-row">
                  <label class="bo-edit-field">
                    <span class="bo-edit-label">Mensaje / notas</span>
                    <textarea v-model="hostProfileModal.bankForm.message" rows="2" class="bo-edit-input bo-edit-textarea"></textarea>
                  </label>
                </div>
                <div class="bo-edit-actions">
                  <button type="button" class="bo-edit-btn bo-edit-btn--ghost" :disabled="hostProfileModal.savingBank" @click="cancelEditBank">Cancelar</button>
                  <button type="button" class="bo-edit-btn bo-edit-btn--primary" :disabled="hostProfileModal.savingBank" @click="saveHostBankData">
                    {{ hostProfileModal.savingBank ? 'Guardando…' : 'Guardar datos bancarios' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- ── DERECHA: lista de alojamientos ── -->
            <div class="bo-host-modal__right">
              <p class="bo-host-modal__section-label">
                Alojamientos
                <span class="bo-host-modal__count-badge">{{ hostProfileModal.accommodations.length }}</span>
              </p>

              <!-- Acciones masivas (solo Sin aprobar / Eliminados). "Todos" lo
                   resuelve la API por host_id: incluye también los que todavía
                   no se cargaron en la tabla. -->
              <div v-if="estadoActual !== 'activos' && hostPendientes > 0" class="bo-host-actions">
                <span class="bo-host-actions__label">
                  {{ formatInt(hostPendientes) }} {{ estadoActual === 'eliminados' ? (hostPendientes !== 1 ? 'eliminados' : 'eliminado') : 'sin aprobar' }} en total
                </span>
                <button
                  type="button"
                  class="bo-host-actions__btn bo-host-actions__btn--approve"
                  :disabled="!!hostProfileModal.accion"
                  @click="accionAlojamientos('approve', null)"
                >
                  <span v-if="hostProfileModal.accion === 'approve-all'" class="bo-spinner"></span>
                  ✓ Aprobar todos ({{ formatInt(hostPendientes) }})
                </button>
                <button
                  v-if="estadoActual === 'sin_aprobar'"
                  type="button"
                  class="bo-host-actions__btn bo-host-actions__btn--delete"
                  :disabled="!!hostProfileModal.accion"
                  @click="accionAlojamientos('delete', null)"
                >
                  <span v-if="hostProfileModal.accion === 'delete-all'" class="bo-spinner"></span>
                  ✕ Eliminar todos ({{ formatInt(hostPendientes) }})
                </button>
              </div>

              <div v-if="hostProfileModal.accommodations.length === 0" class="bo-host-modal__empty">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="margin-bottom:12px;opacity:.4"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                <p v-if="estadoActual === 'activos'">Este host no tiene alojamientos registrados</p>
                <p v-else-if="hostPendientes > 0">Los alojamientos de este host todavía no se cargaron en la tabla. Puedes usar los botones de arriba igualmente.</p>
                <p v-else>No le quedan alojamientos {{ estadoActual === 'eliminados' ? 'eliminados' : 'sin aprobar' }}</p>
              </div>

              <div v-else class="bo-host-acc-list">
                <div
                  v-for="acc in hostProfileModal.accommodations"
                  :key="acc.id"
                  class="bo-host-acc-card"
                >
                  <!-- Galería de imágenes -->
                  <div class="bo-host-acc-card__img-wrap">
                    <template v-if="getAccommodationImages(acc).length > 0">
                      <transition name="bo-img-fade" mode="out-in">
                        <img
                          :key="getGalleryIndex(acc.id)"
                          :src="getAccommodationImages(acc)[getGalleryIndex(acc.id)]"
                          :alt="parseAccommodationName(acc.nombre)"
                          class="bo-host-acc-card__img"
                          @error="$event.target.style.display = 'none'"
                        />
                      </transition>
                      <!-- Controles si hay más de 1 imagen -->
                      <template v-if="getAccommodationImages(acc).length > 1">
                        <button class="bo-gallery-btn bo-gallery-btn--prev" @click.stop="prevGalleryImage(acc)" aria-label="Imagen anterior">&#8249;</button>
                        <button class="bo-gallery-btn bo-gallery-btn--next" @click.stop="nextGalleryImage(acc)" aria-label="Imagen siguiente">&#8250;</button>
                        <div class="bo-gallery-counter">{{ getGalleryIndex(acc.id) + 1 }}/{{ getAccommodationImages(acc).length }}</div>
                        <div class="bo-gallery-dots">
                          <span
                            v-for="(_, i) in getAccommodationImages(acc).slice(0, 8)"
                            :key="i"
                            :class="['bo-gallery-dot', { 'bo-gallery-dot--active': i === getGalleryIndex(acc.id) }]"
                            @click.stop="setGalleryIndex(acc.id, i)"
                          ></span>
                        </div>
                      </template>
                    </template>
                    <div v-else class="bo-host-acc-card__img-placeholder">
                      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                    </div>
                    <div class="bo-host-acc-card__quality-dot" :class="`bo-host-acc-card__quality-dot--${acc.quality}`"></div>
                  </div>

                  <!-- Info -->
                  <div class="bo-host-acc-card__info">
                    <a
                      :href="`${siteUrl}/alojamiento/${acc.id}`"
                      target="_blank"
                      class="bo-host-acc-card__name"
                    >
                      {{ parseAccommodationName(acc.nombre) || 'Alojamiento #' + acc.id }}
                    </a>

                    <div class="bo-host-acc-card__chips">
                      <span class="bo-chip" v-if="acc.tipo">{{ acc.tipo }}</span>
                      <span class="bo-chip bo-chip--gray" v-if="acc.categoria">{{ acc.categoria }}</span>
                      <span :class="['bo-ical', acc.ical ? 'bo-ical--yes' : 'bo-ical--no']" style="font-size:.75rem;">
                        {{ acc.ical ? '✓ iCal' : '✕ Sin iCal' }}
                      </span>
                    </div>

                    <div class="bo-host-acc-card__price">
                      <span class="bo-host-acc-card__price-main">
                        {{ convertAndFormat(acc.arriendo_total || acc.precio, acc.moneda) }}<span class="bo-host-acc-card__price-period">/mes</span>
                      </span>
                      <span v-if="acc.arriendo_diario && Number(acc.arriendo_diario) > 0" class="bo-host-acc-card__price-day">
                        {{ convertAndFormat(acc.arriendo_diario, acc.moneda) }}/día
                      </span>
                    </div>

                    <div class="bo-host-acc-card__features">
                      <span v-if="acc.maximo_huespedes || acc.capacidad_huespedes" class="bo-host-acc-card__feat">
                        👥 {{ acc.maximo_huespedes || acc.capacidad_huespedes }} huéspedes
                      </span>
                      <span v-if="acc.ciudad" class="bo-host-acc-card__feat">
                        📍 {{ acc.ciudad }}{{ acc.pais ? ', ' + acc.pais : '' }}
                      </span>
                      <span v-if="accAddress(acc)" class="bo-host-acc-card__feat" :title="accAddress(acc)">
                        🏠 {{ accAddress(acc) }}
                      </span>
                    </div>
                  </div>

                  <!-- ID + botón Ver -->
                  <div class="bo-host-acc-card__aside">
                    <span class="bo-host-acc-card__aside-id">#{{ acc.id }}</span>
                    <a
                      :href="`${siteUrl}/alojamiento/${acc.id}`"
                      target="_blank"
                      class="bo-host-acc-card__ver-btn"
                    >Ver</a>
                    <template v-if="estadoActual !== 'activos'">
                      <button
                        type="button"
                        class="bo-host-acc-card__act bo-host-acc-card__act--approve"
                        :disabled="!!hostProfileModal.accion"
                        :title="estadoActual === 'eliminados' ? 'Restaurar y aprobar' : 'Aprobar'"
                        @click="accionAlojamientos('approve', acc)"
                      >{{ hostProfileModal.accion === `approve-${acc.id}` ? '…' : 'Aprobar' }}</button>
                      <button
                        v-if="estadoActual === 'sin_aprobar'"
                        type="button"
                        class="bo-host-acc-card__act bo-host-acc-card__act--delete"
                        :disabled="!!hostProfileModal.accion"
                        @click="accionAlojamientos('delete', acc)"
                      >{{ hostProfileModal.accion === `delete-${acc.id}` ? '…' : 'Eliminar' }}</button>
                    </template>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </Transition>

    <!-- ─────────────────────────────────────────
         MENÚ DE COLUMNA (ordenar + filtrar por valores)
    ───────────────────────────────────────── -->
    <Transition name="bo-fade">
      <div v-if="menu.col" class="bo-colmenu-backdrop" @click="closeColumnMenu"></div>
    </Transition>
    <Transition name="bo-fade">
    <div
      v-if="menu.col"
      :class="['bo-colmenu', { 'bo-colmenu--sheet': menu.sheet }]"
      :style="colMenuStyle"
      @click.stop
    >
      <!-- En celular el menú se abre como hoja inferior y muestra qué columna filtra. -->
      <p v-if="menu.sheet" class="bo-colmenu__sheet-head">
        {{ menu.col.label }}
        <button type="button" class="bo-colmenu__sheet-close" @click="closeColumnMenu" aria-label="Cerrar">×</button>
      </p>
      <button type="button" class="bo-colmenu__sort" @click="sortColumn('asc')">Ordenar De la A a la Z</button>
      <button type="button" class="bo-colmenu__sort" @click="sortColumn('desc')">Ordenar De la Z a la A</button>

      <!-- La columna Nombre sólo ordena: no tiene filtro por valores. -->
      <template v-if="menu.col.filterable !== false">
        <div class="bo-colmenu__sep"></div>

        <p class="bo-colmenu__title">Filtrar por valores</p>
        <div class="bo-colmenu__toolbar">
          <button type="button" class="bo-colmenu__link" @click="selectAllOptions">{{ menuSelectLabel }}</button>
          <button type="button" class="bo-colmenu__link" @click="clearOptions">Borrar</button>
          <span class="bo-colmenu__shown">{{ formatInt(menu.draft.size) }} marcado{{ menu.draft.size !== 1 ? 's' : '' }}</span>
        </div>

        <div class="bo-colmenu__search">
          <input
            v-model="menu.search"
            type="text"
            :placeholder="menuSearchPlaceholder"
            class="bo-colmenu__input"
          />
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.6-3.6" />
          </svg>
        </div>

        <div class="bo-colmenu__list">
          <label v-for="opt in menuVisibleOptions" :key="opt" class="bo-colmenu__opt">
            <input type="checkbox" :checked="menu.draft.has(opt)" @change="toggleOption(opt)" />
            <span class="bo-colmenu__opt-text" :title="opt">{{ opt || '(vacío)' }}</span>
          </label>
          <p v-if="!menuVisibleOptions.length" class="bo-colmenu__nores">{{ menuEmptyText }}</p>
          <p v-else-if="menuFilteredOptions.length > menuVisibleOptions.length" class="bo-colmenu__nores">
            +{{ formatInt(menuFilteredOptions.length - menuVisibleOptions.length) }} valores más — refiná la búsqueda
          </p>
        </div>

        <div class="bo-colmenu__footer">
          <button type="button" class="bo-colmenu__btn" @click="closeColumnMenu">Cancelar</button>
          <button type="button" class="bo-colmenu__btn bo-colmenu__btn--primary" @click="applyColumnMenu">Aceptar</button>
        </div>
      </template>
    </div>
    </Transition>

    <!-- ─────────────────────────────────────────
         BARRA DE ACCIONES (selección)
    ───────────────────────────────────────── -->
    <Transition name="bo-slide-up">
      <div v-if="selected.size > 0" class="bo-actionbar">
        <span class="bo-actionbar__count">
          {{ formatInt(selected.size) }} unidad{{ selected.size !== 1 ? 'es' : '' }} seleccionada{{ selected.size !== 1 ? 's' : '' }}
        </span>
        <div class="bo-actionbar__btns">
          <button type="button" class="bo-actionbar__btn" @click="abrirExportModal('seleccion')">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Exportar Excel
          </button>
          <button type="button" class="bo-actionbar__btn" @click="copySelectedLinks">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
            Copiar enlaces
          </button>
          <button type="button" class="bo-actionbar__btn" title="Contactar por WhatsApp" @click="contactSelected">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
              <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.9-4.45 9.9-9.91C21.95 6.45 17.5 2 12.04 2m5.8 14.16c-.24.68-1.42 1.31-1.97 1.39-.5.08-1.14.11-1.84-.12-.42-.13-.97-.31-1.67-.61-2.94-1.27-4.86-4.23-5-4.43-.15-.2-1.2-1.59-1.2-3.03s.76-2.15 1.03-2.44c.27-.3.58-.37.78-.37.19 0 .39 0 .56.01.18.01.42-.07.66.5.24.58.83 2.01.9 2.16.07.15.12.32.02.52-.1.2-.15.32-.29.5-.15.17-.31.38-.44.51-.15.15-.3.31-.13.6.17.3.76 1.25 1.63 2.03 1.12 1 2.06 1.31 2.36 1.46.3.15.47.12.64-.07.17-.2.74-.86.94-1.16.2-.3.39-.25.66-.15.27.1 1.7.8 1.99.95.29.15.48.22.55.34.07.13.07.72-.17 1.41" />
            </svg>
            Contactar
          </button>
          <button type="button" class="bo-actionbar__btn" title="Contactar por correo" @click="openEmailModal">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="2.5 6 12 13 21.5 6" />
            </svg>
            Contactar
          </button>
        </div>
      </div>
    </Transition>

    <!-- ─────────────────────────────────────────
         MODAL PUSH / EMAIL
    ───────────────────────────────────────── -->
    <Transition name="bo-fade">
      <div v-if="emailModal" class="bo-modal-overlay" @click.self="closeEmailModal">
        <div class="bo-modal bo-modal--wide" role="dialog" aria-modal="true">

          <div class="bo-modal__header">
            <h3 class="bo-modal__title">
              Push / Email
              <span class="bo-modal__subtitle">
                {{ formatInt(pushEmail.recipients.length) }} destinatario{{ pushEmail.recipients.length !== 1 ? 's' : '' }}
              </span>
            </h3>
            <button class="bo-modal__close" @click="closeEmailModal" aria-label="Cerrar">×</button>
          </div>

          <div class="bo-modal__body">
            <div class="bo-email-panel">

              <!-- Editor -->
              <div class="bo-email-panel__editor">
                <div class="bo-field">
                  <label class="bo-label">Asunto</label>
                  <input v-model="pushEmail.subject" type="text" class="bo-input" placeholder="Asunto del mensaje…" />
                </div>
                <div class="bo-field">
                  <label class="bo-label">Mensaje</label>
                  <div class="bo-rich-toolbar">
                    <button type="button" class="bo-rich-btn" title="Negrita" @click="wrapText('**', '**')"><b>B</b></button>
                    <button type="button" class="bo-rich-btn" title="Cursiva" @click="wrapText('_', '_')"><em>I</em></button>
                    <button type="button" class="bo-rich-btn" title="Lista" @click="insertBullet">• Lista</button>
                    <button type="button" class="bo-rich-btn" title="Código" @click="wrapText('`', '`')">{ }</button>
                  </div>
                  <textarea
                    ref="editorRef"
                    v-model="pushEmail.body"
                    rows="7"
                    class="bo-textarea"
                    placeholder="Escribe tu mensaje aquí…"
                  ></textarea>
                </div>
                <div class="bo-field">
                  <label class="bo-label">Destinatarios ({{ formatInt(pushEmail.recipients.length) }})</label>
                  <div class="bo-recipients">
                    <span v-for="r in pushEmail.recipients" :key="r" class="bo-recipient-chip">
                      {{ r }}
                      <button type="button" class="bo-recipient-chip__remove" @click="removeRecipient(r)" aria-label="Eliminar">×</button>
                    </span>
                    <input
                      v-model="pushEmail.recipientInput"
                      type="email"
                      class="bo-recipients__input"
                      placeholder="email@ejemplo.com + Enter"
                      @keydown.enter.prevent="addRecipient"
                    />
                  </div>
                </div>
              </div>

              <!-- Vista previa -->
              <div class="bo-email-panel__preview">
                <h4 class="bo-preview-title">Vista previa</h4>
                <div class="bo-email-preview">
                  <div class="bo-email-preview__subject">{{ pushEmail.subject || 'Sin asunto' }}</div>
                  <!-- eslint-disable vue/no-v-html -->
                  <div
                    class="bo-email-preview__body"
                    v-html="parsedPreview || '<span class=\'bo-placeholder\'>El cuerpo del email aparecerá aquí…</span>'"
                  ></div>
                  <!-- eslint-enable vue/no-v-html -->
                  <div class="bo-email-preview__footer">
                    <small>{{ pushEmail.recipients.length ? pushEmail.recipients.join(', ') : 'Sin destinatarios' }}</small>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <div class="bo-modal__footer">
            <Transition name="bo-fade">
              <span v-if="pushEmail.sent" class="bo-inline-success">✓ Enviado</span>
            </Transition>
            <Transition name="bo-fade">
              <span v-if="pushEmail.sendError" class="bo-inline-error">✕ Error al enviar</span>
            </Transition>
            <button type="button" class="bo-btn bo-btn--ghost" @click="closeEmailModal">Cancelar</button>
            <button
              type="button"
              class="bo-btn bo-btn--primary"
              :disabled="pushEmail.sending || !pushEmail.subject || !pushEmail.body || !pushEmail.recipients.length"
              @click="sendPushEmail"
            >
              <span v-if="pushEmail.sending" class="bo-spinner"></span>
              <span v-else>Enviar mensaje</span>
            </button>
          </div>

        </div>
      </div>
    </Transition>

  </div>
</template>

<script setup>
// ─────────────────────────────────────────────────────────────────────────────
// IMPORTS
// ─────────────────────────────────────────────────────────────────────────────
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import axios from 'axios'
import apiUrls from '@/new-constants/apiUrls'
import { priceConverter } from '@/Utils/Utils'
import store from '@/store'
import ManageData from '@/components/Admin/ManageData/ManageData.vue'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTES
// ─────────────────────────────────────────────────────────────────────────────
const siteUrl = process.env.VUE_APP_SITE_URL
// Único correo autorizado a ver/usar la sección "Datos" (Descargar datos / ManageData)
const DATA_ADMIN_EMAIL = 'cristian@houseandflats.com'

// ─────────────────────────────────────────────────────────────────────────────
// STATE
// ─────────────────────────────────────────────────────────────────────────────
// Datos desde la API
const apiAccommodations = ref([])
const fullInventory     = ref([])  // Espejo de las páginas cargadas (ya no es el inventario completo)
const apiHosts          = ref([])
const apiLoading        = ref(false)
const currencySymbol    = ref('$')
// Tabla de símbolos/valores desde backoffice (opcional)
const backofficeExchangeTable = ref([])

// Moneda seleccionada por el visitante (reactiva desde el store)
const visitorCurrency = computed(() => {
  return (store && store.state && store.state.exchange && store.state.exchange.visitorCurrency) || localStorage.getItem('visitorCurrency') || null
})
const locationOptions   = ref([])

// UI State
// 'dashboard' = Panel (activos) | 'sin_aprobar' | 'eliminados' | 'data' = Descargar datos (ManageData)
const activeView     = ref('dashboard')
// Acceso a la sección "Datos" restringido a un único correo autorizado
const canAccessData  = computed(() =>
  (localStorage.getItem('correo') || '').trim().toLowerCase() === DATA_ADMIN_EMAIL
)

// Vista → `estado` que entiende la API (ver BackOfficeController::aplicarEstado).
const ESTADO_POR_VISTA = { dashboard: 'activos', sin_aprobar: 'sin_aprobar', eliminados: 'eliminados' }

// Pestañas visibles. "Datos" solo para el correo autorizado.
const VISTAS = computed(() => [
  { key: 'dashboard',   label: 'Panel' },
  { key: 'sin_aprobar', label: 'Sin aprobar' },
  { key: 'eliminados',  label: 'Eliminados' },
  ...(canAccessData.value ? [{ key: 'data', label: 'Datos' }] : []),
])

// Estado del listado que se está mostrando. Se congela al entrar a "Datos":
// la lista que quedó en memoria sigue siendo la de la última sección.
const estadoActual = ref('activos')
// Solo los super admins pueden editar los datos del host (perfil / bancarios).
// Mismo criterio que BackOfficeController::ensureSuperAdmin en el backend.
const canEditHost = computed(() => !!store.getters.isSuperAdmin)
// Lista de países (idpais / nombre) para el select de nacionalidad al editar el perfil
const countriesList = ref([])

// ── Listado ──────────────────────────────────────────────────────────────────
// Selección de filas (Set reasignado en cada cambio: Vue 2.7 no observa mutaciones de Set).
const selected = ref(new Set())

// Scroll infinito: arranca con 100 filas y pide otras 100 al llegar al final.
//
// Antes `PAGE_CHUNK` recortaba un array que ya tenía el inventario entero; ahora
// es el tamaño de página que se le pide a la API. El listado dejó de descargar
// las ~34.000 filas de un saque: armar ese JSON le costaba al server unos 134 MB
// y lo dejaba sin memoria, y lo que se llegaba a mostrar eran siempre 100 filas.
const PAGE_CHUNK   = 100
const sentinelRef  = ref(null)

// ── Estado del listado paginado ──────────────────────────────────────────────
// `apiAccommodations` ya no es el inventario completo sino las páginas que se
// fueron cargando. El filtrado, el orden y el resumen los hace la API: acá solo
// se acumula lo que llega.
const serverTotal     = ref(0)      // filas del conjunto filtrado completo
const serverAgregados = ref(null)   // resumen del conjunto filtrado completo
const pageLoaded      = ref(0)      // última página recibida
const loadingPage     = ref(false)  // hay una página en vuelo

// Quedan filas por pedir.
const hasMore = computed(() => apiAccommodations.value.length < serverTotal.value)

// Orden activo del listado: { key: <columna>, dir: 'asc' | 'desc' }
const sort = ref({ key: null, dir: 'asc' })

// Filtros por columna: { [colKey]: Set(valores permitidos) }. Sin entrada = sin filtro.
const columnFilters = ref({})

// Buscador global: filtra por el texto de todas las columnas a la vez.
// Lo tipeado se vuelca a `searchQuery` con un pequeño retardo para no recorrer
// las decenas de miles de filas en cada tecla.
const searchInput = ref('')
const searchQuery = ref('')
let searchTimer = null

watch(searchInput, value => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => { searchQuery.value = value }, 180)
})

function clearSearch () {
  clearTimeout(searchTimer)
  searchInput.value = ''
  searchQuery.value = ''
}

// ── Rango de fechas / disponibilidad ─────────────────────────────────────────
// El rango se escribe en `dateRange` y recién se consulta al presionar Aplicar,
// que lo vuelca a `appliedRange`. De ahí lo toma `inventoryParams()`: las fechas
// son un filtro más de la consulta, así que todas las filas que vuelven están
// evaluadas contra ese rango.
const dateRange           = ref({ start: '', end: '' })
const appliedRange        = ref({ start: '', end: '' })
const onlyAvailable       = ref(false)
const availabilityLoading = ref(false)
const availModal          = ref(false)  // popup del rango de fechas

// ── Estado del overlay de carga ──────────────────────────────────────────────
// Corta la consulta si el backend no contesta, en vez de dejar la pantalla
// tapada para siempre.
const AVAILABILITY_TIMEOUT_MS = 180000  // 3 min
// A partir de acá el overlay sugiere acotar los filtros: si tarda tanto es que
// se está evaluando el inventario entero.
const AVISO_LENTO_S = 15

const loadingStage    = ref('')    // etapa en curso, en palabras
const loadingProgress = ref(null)  // 0-100 mientras se procesa | null = sin porcentaje
const loadingElapsed  = ref(0)     // segundos desde que arrancó
let   loadingTimer    = null
let   availabilityAbort = null     // CancelToken de la consulta en vuelo

// `yieldToBrowser()` —pintar un frame y devolver el control al event loop entre
// tanda y tanda— se fue con el procesamiento por tandas: normalizar 100 filas es
// instantáneo y ya no hay nada que repartir.

function startAvailabilityLoading (stage) {
  availabilityLoading.value = true
  loadingStage.value    = stage
  loadingProgress.value = null
  loadingElapsed.value  = 0
  clearInterval(loadingTimer)
  loadingTimer = setInterval(() => { loadingElapsed.value += 1 }, 1000)
}

function stopAvailabilityLoading () {
  availabilityLoading.value = false
  loadingStage.value    = ''
  loadingProgress.value = null
  clearInterval(loadingTimer)
  loadingTimer = null
  availabilityAbort = null
}

// Salir sin recargar la página: corta la consulta y descarta la respuesta que
// pudiera llegar después (el contador de `inventoryRequestId` la invalida).
function cancelAvailability () {
  inventoryRequestId++
  if (availabilityAbort) availabilityAbort.cancel('cancelado por el usuario')
  stopAvailabilityLoading()
  addToast('Cálculo cancelado', 'info', 2500)
}

// Rango escrito válido (fin posterior al inicio).
const hasDateRange = computed(() => {
  const { start, end } = dateRange.value
  return !!(start && end && end > start)
})

// Hay un rango ya aplicado sobre el listado.
const isApplied = computed(() => !!(appliedRange.value.start && appliedRange.value.end))

// El rango escrito todavía no se aplicó (o cambió respecto del aplicado).
const pendingApply = computed(() =>
  hasDateRange.value &&
  (dateRange.value.start !== appliedRange.value.start || dateRange.value.end !== appliedRange.value.end)
)

// Noches entre dos fechas 'YYYY-MM-DD'.
function nightsBetween (start, end) {
  if (!start || !end || end <= start) return 0
  return Math.max(0, Math.round((new Date(end) - new Date(start)) / 86400000))
}

// Noches del rango escrito y del rango ya aplicado (pueden diferir).
const rangeNights   = computed(() => nightsBetween(dateRange.value.start, dateRange.value.end))
const appliedNights = computed(() => nightsBetween(appliedRange.value.start, appliedRange.value.end))

const appliedRangeLabel = computed(() => {
  if (!isApplied.value) return ''
  return `${formatDay(appliedRange.value.start)} → ${formatDay(appliedRange.value.end)}`
})

// Unidades alcanzadas por los filtros actuales.
//
// El "alcance congelado" desapareció: antes, aplicar un rango evaluaba solo los
// ids que estaban filtrados en ese momento y el resto quedaba en "-", porque el
// cálculo se hacía sobre el inventario que el front tenía en memoria. Ahora las
// fechas viajan como un filtro más de la consulta, así que todo lo que vuelve
// está evaluado y no hay nada "fuera de alcance" que reaplicar.
const scopeSize        = computed(() => serverTotal.value)
const appliedScopeSize = computed(() => (isApplied.value ? serverTotal.value : 0))
const outOfScopeCount  = computed(() => 0)

// Disponibles del conjunto filtrado completo (lo calcula la API, no la página).
const availableTotal = computed(() => stats.value.disponibles)

// Menú desplegable de la columna (ordenar + filtrar por valores).
// `cargando` cubre el viaje a la API de las columnas que piden sus valores.
const menu = ref({
  col: null, top: 0, left: 0, width: 304, maxHeight: 0, sheet: false,
  search: '', options: [], draft: new Set(), cargando: false
})

// Host profile modal
const hostProfileModal = ref({ show: false, host: null, accommodations: [] })

// Modal Push / Email: se abre desde la barra de acciones con los correos de los
// anfitriones de los alojamientos seleccionados.
const emailModal = ref(false)
const editorRef  = ref(null)
const pushEmail  = ref({
  subject: '',
  body: '',
  recipients: [],
  recipientInput: '',
  sending: false,
  sent: false,
  sendError: false,
})

// Toasts
const toasts = ref([])
let toastId = 0

// ─────────────────────────────────────────────────────────────────────────────
// COLUMNAS DEL LISTADO
// Cada columna se pinta con colText(). El orden y el filtro los resuelve la API:
// `key` es lo que viaja en `orden`, y los valores marcados en el menú viajan en
// el `f_<columna>` correspondiente (ver inventoryParams()).
//
//   selectAll   → al abrir el menú arranca con todo marcado (pocas opciones).
//                 Sin la marca, el menú abre vacío: se busca y se selecciona.
//   searchOnly  → la lista arranca en blanco; sólo aparece lo que se busca.
//   filterable  → false deja la columna sólo con las opciones de orden.
//   hoverWidth  → ancho al que se estira la columna mientras el mouse está encima.
// ─────────────────────────────────────────────────────────────────────────────
const COLUMNS = [
  { key: 'id',        label: 'ID',              width: 80,  numeric: true },
  { key: 'idHost',    label: 'ID Host',         width: 90,  numeric: true },
  { key: 'host',      label: 'Host',            width: 190 },
  // El código externo del alojamiento (`idcodigo`). Es columna, y no solo un
  // campo más del buscador, porque el índice de búsqueda se arma con el texto
  // de las columnas: agregándola acá se puede buscar por código y además verlo.
  //
  // Va DESPUÉS de Host a propósito: checkbox, ID, ID Host y Host son las
  // columnas congeladas y tienen que quedar pegadas al principio (ver
  // measureFrozenCols). Metida entre ellas, las celdas fijas se superponen.
  { key: 'codigo',    label: 'Código',          width: 110, searchOnly: true },
  { key: 'segmento',  label: 'Segmento',        width: 110, selectAll: true },
  { key: 'direccion', label: 'Dirección',       width: 280, hoverWidth: 560 },
  { key: 'nombre',    label: 'Nombre',          width: 320, hoverWidth: 620, filterable: false },
  { key: 'precio',    label: 'Precio',          width: 160, numeric: true },
  { key: 'tipo',      label: 'Tipo',            width: 120, selectAll: true },
  { key: 'categoria', label: 'Categoría',       width: 130, selectAll: true },
  { key: 'huespedes', label: 'Máx. huéspedes',  width: 130, numeric: true },
  { key: 'email',     label: 'Email host',      width: 230, hoverWidth: 420 },
  { key: 'telefono',  label: 'Teléfono host',   width: 150, searchOnly: true },
  { key: 'whats',     label: 'Whats',           width: 90,  selectAll: true },
  { key: 'ical',      label: 'iCal',            width: 110, selectAll: true },
  { key: 'proveedor', label: 'Proveedor',       width: 120, selectAll: true },
  { key: 'disponible', label: 'Disponibilidad', width: 140, selectAll: true },
  { key: 'actividad', label: 'Actividad',       width: 120, selectAll: true },
]

const COL_BY_KEY = COLUMNS.reduce((m, c) => { m[c.key] = c; return m }, {})

// De dónde viene cada unidad. Importa para leer la columna Disponibilidad: la
// del catálogo propio sale de restricciones y reservas nuestras, la de Spacest
// de su feed, y la de Homiii de la foto que deja el cron `homiii:availability-sync`.
const PROVIDER_LABEL = {
  propio:  'Propio',
  spacest: 'Spacest',
  homiii:  'Homiii',
}

// A partir de cuántas horas la foto de Homiii se considera vieja.
const HOMIII_STALE_HOURS = 36

// Ancho de la columna del checkbox (debe coincidir con el <col> de la tabla).
const CHECK_COL_WIDTH = 48

// ─────────────────────────────────────────────────────────────────────────────
// VIEWPORT
// El zoom del navegador cambia window.innerWidth igual que achicar la ventana,
// así que un único ancho reactivo cubre celular, tablet, escritorio y zoom.
// ─────────────────────────────────────────────────────────────────────────────
const viewportWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1280)

// Por debajo de 1024px no hay hover confiable (touch) ni ancho para estirar
// columnas: se desactivan el ensanchado y el auto-scroll por el borde.
const isNarrow = computed(() => viewportWidth.value < 1024)
const isMobile = computed(() => viewportWidth.value < 640)

// Columnas de texto largo (dirección, nombre, email host): al pasar el mouse por
// encima se ensanchan hasta `hoverWidth` para leer el valor completo sin abrir nada.
const hoveredCol = ref(null)

function colWidth (c) {
  return (c.hoverWidth && !isNarrow.value && hoveredCol.value === c.key) ? c.hoverWidth : c.width
}

// La tabla nunca se comprime por debajo de la suma de sus columnas: al achicar
// la ventana (o al hacer zoom) aparece scroll horizontal en vez de aplastarse.
const tableMinWidth = computed(
  () => COLUMNS.reduce((total, c) => total + colWidth(c), CHECK_COL_WIDTH)
)

function onColEnter (c) {
  // Mientras la tabla se desliza sola, o en pantallas angostas, no se ensancha.
  if (edgeMoving || isNarrow.value) return
  hoveredCol.value = c.hoverWidth ? c.key : null
}

function onColLeave (c) {
  if (hoveredCol.value === c.key) hoveredCol.value = null
}

// ─────────────────────────────────────────────────────────────────────────────
// AUTO-SCROLL HORIZONTAL POR HOVER EN EL BORDE
// La barra de scroll horizontal de la tabla queda al final de un listado muy
// largo, así que acercar el mouse al borde derecho desliza la tabla sola hasta
// la última columna (y al borde izquierdo la trae de vuelta) sin ir a buscarla.
// ─────────────────────────────────────────────────────────────────────────────
const gridScrollRef = ref(null)

const EDGE_ZONE  = 80   // px desde el borde donde se activa el deslizamiento
const EDGE_SPEED = 22   // px por frame en el punto más pegado al borde

let edgeRaf     = null
let edgePointerX = 0
let edgeMoving  = false   // true sólo mientras la tabla se está desplazando

function onGridMouseMove (e) {
  // En pantallas angostas se scrollea con el dedo: el deslizamiento por el borde
  // sólo estorbaría (el tap dispara un mousemove sintético).
  if (isNarrow.value) return
  edgePointerX = e.clientX
  if (edgeRaf == null) edgeRaf = requestAnimationFrame(edgeTick)
}

// ─────────────────────────────────────────────────────────────────────────────
// COLUMNAS CONGELADAS
// El checkbox y el ID (y en escritorio también ID Host y Host) quedan fijos al
// hacer scroll horizontal. El desplazamiento se mide del DOM en vez de fijarlo
// a mano: así sigue siendo exacto con cualquier zoom del navegador.
// ─────────────────────────────────────────────────────────────────────────────
function measureFrozenCols () {
  const el = gridScrollRef.value
  if (!el) return
  const ths = el.querySelectorAll('thead th')
  if (ths.length < 4) return

  // Los <th> son [checkbox, ...COLUMNS]. Se buscan por key y no por posición:
  // con el índice fijo, agregar una columna antes de las congeladas corría los
  // desplazamientos y las celdas fijas se montaban unas sobre otras.
  const anchoDe = key => {
    const i = COLUMNS.findIndex(c => c.key === key)
    const th = i === -1 ? null : ths[i + 1]
    return th ? th.getBoundingClientRect().width : 0
  }

  const o1 = ths[0].getBoundingClientRect().width  // después del checkbox
  const o2 = o1 + anchoDe('id')                    // después de ID
  const o3 = o2 + anchoDe('idHost')                // después de ID Host
  el.style.setProperty('--bo-frozen-1', `${o1}px`)
  el.style.setProperty('--bo-frozen-2', `${o2}px`)
  el.style.setProperty('--bo-frozen-3', `${o3}px`)
}

function edgeTick () {
  edgeRaf = null
  const el = gridScrollRef.value
  // Con el menú de columna abierto no se mueve nada: el popup quedaría desfasado.
  if (!el || menu.value.col) return stopEdgeScroll()

  const rect = el.getBoundingClientRect()
  const max  = el.scrollWidth - el.clientWidth
  const fromRight = rect.right - edgePointerX
  const fromLeft  = edgePointerX - rect.left

  let step = 0
  if (fromRight >= 0 && fromRight < EDGE_ZONE)     step =  EDGE_SPEED * (1 - fromRight / EDGE_ZONE)
  else if (fromLeft >= 0 && fromLeft < EDGE_ZONE)  step = -EDGE_SPEED * (1 - fromLeft / EDGE_ZONE)
  if (!step || max <= 0) return stopEdgeScroll()

  const before = el.scrollLeft
  const next   = Math.max(0, Math.min(max, before + step))
  if (Math.abs(next - before) < 0.5) return stopEdgeScroll()  // ya está en el tope

  el.scrollLeft = next
  edgeMoving = true
  hoveredCol.value = null
  edgeRaf = requestAnimationFrame(edgeTick)
}

function stopEdgeScroll () {
  if (edgeRaf != null) cancelAnimationFrame(edgeRaf)
  edgeRaf = null
  edgeMoving = false
}

// Tramos de precio: 30 tramos y uno abierto, en la moneda en la que se filtra.
// La escala base es de 100 en 100 hasta 3000, pensada para EUR/USD/GBP. En
// monedas de mucho valor nominal (CLP ≈ 1000 por USD) esa escala dejaba todo el
// inventario en "3000+" y el filtro no recortaba nada, así que se multiplica por
// la potencia de 10 más cercana a su cambio contra el dólar: CLP va de 100.000
// en 100.000 hasta 3.000.000. Los números viajan sin separadores de miles: el
// backend los parte por '-' y "100.000" lo leería como 100.
const PRICE_STEP = 100
const PRICE_TOP  = 3000

function escalaPrecio (moneda) {
  const tabla = backofficeExchangeTable.value || []
  const fila = tabla.find(e => e.codigo === moneda)
  const valor = fila ? Number(fila.valor) : NaN
  if (!Number.isFinite(valor) || valor <= 0) return 1
  return Math.max(1, Math.pow(10, Math.round(Math.log10(valor))))
}

const PRICE_BUCKETS = computed(() => {
  const k = escalaPrecio(monedaPrecioFiltro())
  const out = []
  for (let lo = 0; lo < PRICE_TOP; lo += PRICE_STEP) out.push(`${lo * k} - ${(lo + PRICE_STEP) * k}`)
  out.push(`${PRICE_TOP * k}+`)
  return out
})

// Capacidad de 1 a 10 personas (más de 10 cae en "10+").
const GUEST_TOP = 10
const GUEST_BUCKETS = (() => {
  const out = []
  for (let n = 1; n <= GUEST_TOP; n++) out.push(String(n))
  out.push(`${GUEST_TOP}+`)
  return out
})()

// Moneda en la que se muestran los precios y el rango del resumen.
const displayCurrency = computed(() => {
  if (visitorCurrency.value) return visitorCurrency.value
  const first = apiAccommodations.value.find(a => a.moneda)
  return (first && first.moneda) || 'CLP'
})

// Precio mensual del alojamiento como número, ya convertido a la moneda del visitante.
function accPriceNumber (acc) {
  if (!acc) return null
  const raw = acc.arriendo_total || acc.precio
  if (raw == null || raw === '') return null
  const conv = priceConverter({ amount: raw, currency: acc.moneda }, visitorCurrency.value || null)
  const n = Number(conv && conv.amount)
  if (Number.isFinite(n) && n > 0) return n
  const fallback = Number(raw)
  return Number.isFinite(fallback) ? fallback : null
}

// Precio ya formateado (incluye el código de moneda), tal como se ve en la tabla.
function accPriceLabel (acc) {
  return convertAndFormat(acc.arriendo_total || acc.precio, acc.moneda)
}

// Texto de una celda: es la clave de filtrado y el valor que lista el menú.
function colText (col, acc) {
  switch (col.key) {
    case 'id':        return String(acc.id != null ? acc.id : '')
    case 'codigo':    return acc.idcodigo ? String(acc.idcodigo) : '-'
    case 'idHost':    return String(acc.usuario_id != null ? acc.usuario_id : '-')
    case 'host':      return hostName(acc)
    case 'segmento':  return accSegment(acc).toUpperCase()
    case 'direccion': return accAddress(acc) || '-'
    case 'nombre':    return parseAccommodationName(acc.nombre) || '-'
    case 'precio':    return accPriceLabel(acc)
    case 'tipo':      return acc.tipo || '-'
    case 'categoria': return acc.categoria || '-'
    case 'huespedes': return String(acc.maximo_huespedes || acc.capacidad_huespedes || '-')
    case 'email':     return hostEmail(acc)
    case 'telefono':  return hostPhone(acc)
    case 'whats':     return hostTieneGrupoWhatsapp(acc) ? 'Con WhatsApp' : 'Sin WhatsApp'
    case 'ical':      return acc.ical ? 'Con iCal' : 'Sin iCal'
    case 'proveedor': return providerLabel(acc)
    case 'disponible':
      if (acc.disponible == null) return '-'
      return acc.disponible ? 'Disponible' : 'Ocupado'
    case 'actividad': return hostClasificacion(acc) || '-'
    default:          return ''
  }
}

// Lo que se pinta en la celda (el precio agrega el período).
function colDisplay (col, acc) {
  if (col.key === 'precio') return `${accPriceLabel(acc)}/mes`
  return colText(col, acc)
}

// El tramo al que pertenece cada fila (precio y huéspedes) ya no se calcula acá:
// el menú ofrece la escala completa —PRICE_BUCKETS / GUEST_BUCKETS— y quien
// decide qué filas caen en cada tramo es SQL. Ver aplicarFiltroPrecio() y
// aplicarFiltroHuespedes() en BackOfficeController: la definición de los tramos
// está duplicada entre los dos lados y tiene que moverse junta.

// Columnas cuyos valores posibles se le piden a la API. El resto se resuelve
// acá sin viajar: o son una escala fija, o salen de la lista de hosts.
const FACET_COLS = ['id', 'codigo', 'tipo', 'categoria', 'direccion']

/**
 * Valores que ofrece el menú de una columna.
 *
 * Antes salían de recorrer el inventario que el front tenía entero en memoria.
 * Con el listado paginado eso mostraría solo los valores de las cien filas
 * cargadas, así que los que viven en `alojamiento` se consultan con un DISTINCT.
 *
 * `busqueda` es el buscador del propio menú: Dirección y Código tienen miles de
 * valores y la API devuelve como mucho 200, así que afinar la lista es cosa del
 * servidor.
 */
async function opcionesDeColumna (col, busqueda = '') {
  if (!col || col.filterable === false) return []

  // Escalas fijas: la columna las muestra completas, existan o no en los datos.
  if (col.key === 'precio')    return PRICE_BUCKETS.value
  if (col.key === 'huespedes') return GUEST_BUCKETS

  // Derivadas del host: la lista de hosts ya está en memoria (son 565).
  if (COLUMNAS_DE_HOST.indexOf(col.key) !== -1) {
    const values = new Set()
    apiHosts.value.forEach(h => values.add(colText(col, { usuario_id: h.id })))
    return [...values].sort(compareOptions)
  }

  // Conjuntos chicos y conocidos: no vale la pena un viaje a la API.
  if (col.key === 'ical')       return ['Con iCal', 'Sin iCal']
  if (col.key === 'proveedor')  return Object.keys(PROVIDER_LABEL).map(k => PROVIDER_LABEL[k])
  if (col.key === 'disponible') return ['Disponible', 'Ocupado', '-']

  if (FACET_COLS.indexOf(col.key) === -1) return []

  try {
    // POST por el mismo motivo que fetchInventoryPage(): filtros grandes en el body.
    const { data } = await axios.post(
      apiUrls.backofficeInventoryFacets,
      inventoryParams({ col: col.key, busqueda }, col.key),
    )
    return data.values || []
  } catch (err) {
    console.error('[BackOffice] No se pudieron cargar los valores de la columna', col.key, err)
    return []
  }
}

function compareOptions (a, b) {
  return String(a).localeCompare(String(b), 'es', { numeric: true, sensitivity: 'base' })
}

// El orden también se fue a SQL: `inventoryParams()` manda `orden` y `dir`, y
// BackOfficeController::aplicarOrden() los traduce a columnas reales con
// desempate por id (sin un orden estable las páginas se pisan entre sí).

// ─────────────────────────────────────────────────────────────────────────────
// BÚSQUEDA GLOBAL
// ─────────────────────────────────────────────────────────────────────────────
// Marcas de acento que deja la descomposición NFD (U+0300 … U+036F).
const DIACRITIC_RE = new RegExp('[' + String.fromCharCode(768) + '-' + String.fromCharCode(879) + ']', 'g')

// Texto comparable: en minúsculas y sin acentos, así "nunoa" encuentra "Ñuñoa".
function normalizeText (value) {
  return String(value == null ? '' : value)
    .toLowerCase()
    .normalize('NFD')
    .replace(DIACRITIC_RE, '')
}

// Cada palabra tipeada tiene que aparecer en alguna columna (búsqueda tipo AND):
// "providencia 2" trae los de Providencia con un 2 en cualquier otra columna.
const searchTokens = computed(() =>
  normalizeText(searchQuery.value).split(/\s+/).filter(Boolean)
)

// El índice de búsqueda en memoria —un WeakMap con el texto de las 17 columnas
// por fila, precalculado en tandas— desapareció junto con el inventario
// completo: ahora el `LIKE` lo hace MySQL sobre la tabla entera, no sobre las
// filas que el front alcanzó a cargar. `normalizeText` y `searchTokens` siguen
// acá porque los usa `inventoryParams()` para decidir qué hosts coinciden con
// el texto buscado (esos no están en la tabla `alojamiento`).

// ─────────────────────────────────────────────────────────────────────────────
// FILTRADO + ORDEN
// ─────────────────────────────────────────────────────────────────────────────
// El filtrado en memoria se fue entero a SQL: los filtros de columna viajan como
// parámetros `f_*` y los derivados del host como `host_ids`. Ver
// `inventoryParams()`, que es donde se arman.

/**
 * Filas del listado: las páginas que devolvió la API, sin retocar.
 *
 * Antes acá se filtraba y se ordenaba en JavaScript sobre el inventario
 * completo. Ahora el WHERE y el ORDER BY los resuelve MySQL y esto recibe la
 * página ya hecha: volver a filtrar en memoria solo miraría las filas cargadas
 * y daría resultados incompletos.
 *
 * Quedan los dos nombres —la plantilla y la selección los usan por separado—
 * pero ya no se recorta nada: el scroll infinito pide la página siguiente en vez
 * de revelar más filas de un array que estaba entero en memoria.
 */
const filteredRows = computed(() => apiAccommodations.value)
const visibleRows  = computed(() => apiAccommodations.value)

// ─────────────────────────────────────────────────────────────────────────────
// RESUMEN (resultados / hosts distintos / precio promedio / rango)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Resumen del conjunto filtrado COMPLETO, no de las filas cargadas.
 *
 * Lo calcula la API (`agregados` en BackOfficeController) y llega con los
 * precios agrupados por moneda: sumar importes de monedas distintas daría
 * cualquier cosa, así que la conversión a la moneda del usuario se hace acá,
 * grupo por grupo, y recién después se combinan.
 *
 * Antes esto recorría el inventario entero en memoria. Con el listado paginado
 * eso hablaría solo de las cien filas visibles.
 */
const stats = computed(() => {
  const ag = serverAgregados.value
  if (!ag) {
    return { resultados: serverTotal.value, hosts: 0, promedio: 0, max: 0, maxMoneda: null, disponibles: 0 }
  }

  let suma = 0, cantidad = 0, max = 0, maxMoneda = null

  ;(ag.precios_por_moneda || []).forEach(grupo => {
    const n = Number(grupo.cantidad) || 0
    if (!n) return

    // La conversión es lineal, así que convertir la suma equivale a convertir
    // cada precio y sumarlos después.
    const sumaConv = priceConverter({ amount: Number(grupo.suma) || 0, currency: grupo.moneda }, visitorCurrency.value || null)
    const maxConv  = priceConverter({ amount: Number(grupo.maximo) || 0, currency: grupo.moneda }, visitorCurrency.value || null)

    suma += Number(sumaConv && sumaConv.amount) || 0
    cantidad += n

    const m = Number(maxConv && maxConv.amount) || 0
    if (m > max) { max = m; maxMoneda = grupo.moneda }
  })

  return {
    resultados: serverTotal.value,
    hosts: Number(ag.hosts) || 0,
    promedio: cantidad ? Math.round(suma / cantidad) : 0,
    max: Math.round(max),
    maxMoneda,
    disponibles: ag.disponibles == null ? 0 : Number(ag.disponibles) || 0,
  }
})

// Precio más alto formateado igual que la celda de la tabla (con su moneda).
// `stats.max` ya viene convertido a la moneda del usuario, así que se formatea
// en esa misma moneda: la conversión de convertAndFormat queda en identidad.
const maxPriceLabel = computed(() =>
  stats.value.max > 0 ? convertAndFormat(stats.value.max, displayCurrency.value) : '-'
)

// Tooltip de la tarjeta del precio más alto.
//
// Antes nombraba el alojamiento concreto, porque el máximo salía de recorrer las
// filas en memoria y se guardaba la fila ganadora. Ahora lo calcula MySQL sobre
// el conjunto filtrado completo y vuelve solo el importe: el alojamiento dueño
// de ese precio puede estar en cualquier página. Se gana que el número sea el
// máximo real y no el de las cien filas cargadas.
const maxPriceTitle = computed(() => {
  if (!(stats.value.max > 0)) return 'Sin precios cargados en el listado actual'
  return 'El más alto de todos los resultados filtrados'
})

// ─────────────────────────────────────────────────────────────────────────────
// SELECCIÓN
// ─────────────────────────────────────────────────────────────────────────────
const allSelected = computed(() =>
  filteredRows.value.length > 0 && filteredRows.value.every(a => selected.value.has(a.id))
)
const someSelected = computed(() => filteredRows.value.some(a => selected.value.has(a.id)))

// ─────────────────────────────────────────────────────────────────────────────
// MENÚ DE COLUMNA
// ─────────────────────────────────────────────────────────────────────────────
const MENU_MAX_OPTIONS = 200

// Opciones que se listan: las que coinciden con la búsqueda. En las columnas
// `searchOnly` (teléfono) la lista arranca vacía y sólo muestra lo ya marcado.
const menuFilteredOptions = computed(() => {
  const m = menu.value
  const q = (m.search || '').trim().toLowerCase()
  if (!q) {
    if (m.col && m.col.searchOnly) return m.options.filter(o => m.draft.has(o))
    return m.options
  }
  return m.options.filter(o => String(o).toLowerCase().includes(q))
})

const menuVisibleOptions = computed(() => menuFilteredOptions.value.slice(0, MENU_MAX_OPTIONS))

const menuSearchPlaceholder = computed(() => {
  const key = menu.value.col && menu.value.col.key
  if (key === 'host')      return 'Buscar por nombre del host…'
  if (key === 'direccion') return 'Buscar por dirección o comuna…'
  if (key === 'email')     return 'Buscar por correo…'
  if (key === 'telefono')  return 'Buscar por teléfono…'
  return 'Buscar valor…'
})

// "Seleccionar" marca lo que trajo la búsqueda; sin búsqueda, todas las opciones.
const menuSelectLabel = computed(() => {
  const n = formatInt(menuFilteredOptions.value.length)
  return (menu.value.search || '').trim()
    ? `Seleccionar los ${n} resultados`
    : `Seleccionar las ${n} opciones`
})

// Mensaje cuando no hay nada que listar.
const menuEmptyText = computed(() => {
  const m = menu.value
  if (m.cargando) return 'Cargando valores…'
  if ((m.search || '').trim()) return 'Sin coincidencias'
  if (m.col && m.col.searchOnly) return 'Buscá un valor para verlo acá'
  return 'Sin valores'
})

function isColumnActive (key) {
  return !!columnFilters.value[key]
}

// Ancho ideal del menú; se recorta si la ventana es más angosta.
const COLMENU_WIDTH = 304
const COLMENU_GAP   = 8    // aire contra los bordes del viewport
const COLMENU_MIN_H = 220  // por debajo de esto no vale la pena abrir hacia ese lado

async function openColumnMenu (col, ev) {
  if (menu.value.col && menu.value.col.key === col.key) { closeColumnMenu(); return }
  const rect = ev.currentTarget.getBoundingClientRect()

  // En celular no hay lugar para anclarlo a la columna: va como hoja inferior.
  const sheet = isMobile.value
  const width = Math.min(COLMENU_WIDTH, window.innerWidth - COLMENU_GAP * 2)
  const left  = Math.max(COLMENU_GAP, Math.min(rect.left, window.innerWidth - width - COLMENU_GAP))

  // Abre hacia abajo si entra; si no, hacia arriba. En ambos casos la altura se
  // recorta al espacio libre para que el listado no se vaya fuera de la pantalla.
  const roomBelow = window.innerHeight - rect.bottom - 6 - COLMENU_GAP
  const roomAbove = rect.top - 6 - COLMENU_GAP
  const openUp    = roomBelow < COLMENU_MIN_H && roomAbove > roomBelow
  const maxHeight = Math.min(
    window.innerHeight - COLMENU_GAP * 2,
    Math.max(COLMENU_MIN_H, openUp ? roomAbove : roomBelow)
  )
  const top       = openUp
    ? Math.max(COLMENU_GAP, rect.top - 6 - maxHeight)
    : rect.bottom + 6

  // Si la columna ya filtra, se reabre con esa selección.
  const current = columnFilters.value[col.key]

  // El menú se pinta YA, vacío y en "cargando": varias columnas tienen que
  // pedirle los valores a la API y esperar el viaje con la pantalla quieta
  // parecería que el clic no hizo nada.
  menu.value = {
    col, top, left, width, maxHeight, sheet, search: '',
    options: [], draft: current ? new Set(current) : new Set(), cargando: true,
  }

  const options = await opcionesDeColumna(col)

  // Mientras se pedían los valores el usuario pudo cerrar el menú o abrir otro.
  if (!menu.value.col || menu.value.col.key !== col.key) return

  // Sin filtro previo arranca vacío, salvo en las columnas de pocas opciones
  // (marcadas con selectAll), que arrancan con todo marcado.
  const draft = current
    ? new Set(current)
    : new Set(col.selectAll ? options : [])

  menu.value = { ...menu.value, options, draft, cargando: false }
}

function closeColumnMenu () {
  menu.value = {
    col: null, top: 0, left: 0, width: COLMENU_WIDTH, maxHeight: 0, sheet: false,
    search: '', options: [], draft: new Set(), cargando: false
  }
}

/**
 * Buscar dentro del menú de Dirección, Código o ID vuelve a consultar: la API
 * devuelve como mucho 200 valores, así que la lista completa no está acá y
 * filtrarla en memoria solo miraría esos primeros 200.
 */
let menuBusquedaTimer = null

watch(() => menu.value.search, busqueda => {
  const col = menu.value.col
  if (!col || FACET_COLS.indexOf(col.key) === -1) return

  clearTimeout(menuBusquedaTimer)
  menuBusquedaTimer = setTimeout(async () => {
    const options = await opcionesDeColumna(col, busqueda)
    if (!menu.value.col || menu.value.col.key !== col.key) return
    menu.value = { ...menu.value, options, cargando: false }
  }, 180)
})

// Como hoja inferior el tamaño lo pone el CSS; anclado a la columna, el JS.
const colMenuStyle = computed(() => {
  const m = menu.value
  if (!m.col || m.sheet) return {}
  return {
    top: `${m.top}px`,
    left: `${m.left}px`,
    width: `${m.width}px`,
    maxHeight: `${m.maxHeight}px`
  }
})

// Set reasignado: Vue 2.7 no reacciona a las mutaciones de un Set.
function toggleOption (value) {
  const draft = new Set(menu.value.draft)
  draft.has(value) ? draft.delete(value) : draft.add(value)
  menu.value = { ...menu.value, draft }
}

// Suma a lo ya marcado todo lo que trajo la búsqueda actual.
function selectAllOptions () {
  const draft = new Set(menu.value.draft)
  menuFilteredOptions.value.forEach(o => draft.add(o))
  menu.value = { ...menu.value, draft }
}

function clearOptions () {
  menu.value = { ...menu.value, draft: new Set() }
}

function applyColumnMenu () {
  const m = menu.value
  if (!m.col) return
  const next = { ...columnFilters.value }
  // Sin nada marcado la columna no filtra. Con todas las opciones marcadas
  // tampoco, PERO sólo si la lista es el universo completo: las de FACET_COLS
  // muestran lo que devolvió la API —como mucho MAX_FACET_VALUES y recortado por
  // el buscador del menú—, así que ahí "todas" no significa "todas las que hay".
  // En Código eso pasaba siempre: el código es único, buscarlo deja una sola
  // opción, y marcarla parecía marcar todo, con lo cual el filtro se borraba en
  // lugar de aplicarse.
  const listaCompleta = FACET_COLS.indexOf(m.col.key) === -1
  const all = listaCompleta && m.options.length > 0 && m.options.every(o => m.draft.has(o))
  if (m.draft.size === 0 || all) delete next[m.col.key]
  else next[m.col.key] = new Set(m.draft)
  // Tocar el precio a mano vuelve a la moneda propia (ver monedaPrecioFiltro).
  if (m.col.key === 'precio') monedaPrecioLink.value = null
  columnFilters.value = next
  closeColumnMenu()
}

function sortColumn (dir) {
  const m = menu.value
  if (!m.col) return
  sort.value = { key: m.col.key, dir }
  closeColumnMenu()
}

// ─────────────────────────────────────────────────────────────────────────────
// WATCHERS
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Firma de todo lo que define el conjunto filtrado. Los filtros son Sets, que no
 * se pueden comparar por identidad: se serializan ordenados para que el watcher
 * dispare solo cuando el contenido cambia de verdad.
 */
const filtrosFirma = computed(() => JSON.stringify({
  f: Object.keys(columnFilters.value).sort().map(k => [k, [...columnFilters.value[k]].sort()]),
  s: sort.value,
  q: searchQuery.value,
  r: appliedRange.value,
  d: onlyAvailable.value,
}))

/**
 * Único punto por el que se rehace la consulta.
 *
 * Cualquier cambio de filtro, orden, búsqueda o rango vuelve a la página 1: el
 * WHERE es otro y el resumen de arriba también. El retardo corto junta las
 * ráfagas —marcar tres opciones de un menú, o aplicar un rango que además
 * enciende "sólo disponibles"— en una sola consulta.
 *
 * Devuelve una promesa con el resultado para que quien necesite saber si salió
 * bien (aplicar fechas, que muestra un aviso y revierte si falla) pueda esperarla.
 */
const RECARGA_DEBOUNCE_MS = 120

let recargaTimer   = null
let recargaPromesa = null
let recargaResolve = null
let recargaOverlay = false

function programarRecarga (overlay = false) {
  recargaOverlay = recargaOverlay || overlay

  if (!recargaPromesa) {
    recargaPromesa = new Promise(resolve => { recargaResolve = resolve })
  }

  clearTimeout(recargaTimer)
  recargaTimer = setTimeout(async () => {
    const conOverlay = recargaOverlay
    const resolver   = recargaResolve

    recargaPromesa = null
    recargaResolve = null
    recargaOverlay = false

    const ok = await reloadInventory(conOverlay)
    nextTick(maybeLoadMore)
    resolver(ok)
  }, RECARGA_DEBOUNCE_MS)

  return recargaPromesa
}

watch(filtrosFirma, () => {
  // Durante el arranque la primera página la pide loadApiData().
  if (apiLoading.value) return
  programarRecarga()
})

// Avisa apenas se escribe un rango dado vuelta; el resto de la validación
// ocurre al aplicar.
watch(() => `${dateRange.value.start}|${dateRange.value.end}`, () => {
  const { start, end } = dateRange.value
  if (start && end && end <= start) {
    addToast('La fecha de término debe ser posterior a la de inicio', 'error')
  }
})

// ─────────────────────────────────────────────────────────────────────────────
// MÉTODOS – Toast
// ─────────────────────────────────────────────────────────────────────────────
function addToast (message, type = 'info', duration = 3500) {
  const id = ++toastId
  toasts.value.push({ id, message, type })
  setTimeout(() => { toasts.value = toasts.value.filter(t => t.id !== id) }, duration)
}

// Entero con separador de miles (resumen, contadores del menú de columna).
function formatInt (value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '0'
  return Math.round(n).toLocaleString('es-CL')
}

// Fecha corta (11 ago) para el resumen del rango aplicado.
function formatDay (value) {
  if (!value) return ''
  const [y, m, d] = String(value).split('-').map(Number)
  if (!y || !m || !d) return String(value)
  return new Date(y, m - 1, d).toLocaleDateString('es-CL', { day: 'numeric', month: 'short' })
}

// Formatea precios numéricos para mostrar separador de miles
function formatPrice (value) {
  if (value === null || value === undefined || value === '') return '-'
  const n = Number(value)
  if (Number.isNaN(n)) return String(value)
  return n.toLocaleString('es-CL', { maximumFractionDigits: 0 })
}

// Convierte y formatea precio según la moneda seleccionada por el usuario
function convertAndFormat (amount, sourceCurrency) {
  if (amount === null || amount === undefined || amount === '') return '-'
  const vCurrency = visitorCurrency.value || null
  const converted = priceConverter({ amount: amount, currency: sourceCurrency }, vCurrency)
  const amt = converted.amount
  const cur = converted.currency || vCurrency || sourceCurrency || ''
  // If conversion failed (no table yet), fallback to original amount with source currency
  if (amt === 0) return `${cur} 0`
  if (!amt || isNaN(amt)) {
    // Fallback display: show original amount with source currency
    if (sourceCurrency === 'CLP') return `${sourceCurrency} ${formatPrice(Math.ceil(amount))}`
    return `${sourceCurrency} ${Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
  }
  if (cur === 'CLP') {
    return `${cur} ${formatPrice(Math.ceil(amt))}`
  }
  // For other currencies show two decimals
  return `${cur} ${Number(amt).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
}

// Parsea el campo nombre que puede venir como JSON {"es":"...","en":"..."}
function parseAccommodationName (nombre) {
  if (!nombre) return ''
  
  // Si es un string que parece JSON, intentar parsearlo
  if (typeof nombre === 'string' && nombre.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(nombre)
      // Preferir español, luego inglés, luego el objeto completo
      return parsed.es || parsed.en || nombre
    } catch (e) {
      // Si falla el parse, devolver el string original
      return nombre
    }
  }
  
  // Si ya es un objeto, extraer español o inglés
  if (typeof nombre === 'object' && nombre !== null) {
    return nombre.es || nombre.en || JSON.stringify(nombre)
  }
  
  // Si es un string normal, devolverlo tal cual
  return String(nombre)
}

// Dirección del alojamiento (tolerante a los distintos nombres de campo).
function accAddress (acc) {
  if (!acc) return ''
  return acc.direccion || acc.direccion_completa || acc.address || ''
}

// Mapa id -> host para lookups O(1) desde la tabla de alojamientos.
const hostMapById = computed(() => {
  const m = {}
  apiHosts.value.forEach(h => { m[h.id] = h })
  return m
})

// Host dueño de un alojamiento (o null si no se encuentra).
function hostFor (acc) {
  if (!acc || acc.usuario_id == null) return null
  return hostMapById.value[acc.usuario_id] || null
}

// Nombre completo del host de un alojamiento.
function hostName (acc) {
  const h = hostFor(acc)
  if (!h) return '-'
  return [h.nombre, h.apellido].filter(Boolean).join(' ') || '-'
}

// Email del host de un alojamiento.
function hostEmail (acc) {
  const h = hostFor(acc)
  return (h && h.email) ? h.email : '-'
}

// Teléfono del host de un alojamiento.
function hostPhone (acc) {
  const h = hostFor(acc)
  return (h && h.telefono) ? h.telefono : '-'
}

// Estado (clasificación) del host dueño de un alojamiento: activo | lento | inactivo.
function hostClasificacion (acc) {
  const h = hostFor(acc)
  return h && h.clasificacion ? h.clasificacion : null
}

// Puntuación del host (0–4 estrellas), igual que en el modal de perfil.
function hostRating (acc) {
  const h = hostFor(acc)
  return h ? (Number(h.puntuacion) || 0) : 0
}

// Si el host está en un grupo de WhatsApp (para el logo de la columna "Whats").
function hostTieneGrupoWhatsapp (acc) {
  const h = hostFor(acc)
  return !!(h && h.tiene_grupo_whatsapp)
}

// ── Segmentación comercial B2B / B2C ──────────────────────────────────────────
// Un anfitrión con MÁS de 10 alojamientos se considera B2B; en caso contrario B2C.
const B2B_THRESHOLD = 10

// Respaldo histórico: contaba los alojamientos por host recorriendo el
// inventario completo. Con el listado paginado solo vería las páginas cargadas,
// así que en la práctica no se usa — /backoffice/hosts manda siempre
// `cantidad_alojamientos` y hostAccommodationsCount() lo prefiere.
const accCountByHostId = computed(() => {
  const m = {}
  fullInventory.value.forEach(a => {
    if (a.usuario_id != null) m[a.usuario_id] = (m[a.usuario_id] || 0) + 1
  })
  return m
})

// Nº de alojamientos de un host (prioriza el campo de la API si viene).
function hostAccommodationsCount (host) {
  if (!host) return 0
  if (host.cantidad_alojamientos != null) return Number(host.cantidad_alojamientos) || 0
  return accCountByHostId.value[host.id] || 0
}

// Segmento del host: 'b2b' (>10 alojamientos) o 'b2c'.
function hostSegment (host) {
  return hostAccommodationsCount(host) > B2B_THRESHOLD ? 'b2b' : 'b2c'
}

// Segmento a partir de un alojamiento (para la tabla de inventario).
function accSegment (acc) {
  return hostSegment(hostFor(acc))
}

// ─────────────────────────────────────────────────────────────────────────────
// MÉTODOS – Carga de datos desde el backend
// ─────────────────────────────────────────────────────────────────────────────
// Separador de los filtros `f_*`. No es la coma porque las direcciones la traen
// adentro ("Av. Providencia 1234, Providencia") y partiría los valores al medio.
// Tiene que coincidir con valoresDesdeLista() en BackOfficeController.
const FILTRO_SEP = '|'

// Columnas cuyo valor no sale de `alojamiento` sino del host. No tienen filtro
// propio en la API: se resuelven acá contra la lista de hosts (son 565, está en
// memoria) y viajan como `host_ids`.
const COLUMNAS_DE_HOST = ['host', 'idHost', 'segmento', 'actividad', 'email', 'telefono', 'whats']

/**
 * Ids de los hosts que pasan los filtros de columna derivados del host.
 *
 * Cada columna se evalúa con las MISMAS funciones que pintan la celda —se le
 * pasa un alojamiento de mentira con solo `usuario_id`—, así el valor que se
 * compara es exactamente el que el usuario marcó en el menú.
 *
 * Varias columnas activas se combinan con AND: el host tiene que pasarlas todas.
 * Devuelve null si no hay ningún filtro de host (no se manda `host_ids`).
 */
function hostIdsParaFiltros (skipKey) {
  const activas = COLUMNAS_DE_HOST
    .filter(key => key !== skipKey && columnFilters.value[key])

  if (!activas.length) return null

  const ids = []
  apiHosts.value.forEach(h => {
    const fila = { usuario_id: h.id }
    const pasa = activas.every(key => columnFilters.value[key].has(colText(COL_BY_KEY[key], fila)))
    if (pasa) ids.push(h.id)
  })

  return ids
}

/**
 * Factores de conversión de precio, uno por moneda.
 *
 * Los tramos de la columna Precio están en la moneda en la que mira el usuario,
 * ya convertida; la base guarda `arriendo` en la moneda de cada alojamiento. El
 * factor cumple `arriendo = precio_mostrado * factor`, así el backend compara
 * contra la columna cruda y no pierde el índice. Ver aplicarFiltroPrecio().
 */
function factoresDePrecio () {
  const tabla = backofficeExchangeTable.value || []
  if (!tabla.length) return ''

  // Un link compartido trae los tramos en la moneda de quien lo armó; si no,
  // el mismo "0 - 100" significaría otra cosa para quien lo abre.
  const monedaTramos = monedaPrecioFiltro()

  // Se calcula con la tabla que trae el propio backoffice (`valor` = unidades
  // por USD), no con priceConverter(): ese depende de la tabla del store, que la
  // carga el header y puede no estar. Sin ella todos los factores salían NaN, no
  // viajaba ninguno y la API comparaba los tramos contra el precio crudo de cada
  // moneda —"500 - 600" en CLP traía departamentos de 500 EUR—.
  //   mostrado = arriendo * valor(vista) / valor(moneda)
  //   ⇒ arriendo = mostrado * valor(moneda) / valor(vista)
  const vista = tabla.find(e => e.codigo === monedaTramos)
  const valorVista = vista ? Number(vista.valor) : NaN

  return tabla.map(e => {
    let factor = Number(e.valor) / valorVista
    if (!Number.isFinite(factor) || factor <= 0) {
      const conv = priceConverter({ amount: 1, currency: monedaTramos }, e.codigo)
      factor = Number(conv && conv.amount)
    }
    return Number.isFinite(factor) && factor > 0 ? `${e.codigo}:${factor}` : null
  }).filter(Boolean).join(',')
}

/**
 * Parámetros del listado: filtros, orden, página y fechas.
 *
 * `skipKey` omite el filtro de esa columna, para pedirle a la API los valores
 * posibles de su menú sin que la propia columna los recorte.
 */
function inventoryParams (extra = {}, skipKey = null) {
  const params = { per_page: PAGE_CHUNK, estado: estadoActual.value, ...extra }

  // Fechas: van las APLICADAS, no las que se están escribiendo en el popup.
  // Con fechas, cada fila llega con `disponible` ya calculado.
  if (isApplied.value) {
    params.fecha_inicio  = appliedRange.value.start
    params.fecha_termino = appliedRange.value.end
  }

  // Buscador global. Las columnas del host no están en la tabla `alojamiento`,
  // así que los hosts que coinciden con el texto viajan aparte y la API los suma
  // con OR: buscar "Estela" encuentra sus alojamientos aunque su nombre no
  // figure en ninguna columna de la tabla.
  const texto = searchQuery.value.trim()
  if (texto) {
    params.q = texto
    const tokens = searchTokens.value
    const hostsQueCoinciden = apiHosts.value
      .filter(h => {
        const fila = { usuario_id: h.id }
        const txt = normalizeText(COLUMNAS_DE_HOST.map(k => colText(COL_BY_KEY[k], fila)).join(' '))
        return tokens.every(t => txt.includes(t))
      })
      .map(h => h.id)

    if (hostsQueCoinciden.length) params.q_host_ids = hostsQueCoinciden.join(',')
  }

  // Filtros derivados del host, ya resueltos a ids.
  //
  // El `-1` cuando la lista queda vacía no es un detalle menor: un `host_ids`
  // vacío el backend lo lee como "sin filtro" y devolvería el inventario entero,
  // justo lo contrario de lo que pidió el usuario. Con un id inexistente el
  // WHERE IN no matchea nada, que es la respuesta correcta.
  const hostIds = hostIdsParaFiltros(skipKey)
  if (hostIds) params.host_ids = hostIds.length ? hostIds.join(',') : '-1'

  // Filtros de columna que sí viven en `alojamiento`.
  const directos = {
    tipo: 'f_tipo', categoria: 'f_categoria', direccion: 'f_direccion',
    codigo: 'f_codigo', huespedes: 'f_huespedes', precio: 'f_precio',
    ical: 'f_ical', proveedor: 'f_proveedor', disponible: 'f_disponible',
  }

  Object.keys(directos).forEach(key => {
    if (key === skipKey) return
    const sel = columnFilters.value[key]
    if (sel && sel.size) params[directos[key]] = [...sel].join(FILTRO_SEP)
  })

  if (skipKey !== 'id' && columnFilters.value.id) {
    params.f_id = [...columnFilters.value.id].join(',')
  }

  // El precio necesita las tasas para poder comparar contra la columna cruda.
  if (params.f_precio) params.f_precio_factores = factoresDePrecio()

  // "Sólo disponibles" es un filtro más, y la API lo entiende como el de la
  // columna Disponibilidad. Solo tiene sentido con un rango aplicado.
  if (onlyAvailable.value && isApplied.value && !params.f_disponible && skipKey !== 'disponible') {
    params.f_disponible = 'Disponible'
  }

  // Orden. Las columnas del host no se pueden ordenar en SQL (el nombre vive en
  // `usuario` y el segmento ni siquiera existe como columna): el front manda los
  // ids ya ordenados y MySQL respeta ese orden con FIELD().
  //
  // OJO: las columnas que no están en el mapa de aplicarOrden() —iCal, Proveedor,
  // Disponibilidad, Código— caen en el orden por id. Antes se ordenaban en
  // memoria porque el inventario estaba entero acá.
  const { key, dir } = sort.value
  if (key) {
    params.dir = dir
    if (COLUMNAS_DE_HOST.indexOf(key) === -1) {
      params.orden = key
    } else {
      const col = COL_BY_KEY[key]
      const ordenados = [...apiHosts.value]
        .sort((a, b) => compareOptions(
          colText(col, { usuario_id: a.id }),
          colText(col, { usuario_id: b.id })
        ))
        .map(h => h.id)
      if (ordenados.length) params.orden_host_ids = ordenados.join(',')
    }
  }

  return params
}

// Normaliza una fila del inventario que llega de la API y le calcula la calidad
// a partir del host (mapa id → host).
function normalizeRow (acc, hostMap) {
  const host = hostMap[acc.usuario_id]
  let quality = 'malo'

  // Criterios de calidad:
  // - Bueno: tiene iCal Y host activo
  // - Promedio: tiene iCal O host no inactivo
  // - Malo: sin iCal Y (sin host o host inactivo)

  const hasIcal = acc.ical || acc.calendario_sincronizado
  const hasActiveHost = host && host.clasificacion === 'activo'
  const hasNonInactiveHost = host && host.clasificacion !== 'inactivo'

  if (hasIcal && hasActiveHost) {
    quality = 'bueno'
  } else if (hasIcal || hasNonInactiveHost) {
    quality = 'promedio'
  } else {
    quality = 'malo'
  }

  // Normalizar identificador y capacidad para la vista
  const normalizedId = acc.id || acc.idalojamiento || acc.id_alojamiento || null
  const normalizedMaxGuests = acc.maximo_huespedes || acc.capacidad_huespedes || acc.capacidad || null
  const hostEmail = acc.host_email || (host && host.email) || null
  // Sin rango de fechas la API manda null: la columna queda en "-"
  const disponible = acc.disponible == null ? null : !!acc.disponible

  // Los alojamientos viejos de la API pueden no traer proveedor.
  const proveedor = acc.proveedor || 'propio'

  return {
    ...acc,
    id: normalizedId,
    maximo_huespedes: normalizedMaxGuests,
    host_email: hostEmail,
    quality,
    disponible,
    proveedor,
  }
}

/**
 * Normaliza las filas de una página.
 *
 * Antes esto iba en tandas de 2.000 con un respiro entre cada una, porque
 * recorrer las ~34.000 filas del inventario completo de un tirón congelaba la
 * pestaña varios segundos. Con páginas de 100 el trabajo es imperceptible y no
 * hace falta repartirlo.
 *
 * Las filas salen congeladas a propósito: Vue no observa objetos congelados, y
 * así asignarlas no recorre ~17 propiedades por fila. Es seguro porque nadie
 * muta una fila en su lugar: siempre se reemplazan por copias nuevas. Lo que sí
 * se muta son los hosts, que viven aparte en `apiHosts` y los lee `hostFor()` en
 * cada celda, así que editarlos se refleja solo.
 */
function mapRows (rows, hostMap) {
  return (rows || []).map(r => Object.freeze(normalizeRow(r, hostMap)))
}

// ── Proveedor y frescura del dato de disponibilidad ──────────────────────────
function providerLabel (acc) {
  return PROVIDER_LABEL[acc && acc.proveedor] || PROVIDER_LABEL.propio
}

// Horas transcurridas desde la última sincronización, o null si nunca se sondeó.
function hoursSinceSync (acc) {
  const stamp = acc && acc.disponibilidad_sincronizada
  if (!stamp) return null

  // La API manda 'YYYY-MM-DD HH:MM:SS'; Safari no parsea ese formato con espacio.
  const parsed = new Date(String(stamp).replace(' ', 'T'))
  if (isNaN(parsed.getTime())) return null

  return (Date.now() - parsed.getTime()) / 3600000
}

/**
 * La disponibilidad de Homiii es una foto que deja un cron, no el estado en
 * vivo. Si nunca se sondeó esa habitación, o la foto quedó vieja, la celda lo
 * avisa en vez de dar un "Disponible" que nadie puede respaldar.
 */
function availabilityIsStale (acc) {
  if (!acc || acc.proveedor !== 'homiii') return false
  if (acc.disponible == null) return false

  const hours = hoursSinceSync(acc)
  return hours === null || hours > HOMIII_STALE_HOURS
}

function availabilityTitle (acc) {
  if (!acc || acc.disponible == null) {
    // Con fechas aplicadas, un "-" ya no significa "no elegiste fechas": esta
    // unidad entró al filtro DESPUÉS de aplicarlas y nunca se evaluó.
    return isApplied.value
      ? 'Entró al filtro después de aplicar las fechas: reaplicá para evaluarla'
      : 'Elegí un rango de fechas'
  }

  const estado = acc.disponible ? 'Disponible' : 'Ocupado'

  if (acc.proveedor === 'spacest') {
    return `${estado} · según el feed de Spacest`
  }

  if (acc.proveedor === 'homiii') {
    const hours = hoursSinceSync(acc)
    if (hours === null) {
      return `${estado} · esta habitación nunca se sondeó en Homiii, el dato no está confirmado`
    }
    if (hours > HOMIII_STALE_HOURS) {
      return `${estado} · según un sondeo de hace ${Math.round(hours)} h, puede estar desactualizado`
    }
    return `${estado} · según el sondeo a Homiii de hace ${Math.round(hours)} h`
  }

  return `${estado} · según restricciones y reservas propias`
}

/**
 * Pide una página del listado.
 *
 * Con `reset` reemplaza el listado y vuelve a la página 1 (cambió un filtro, el
 * orden o el buscador); sin `reset` concatena la siguiente (scroll infinito).
 *
 * Los agregados del resumen recorren el conjunto filtrado entero, así que solo
 * se piden cuando ese conjunto cambia — al scrollear no hacen falta.
 *
 * El contador `inventoryRequestId` descarta las respuestas que quedaron viejas:
 * sin él, una página lenta pisaba el resultado de un filtro posterior.
 */
let inventoryRequestId = 0

async function fetchInventoryPage ({ reset = false, overlay = false } = {}) {
  if (loadingPage.value && !reset) return false

  const page  = reset ? 1 : pageLoaded.value + 1
  const reqId = ++inventoryRequestId

  loadingPage.value = true
  if (overlay) startAvailabilityLoading('Consultando la API…')

  const source = axios.CancelToken.source()
  availabilityAbort = source

  try {
    const hostMap = {}
    apiHosts.value.forEach(h => { hostMap[h.id] = h })

    // POST y no GET: los filtros van en el body. Con muchos valores marcados
    // (cientos de direcciones) la query string superaba el límite del servidor
    // y la petición moría como "Network Error"/CORS.
    const { data } = await axios.post(
      apiUrls.backofficeInventory,
      inventoryParams({ page, agregados: reset ? 1 : 0 }),
      { cancelToken: source.token, timeout: AVAILABILITY_TIMEOUT_MS },
    )
    if (reqId !== inventoryRequestId) return false

    const rows = mapRows(data.accommodations, hostMap)

    apiAccommodations.value = reset ? rows : apiAccommodations.value.concat(rows)
    fullInventory.value = apiAccommodations.value
    pageLoaded.value = page
    serverTotal.value = Number(data.total) || 0
    if (reset) serverAgregados.value = data.agregados || null

    return true
  } catch (err) {
    if (reqId !== inventoryRequestId || axios.isCancel(err)) return false

    console.error('[BackOffice] Error cargando el inventario:', err)
    const esTimeout = err && err.code === 'ECONNABORTED'
    addToast(
      esTimeout
        ? 'La consulta tardó demasiado. Acotá los filtros y volvé a intentar.'
        : 'No se pudo cargar el inventario',
      'error',
    )
    return false
  } finally {
    if (reqId === inventoryRequestId) {
      loadingPage.value = false
      if (overlay) stopAvailabilityLoading()
    }
  }
}

// Recarga desde la página 1. Es lo que corre al tocar un filtro, el orden o el
// buscador: el conjunto filtrado cambió y hay que rehacer también el resumen.
function reloadInventory (overlay = false) {
  return fetchInventoryPage({ reset: true, overlay })
}

// ── Popup de disponibilidad ──────────────────────────────────────────────────
function openAvailModal () {
  availModal.value = true
  document.body.style.overflow = 'hidden'
  // Foco en la primera fecha para poder tipear de una.
  nextTick(() => {
    const el = document.getElementById('bo-date-start')
    if (el) el.focus()
  })
}

function closeAvailModal () {
  if (availabilityLoading.value) return
  availModal.value = false
  document.body.style.overflow = ''
}

// Aplica el rango a las unidades que hoy pasan el resto de los filtros.
async function applyDateRange () {
  // El "Reaplicar" de la barra entra por acá con el popup cerrado. Si alguien
  // vació los campos de fecha a mano sin llegar a limpiar el filtro, el rango
  // que vale es el que está aplicado sobre el listado, no el campo vacío.
  if ((!dateRange.value.start || !dateRange.value.end) && isApplied.value) {
    dateRange.value = { ...appliedRange.value }
  }

  const { start, end } = dateRange.value
  if (!start || !end) {
    addToast('Elegí la fecha de inicio y la de término', 'error')
    return
  }
  if (end <= start) {
    addToast('La fecha de término debe ser posterior a la de inicio', 'error')
    return
  }

  // Antes de pisar appliedRange: distingue la primera aplicación de un reaplicado.
  const eraPrimeraVez = !isApplied.value

  // El popup se cierra antes de consultar, no después: así el overlay de carga
  // toma la pantalla de una y nadie sigue apretando "Aplicar" sobre una
  // interfaz que no responde. El rango escrito queda igual, para poder
  // corregirlo si la consulta falla o se cancela.
  closeAvailModal()

  // El rango se aplica ANTES de consultar porque `inventoryParams()` lee
  // `appliedRange` para mandar las fechas. Si la consulta falla se revierte.
  const rangoPrevio = { ...appliedRange.value }
  appliedRange.value = { start, end }

  // "Sólo disponibles" se enciende al aplicar por primera vez, que es lo que
  // espera cualquiera al filtrar por fechas. Pero al REaplicar hay que respetar
  // lo que el usuario haya elegido: si lo destildó para ver los ocupados,
  // volver a encenderlo hacía que "Reaplicar" no tuviera ningún efecto visible.
  const disponiblesPrevio = onlyAvailable.value
  if (eraPrimeraVez) onlyAvailable.value = true

  // El rango y "sólo disponibles" ya entran en la firma de filtros, así que la
  // recarga la dispara el watcher; acá solo se espera su resultado para poder
  // avisar y revertir si falló.
  const ok = await programarRecarga(true)
  if (!ok) {
    appliedRange.value = rangoPrevio
    onlyAvailable.value = disponiblesPrevio
    return
  }

  // Ya no hay "alcance congelado": el rango es un filtro más de la consulta, así
  // que todas las filas que vuelven están evaluadas contra esas fechas. Antes
  // había que congelar los ids porque la evaluación se hacía sobre el inventario
  // que estaba en memoria y el resto quedaba en "-".
  addToast(
    `Disponibilidad aplicada a ${formatInt(serverTotal.value)} unidad${serverTotal.value !== 1 ? 'es' : ''}`,
    'success',
  )
}

// Limpia el rango: el listado vuelve a mostrar todos los alojamientos.
//
// Antes alcanzaba con borrar el `disponible` de las filas que estaban en
// memoria. Ahora las fechas son parte de la consulta, así que hay que volver a
// pedir el listado sin ellas — lo dispara el watcher de los filtros.
function clearDateRange () {
  dateRange.value    = { start: '', end: '' }
  appliedRange.value = { start: '', end: '' }
  onlyAvailable.value = false

  // El filtro de la columna Disponibilidad ya no tiene sentido sin fechas.
  // Limpiar el rango ya dispara la recarga: `appliedRange` entra en la firma.
  if (columnFilters.value.disponible) {
    const next = { ...columnFilters.value }
    delete next.disponible
    columnFilters.value = next
  }

  // Si se limpió desde el popup hay que cerrarlo. El listado de atrás ya quedó
  // sin el filtro, pero con el popup encima no se ve nada de eso: se apretaba
  // "Limpiar", la pantalla no cambiaba y parecía que el botón no hacía nada.
  // Llamado desde la × de la barra esto no molesta: el popup ya está cerrado.
  closeAvailModal()
}

/**
 * Arranque: primero los tres catálogos, después la primera página del listado.
 *
 * El orden importa. Los filtros de Host, Segmento, Email, Teléfono, Whats y
 * Actividad se resuelven contra `apiHosts`, y el de Precio necesita la tabla de
 * cambio: si el inventario saliera en paralelo, la primera consulta podría irse
 * sin esos parámetros. Son catálogos chicos y el costo es un viaje extra.
 */
async function loadApiData () {
  apiLoading.value = true
  // Si se cambia de pestaña mientras esto carga, cambiarVista() toma el control.
  const estado = estadoActual.value
  const reqId  = vistaRequestId
  try {
    const [hostsRes, exchRes, locRes] = await Promise.all([
      axios.get(apiUrls.backofficeHosts, { params: { estado } }),
      axios.get(apiUrls.backofficeExchange),
      axios.get(apiUrls.backofficeLocations),
    ])

    hostsPorEstado[estado] = hostsRes.data.hosts || []
    if (reqId === vistaRequestId) apiHosts.value = hostsPorEstado[estado]

    const exch = exchRes.data.exchange || []
    backofficeExchangeTable.value = exch
    const clpEntry = exch.find(e => e.codigo === 'CLP')
    currencySymbol.value = clpEntry ? clpEntry.simbolo : '$'

    locationOptions.value = locRes.data.locations || []

    // Ya se cambió de pestaña: la primera página la pide cambiarVista().
    if (reqId !== vistaRequestId) return
    await reloadInventory()
  } catch (err) {
    console.error('[BackOffice] Error cargando datos:', err)
    addToast('Error al cargar datos del servidor', 'error')
  } finally {
    if (reqId === vistaRequestId) apiLoading.value = false
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// CAMBIO DE SECCIÓN (Panel / Sin aprobar / Eliminados / Datos)
// ─────────────────────────────────────────────────────────────────────────────
// Hosts ya pedidos por estado. Es un catálogo chico (cientos de filas) y el de
// activos es la consulta más lenta del arranque: volver a una sección no lo
// repite. Las FILAS no se guardan: cada sección arranca de cero con 100.
const hostsPorEstado = {}

// Invalida los cambios de sección que quedaron a medio camino (clics rápidos).
let vistaRequestId = 0

/**
 * Cambia de pestaña. Para que el navegador tenga una sola sección cargada:
 *  1. corta la consulta en vuelo y la recarga pendiente,
 *  2. vacía la lista, la selección, los filtros, el orden, la búsqueda y las fechas
 *     (los filtros de una sección no tienen por qué existir en la otra),
 *  3. trae los hosts del estado nuevo (o los toma del caché) y la página 1.
 * "Datos" no toca el listado: la tabla se desmonta (v-if) y al volver se
 * vuelve a dibujar con lo que ya estaba.
 */
async function cambiarVista (vista) {
  if (vista === activeView.value) return
  if (vista === 'data' && !canAccessData.value) return

  closeColumnMenu()
  if (availModal.value && !availabilityLoading.value) closeAvailModal()
  if (hostProfileModal.value.show) closeHostProfile()

  activeView.value = vista
  if (vista === 'data') return

  const estado = ESTADO_POR_VISTA[vista]
  if (estado === estadoActual.value) {
    // Volviendo de "Datos" a la misma sección: la lista sigue en memoria.
    nextTick(() => { measureFrozenCols(); maybeLoadMore() })
    return
  }

  const reqId = ++vistaRequestId

  // 1. Cortar lo que esté en vuelo.
  inventoryRequestId++
  if (availabilityAbort) availabilityAbort.cancel('cambio de sección')
  stopAvailabilityLoading()
  clearTimeout(recargaTimer)
  if (recargaResolve) recargaResolve(false)
  recargaTimer = recargaPromesa = recargaResolve = null
  recargaOverlay = false

  // 2. Vaciar. `apiLoading` en true frena al watcher de filtros: la recarga
  // la hace esta función una sola vez, no una por cada cosa que se limpia.
  apiLoading.value      = true
  loadingPage.value     = false
  estadoActual.value    = estado
  apiAccommodations.value = []
  fullInventory.value   = []
  serverTotal.value     = 0
  serverAgregados.value = null
  pageLoaded.value      = 0
  selected.value        = new Set()
  columnFilters.value   = {}
  sort.value            = { key: null, dir: 'asc' }
  clearSearch()
  dateRange.value       = { start: '', end: '' }
  appliedRange.value    = { start: '', end: '' }
  onlyAvailable.value   = false
  monedaPrecioLink.value = null
  window.scrollTo(0, 0)
  if (gridScrollRef.value) gridScrollRef.value.scrollLeft = 0

  try {
    // 3. Hosts del estado nuevo y primera página.
    if (!hostsPorEstado[estado]) {
      const { data } = await axios.get(apiUrls.backofficeHosts, { params: { estado } })
      if (reqId !== vistaRequestId) return
      hostsPorEstado[estado] = data.hosts || []
    }
    apiHosts.value = hostsPorEstado[estado]

    await reloadInventory()
  } catch (err) {
    if (reqId !== vistaRequestId) return
    console.error('[BackOffice] Error cambiando de sección:', err)
    addToast('No se pudo cargar la sección', 'error')
  } finally {
    if (reqId === vistaRequestId) {
      apiLoading.value = false
      nextTick(() => { measureFrozenCols(); maybeLoadMore() })
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// MÉTODOS – Host Profile Modal
// ─────────────────────────────────────────────────────────────────────────────
// Tope de alojamientos a los que se les pide la galería completa. Coincide con
// el del backend: hay hosts con decenas de miles de alojamientos (Spacest) y
// pedirlas todas reproduciría el problema que la carga a pedido vino a evitar.
const MAX_GALERIAS = 200

/**
 * Trae las galerías completas de los alojamientos del host y las mezcla en el
 * modal. El listado general solo trae `foto_principal`, así que sin esto el
 * carrusel mostraría una sola imagen por alojamiento.
 */
async function cargarGaleriasDelHost (accs) {
  const ids = accs.slice(0, MAX_GALERIAS).map(a => a.id).filter(Boolean)
  if (!ids.length) return

  try {
    const { data } = await axios.get(apiUrls.backofficeGalerias, { params: { ids: ids.join(',') } })
    const galerias = data.galerias || {}

    // Reasignación: el modal ya está abierto y necesita ver el cambio.
    hostProfileModal.value = {
      ...hostProfileModal.value,
      accommodations: hostProfileModal.value.accommodations.map(a => (
        galerias[a.id] ? { ...a, galeria: galerias[a.id] } : a
      )),
    }
  } catch (err) {
    // Sin galería el modal sigue funcionando: cae a la foto principal.
    console.error('[BackOffice] No se pudieron cargar las galerías', err)
  }
}

function openHostProfile (hostId) {
  if (!hostId) return
  const host = apiHosts.value.find(h => String(h.id) === String(hostId))
  if (!host) {
    addToast('Host no encontrado', 'error')
    return
  }
  const accs = apiAccommodations.value.filter(a => String(a.usuario_id) === String(hostId))
  hostProfileModal.value = {
    show: true,
    host,
    accommodations: accs,
    rating: Number(host.puntuacion) || 0,
    savingRating: false,
    tieneGrupoWhatsapp: !!host.tiene_grupo_whatsapp,
    savingWhatsapp: false,
    // Aprobar / eliminar en curso: 'approve-all' | 'delete-all' | 'approve-<id>' | 'delete-<id>'
    accion: null,
    // Edición (solo superadmins)
    editingProfile: false,
    savingProfile: false,
    profileForm: {},
    editingBank: false,
    savingBank: false,
    bankForm: {},
  }
  document.body.style.overflow = 'hidden'

  cargarGaleriasDelHost(accs)
}

// Carga la lista de países (idpais / nombre) para el select de nacionalidad.
async function loadCountries () {
  if (countriesList.value.length) return
  try {
    const { data } = await axios.get(apiUrls.countries)
    countriesList.value = Array.isArray(data) ? data : (data.data || [])
  } catch (err) {
    console.error('[BackOffice] Error cargando países:', err)
  }
}

// ── Edición del PERFIL del host ──────────────────────────────────────────────
function startEditProfile () {
  const modal = hostProfileModal.value
  const h = modal.host || {}
  modal.profileForm = {
    nombre: h.nombre || '',
    apellido: h.apellido || '',
    telefono: h.telefono || '',
    fecha_nacimiento: h.fecha_nacimiento ? String(h.fecha_nacimiento).slice(0, 10) : '',
    pais_idpais: h.pais_idpais || '',
    sexo: h.sexo || '',
    biografia: h.biografia || '',
  }
  modal.editingProfile = true
  loadCountries()
}

function cancelEditProfile () {
  hostProfileModal.value.editingProfile = false
}

async function saveHostProfile () {
  const modal = hostProfileModal.value
  if (!modal.host || modal.savingProfile) return
  const f = modal.profileForm

  if (!f.nombre || !f.apellido || !f.telefono || !f.fecha_nacimiento || !f.pais_idpais || !f.sexo) {
    addToast('Completá nombre, apellido, teléfono, fecha de nacimiento, país y sexo', 'error')
    return
  }

  modal.savingProfile = true
  try {
    const { data } = await axios.put(apiUrls.backofficeUpdateHostProfile(modal.host.id), { ...f })
    const updated = (data && data.host) || f
    // Reflejar en memoria (modal + listado) para que persista sin recargar.
    const apply = (obj) => Object.assign(obj, {
      nombre: updated.nombre,
      apellido: updated.apellido,
      telefono: updated.telefono,
      fecha_nacimiento: updated.fecha_nacimiento,
      pais_idpais: updated.pais_idpais,
      sexo: updated.sexo,
      biografia: updated.biografia,
    })
    apply(modal.host)
    const h = apiHosts.value.find(x => String(x.id) === String(modal.host.id))
    if (h) apply(h)
    modal.editingProfile = false
    addToast('Perfil del host actualizado ✓', 'success', 2500)
  } catch (err) {
    console.error('[BackOffice] Error guardando perfil del host:', err)
    const status = err && err.response ? err.response.status : null
    const msg = status === 403
      ? 'No tenés permisos para editar este host'
      : 'Error al guardar el perfil'
    addToast(msg, 'error')
  } finally {
    modal.savingProfile = false
  }
}

// ── Edición de los DATOS BANCARIOS del host ──────────────────────────────────
function startEditBank () {
  const modal = hostProfileModal.value
  const b = (modal.host && modal.host.bank_account) || {}
  modal.bankForm = {
    first_name: b.first_name || '',
    last_name: b.last_name || '',
    government_id: b.government_id || '',
    phone: b.phone || '',
    email: b.email || '',
    bank_account_number: b.bank_account_number || '',
    bank_code: b.bank_code || '',
    bank_name: b.bank_name || '',
    bank_address: b.bank_address || '',
    branch_code: b.branch_code || '',
    message: b.message || '',
  }
  modal.editingBank = true
}

function cancelEditBank () {
  hostProfileModal.value.editingBank = false
}

async function saveHostBankData () {
  const modal = hostProfileModal.value
  if (!modal.host || modal.savingBank) return

  modal.savingBank = true
  try {
    const { data } = await axios.put(apiUrls.backofficeUpdateHostBankData(modal.host.id), { ...modal.bankForm })
    const saved = (data && data.bank_account) || modal.bankForm
    // Reflejar en memoria (modal + listado).
    const bank = {
      first_name: saved.first_name,
      last_name: saved.last_name,
      government_id: saved.government_id,
      phone: saved.phone,
      email: saved.email,
      bank_account_number: saved.bank_account_number,
      bank_code: saved.bank_code,
      bank_name: saved.bank_name,
      bank_address: saved.bank_address,
      branch_code: saved.branch_code,
      message: saved.message,
    }
    modal.host.bank_account = bank
    const h = apiHosts.value.find(x => String(x.id) === String(modal.host.id))
    if (h) h.bank_account = bank
    modal.editingBank = false
    addToast('Datos bancarios del host actualizados ✓', 'success', 2500)
  } catch (err) {
    console.error('[BackOffice] Error guardando datos bancarios del host:', err)
    const status = err && err.response ? err.response.status : null
    const msg = status === 403
      ? 'No tenés permisos para editar este host'
      : 'Error al guardar los datos bancarios'
    addToast(msg, 'error')
  } finally {
    modal.savingBank = false
  }
}

// Guarda la puntuación (0–4 estrellas) del anfitrión en puntuacion_anfitriones.
// Volver a hacer clic sobre la misma estrella la deselecciona (vuelve a 0).
async function setHostRating (n) {
  const modal = hostProfileModal.value
  if (!modal.host || modal.savingRating) return

  const current   = modal.rating || 0
  const estrellas = (n === current) ? 0 : n
  const prev      = current

  modal.rating = estrellas
  modal.savingRating = true
  try {
    await axios.post(apiUrls.backofficeHostRating, {
      idhost: modal.host.id,
      estrellas,
    })
    // Reflejar en memoria para que persista al reabrir el modal sin recargar.
    const h = apiHosts.value.find(x => String(x.id) === String(modal.host.id))
    if (h) h.puntuacion = estrellas
    modal.host.puntuacion = estrellas
    addToast('Puntuación guardada ✓', 'success', 2500)
  } catch (err) {
    console.error('[BackOffice] Error guardando puntuación:', err)
    modal.rating = prev
    addToast('Error al guardar la puntuación', 'error')
  } finally {
    modal.savingRating = false
  }
}

// Alterna si el anfitrión está en un grupo de WhatsApp (logo gris ↔ color).
async function toggleHostWhatsappGroup () {
  const modal = hostProfileModal.value
  if (!modal.host || modal.savingWhatsapp) return

  const prev = !!modal.tieneGrupoWhatsapp
  const next = !prev

  modal.tieneGrupoWhatsapp = next
  modal.savingWhatsapp = true
  try {
    await axios.post(apiUrls.backofficeHostWhatsappGroup, {
      idhost: modal.host.id,
      tiene_grupo_whatsapp: next,
    })
    // Reflejar en memoria para que persista al reabrir el modal sin recargar.
    const h = apiHosts.value.find(x => String(x.id) === String(modal.host.id))
    if (h) h.tiene_grupo_whatsapp = next
    modal.host.tiene_grupo_whatsapp = next
    addToast(next ? 'Marcado en grupo de WhatsApp ✓' : 'Quitado del grupo de WhatsApp', 'success', 2500)
  } catch (err) {
    console.error('[BackOffice] Error guardando grupo de WhatsApp:', err)
    modal.tieneGrupoWhatsapp = prev
    addToast('Error al guardar el grupo de WhatsApp', 'error')
  } finally {
    modal.savingWhatsapp = false
  }
}

function closeHostProfile () {
  hostProfileModal.value = { show: false, host: null, accommodations: [] }
  document.body.style.overflow = ''
}

// ─────────────────────────────────────────────────────────────────────────────
// MÉTODOS – Aprobar / eliminar (secciones Sin aprobar y Eliminados)
// ─────────────────────────────────────────────────────────────────────────────
// Cuántos alojamientos del host hay en la sección actual. Sale de la API
// (`cantidad_alojamientos` de /hosts?estado=…), no de las filas cargadas: el
// modal solo lista las que ya bajaron en la tabla.
const hostPendientes = computed(() => {
  const h = hostProfileModal.value.host
  return h ? Number(h.cantidad_alojamientos) || 0 : 0
})

/**
 * Aprueba o elimina (borrado lógico) un alojamiento, o todos los del host en
 * esta sección si `acc` es null.
 *   approve → aprobado = 1, eliminado = 0. La API manda 1 correo por host:
 *             el de siempre si es uno, un resumen si son varios.
 *   delete  → eliminado = 1, sin correo. Solo desde Sin aprobar.
 */
async function accionAlojamientos (tipo, acc) {
  const modal  = hostProfileModal.value
  const estado = estadoActual.value
  if (!modal.host || modal.accion || estado === 'activos') return
  if (tipo === 'delete' && estado !== 'sin_aprobar') return

  const n = acc ? 1 : hostPendientes.value
  if (!n) return

  const host = [modal.host.nombre, modal.host.apellido].filter(Boolean).join(' ') || `#${modal.host.id}`
  const plural = n !== 1 ? 's' : ''
  const pregunta = tipo === 'approve'
    ? (acc
        ? `¿Aprobar el alojamiento #${acc.id}?\n\nSe publica y se le envía el correo de aprobación al anfitrión.`
        : `¿Aprobar ${formatInt(n)} alojamiento${plural} de ${host}?\n\nSe publican y el anfitrión recibe ${n > 1 ? 'un solo correo con el resumen' : 'el correo de aprobación'}.`)
    : (acc
        ? `¿Eliminar el alojamiento #${acc.id}?\n\nPasa a la sección Eliminados (no se borra de la base). No se envía correo.`
        : `¿Eliminar ${formatInt(n)} alojamiento${plural} sin aprobar de ${host}?\n\nPasan a la sección Eliminados (no se borran de la base). No se envía correo.`)
  if (!window.confirm(pregunta)) return

  hostProfileModal.value = { ...modal, accion: `${tipo}-${acc ? acc.id : 'all'}` }

  const body = { estado, host_id: modal.host.id }
  if (acc) body.ids = [acc.id]

  try {
    const url = tipo === 'approve' ? apiUrls.backofficeApproveAccommodations : apiUrls.backofficeDeleteAccommodations
    const { data } = await axios.post(url, body)
    const ids = new Set((data.ids || []).map(Number))

    aplicarResultadoAccion(ids, modal.host.id, estado)

    const hechos = ids.size
    let msg = tipo === 'approve'
      ? `${formatInt(hechos)} alojamiento${hechos !== 1 ? 's' : ''} aprobado${hechos !== 1 ? 's' : ''}`
      : `${formatInt(hechos)} alojamiento${hechos !== 1 ? 's' : ''} eliminado${hechos !== 1 ? 's' : ''}`
    if (tipo === 'approve' && data.correos) msg += ' · correo enviado al anfitrión'
    addToast(msg, 'success')
    if (data.correos_fallidos) addToast('Se aprobó, pero no se pudo enviar el correo al anfitrión', 'error', 6000)
  } catch (err) {
    console.error('[BackOffice] Error en la acción', tipo, err)
    addToast(tipo === 'approve' ? 'No se pudo aprobar' : 'No se pudo eliminar', 'error')
  } finally {
    if (hostProfileModal.value.show) hostProfileModal.value = { ...hostProfileModal.value, accion: null }
  }
}

/**
 * Refleja una acción ya hecha en la API: saca las filas del modal y de la
 * selección, descuenta el contador del host y vuelve a pedir el listado.
 *
 * El listado se pide de nuevo desde la página 1 en vez de quitar las filas a
 * mano: la paginación es por número de página, así que al desaparecer filas de
 * las páginas ya cargadas la siguiente se correría y se saltearía alojamientos.
 */
function aplicarResultadoAccion (ids, hostId, estado) {
  if (!ids.size) return

  // Las otras secciones cambiaron (lo aprobado entra en Panel, lo eliminado en
  // Eliminados): sus hosts se vuelven a pedir la próxima vez que se entre.
  Object.keys(hostsPorEstado).forEach(k => { if (k !== estado) delete hostsPorEstado[k] })

  // Si mientras tanto se cambió de sección, el listado ya es otro.
  if (estadoActual.value !== estado) return

  apiHosts.value = apiHosts.value.map(h => (
    String(h.id) === String(hostId)
      ? { ...h, cantidad_alojamientos: Math.max(0, (Number(h.cantidad_alojamientos) || 0) - ids.size) }
      : h
  ))
  hostsPorEstado[estado] = apiHosts.value

  const modal = hostProfileModal.value
  if (modal.show && modal.host && String(modal.host.id) === String(hostId)) {
    hostProfileModal.value = {
      ...modal,
      host: apiHosts.value.find(h => String(h.id) === String(hostId)) || modal.host,
      accommodations: modal.accommodations.filter(a => !ids.has(Number(a.id))),
    }
  }

  if ([...selected.value].some(id => ids.has(Number(id)))) {
    selected.value = new Set([...selected.value].filter(id => !ids.has(Number(id))))
  }

  reloadInventory().then(() => nextTick(maybeLoadMore))
}

function hostHasBankData (host) {
  if (!host || !host.bank_account) return false
  const b = host.bank_account
  return !!(b.bank_name || b.bank_account_number || b.bank_code ||
            b.first_name || b.last_name || b.government_id ||
            b.bank_address || b.branch_code)
}

// ─────────────────────────────────────────────────────────────────────────────
// MÉTODOS – Gallery Slider
// ─────────────────────────────────────────────────────────────────────────────
const galleryIndexes = ref({})
const s3Url = process.env.VUE_APP_S3_URL || ''

function toFullUrl (url) {
  if (!url) return null
  return url.indexOf('http') > -1 ? url : s3Url + url
}

function getAccommodationImages (acc) {
  let urls = []
  if (Array.isArray(acc.galeria) && acc.galeria.length > 0) {
    urls = acc.galeria
  } else if (acc.foto_principal) {
    urls = [acc.foto_principal]
  } else if (acc.foto) {
    urls = [acc.foto]
  } else if (acc.image) {
    urls = [acc.image]
  }
  return urls.map(toFullUrl).filter(Boolean)
}

function getGalleryIndex (accId) {
  return galleryIndexes.value[accId] || 0
}

function setGalleryIndex (accId, idx) {
  galleryIndexes.value = { ...galleryIndexes.value, [accId]: idx }
}

function nextGalleryImage (acc) {
  const imgs = getAccommodationImages(acc)
  if (!imgs.length) return
  const cur = galleryIndexes.value[acc.id] || 0
  setGalleryIndex(acc.id, (cur + 1) % imgs.length)
}

function prevGalleryImage (acc) {
  const imgs = getAccommodationImages(acc)
  if (!imgs.length) return
  const cur = galleryIndexes.value[acc.id] || 0
  setGalleryIndex(acc.id, (cur - 1 + imgs.length) % imgs.length)
}

// ─────────────────────────────────────────────────────────────────────────────
// MÉTODOS – Selección de filas
// ─────────────────────────────────────────────────────────────────────────────
function toggleSelection (id) {
  const s = new Set(selected.value)
  s.has(id) ? s.delete(id) : s.add(id)
  selected.value = s
}

function toggleAll () {
  const s = new Set(selected.value)
  if (allSelected.value) filteredRows.value.forEach(a => s.delete(a.id))
  else filteredRows.value.forEach(a => s.add(a.id))
  selected.value = s
}

// Clic en una celda: el host abre su ficha, el ID deja pasar el enlace y el
// resto de las columnas alterna la selección de la fila.
function onCellClick (col, acc) {
  if (col.key === 'host') {
    if (acc.usuario_id) openHostProfile(acc.usuario_id)
    return
  }
  if (col.key === 'id') return
  toggleSelection(acc.id)
}

// Filas seleccionadas, en el orden en que se ven en la tabla.
const selectedRows = computed(() => filteredRows.value.filter(a => selected.value.has(a.id)))

// ─────────────────────────────────────────────────────────────────────────────
// MÉTODOS – Acciones sobre la selección
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// EXPORTAR A EXCEL
// Exporta el conjunto filtrado COMPLETO (no solo las filas cargadas con el
// scroll): sin filtros es la sección entera; con búsqueda, fechas o filtros de
// columna, exactamente lo que da ese filtro. Se pide a la API en páginas de
// EXPORT_PAGE (el tope de MAX_PER_PAGE del backend) con los mismos parámetros
// que el listado, así el Excel y la tabla nunca pueden diferir.
// ─────────────────────────────────────────────────────────────────────────────
const EXPORT_PAGE        = 1000
const EXPORT_CONCURRENCY = 3     // páginas en paralelo (la API local tarda ~2 s cada una)

const exportando     = ref(false)
const exportHechas   = ref(0)
const exportPaginas  = ref(0)
let   exportAbort    = null
let   exportId       = 0

const exportPorcentaje = computed(() =>
  exportPaginas.value ? Math.min(100, Math.round(exportHechas.value / exportPaginas.value * 100)) : 0
)

const ETIQUETA_SECCION = { activos: 'Panel (activos)', sin_aprobar: 'Sin aprobar', eliminados: 'Eliminados' }

// Hay algo que recorta el listado (para el texto del botón y del aviso).
const hayFiltros = computed(() =>
  !!searchQuery.value.trim() || isApplied.value || Object.keys(columnFilters.value).length > 0
)

const exportTitle = computed(() => {
  const n = formatInt(serverTotal.value)
  return hayFiltros.value
    ? `Descarga en Excel los ${n} resultados del filtro actual`
    : `Descarga en Excel todo el listado de ${ETIQUETA_SECCION[estadoActual.value] || 'la sección'} (${n})`
})

// Precio para la planilla: número (no texto) en la moneda en que se mira la
// tabla, con el mismo redondeo que convertAndFormat. Si no hay tabla de cambio
// queda el importe original con su moneda, igual que en la celda.
function precioParaExcel (acc) {
  const raw = acc.arriendo_total || acc.precio
  if (raw == null || raw === '') return { monto: null, moneda: acc.moneda || '' }
  const conv = priceConverter({ amount: raw, currency: acc.moneda }, visitorCurrency.value || null)
  let monto = Number(conv && conv.amount)
  let moneda = (conv && conv.currency) || visitorCurrency.value || acc.moneda || ''
  if (!Number.isFinite(monto) || (monto === 0 && Number(raw) !== 0)) {
    monto = Number(raw)
    moneda = acc.moneda || ''
  }
  if (!Number.isFinite(monto)) return { monto: null, moneda }
  monto = moneda === 'CLP' ? Math.ceil(monto) : Math.round(monto * 100) / 100
  return { monto, moneda }
}

const COLUMNAS_NUMERICAS_EXCEL = ['id', 'idHost', 'huespedes']

// Columnas de la planilla: las de la tabla, con el precio partido en importe +
// moneda (para poder sumar y ordenar en Excel). Disponibilidad solo con fechas:
// sin rango la columna entera sería "-". `keys` (Set) deja solo las elegidas
// en el popup; null = todas.
function columnasExcel (conDisponibilidad, keys = null) {
  const out = []
  COLUMNS.forEach(c => {
    if (c.key === 'disponible' && !conDisponibilidad) return
    if (keys && !keys.has(c.key)) return
    if (c.key === 'precio') {
      out.push({ label: 'Precio (mes)', width: 14, valor: a => precioParaExcel(a).monto })
      out.push({ label: 'Moneda', width: 8, valor: a => precioParaExcel(a).moneda })
      return
    }
    out.push({
      label: c.label,
      width: Math.max(8, Math.min(60, Math.round(c.width / 7))),
      valor: a => {
        const txt = colText(c, a)
        if (COLUMNAS_NUMERICAS_EXCEL.indexOf(c.key) !== -1 && /^\d+$/.test(txt)) return Number(txt)
        return txt === '-' ? '' : txt
      },
    })
  })
  if (!keys || keys.has('link')) {
    out.push({ label: 'Link', width: 40, valor: a => `${siteUrl}/alojamiento/${a.id}` })
  }
  return out
}

// Hoja "Filtros": qué se exportó, para que el archivo se entienda solo.
function filasDeFiltros (total) {
  const filas = [
    ['Exportado el', new Date().toLocaleString('es-CL')],
    ['Sección', ETIQUETA_SECCION[estadoActual.value] || estadoActual.value],
    ['Resultados', total],
    ['Búsqueda', searchQuery.value.trim() || '(ninguna)'],
  ]
  if (isApplied.value) {
    filas.push(['Fechas', `${appliedRange.value.start} → ${appliedRange.value.end}`])
    filas.push(['Solo disponibles', onlyAvailable.value ? 'Sí' : 'No'])
  }
  const claves = Object.keys(columnFilters.value)
  if (!claves.length) filas.push(['Filtros de columna', '(ninguno)'])
  COLUMNS.forEach(c => {
    const sel = columnFilters.value[c.key]
    if (!sel || !sel.size) return
    const extra = c.key === 'precio' ? ` (${monedaPrecioFiltro()})` : ''
    filas.push([`Filtro: ${c.label}${extra}`, [...sel].join(', ')])
  })
  if (sort.value.key && COL_BY_KEY[sort.value.key]) {
    filas.push(['Orden', `${COL_BY_KEY[sort.value.key].label} (${sort.value.dir === 'desc' ? 'descendente' : 'ascendente'})`])
  }
  filas.push(['Moneda de los precios', displayCurrency.value])
  return filas
}

// Arma y descarga el .xlsx. `filasFiltros` (opcional) es la hoja "Filtros".
function descargarExcel (rows, { filasFiltros = null, conDisp = isApplied.value, nombre, keys = null } = {}) {
  const cols = columnasExcel(conDisp, keys)
  const aoa  = [cols.map(c => c.label)]
  rows.forEach(a => aoa.push(cols.map(c => c.valor(a))))

  const ws = XLSX.utils.aoa_to_sheet(aoa)
  ws['!cols'] = cols.map(c => ({ wch: c.width }))
  ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: aoa.length - 1, c: cols.length - 1 } }) }

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Alojamientos')

  if (filasFiltros) {
    const wsF = XLSX.utils.aoa_to_sheet(filasFiltros)
    wsF['!cols'] = [{ wch: 26 }, { wch: 90 }]
    XLSX.utils.book_append_sheet(wb, wsF, 'Filtros')
  }

  const hoy = new Date().toISOString().slice(0, 10)
  // compression: sin ella xlsx 0.16 guarda el zip sin comprimir (~30 MB el Panel entero).
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array', compression: true })
  saveAs(
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `${nombre}-${hoy}.xlsx`
  )
}

async function exportarExcel (keys = null) {
  if (exportando.value || !serverTotal.value) return

  const miId = ++exportId
  // Foto de los parámetros y de los hosts AL APRETAR: si se toca un filtro o se
  // cambia de pestaña mientras descarga, el archivo sigue siendo el que se pidió.
  const params  = inventoryParams({ per_page: EXPORT_PAGE, agregados: 0 })
  const hostMap = {}
  apiHosts.value.forEach(h => { hostMap[h.id] = h })
  const filasFiltros = filasDeFiltros(0)
  const conDisp = isApplied.value
  if (keys) filasFiltros.push(['Columnas', columnasExcel(conDisp, keys).map(c => c.label).join(', ')])
  const nombre  = `backoffice-${estadoActual.value}${hayFiltros.value ? '-filtrado' : ''}`

  const source = axios.CancelToken.source()
  exportAbort = source
  exportando.value   = true
  exportHechas.value = 0
  exportPaginas.value = Math.max(1, Math.ceil(serverTotal.value / EXPORT_PAGE))

  // POST: mismos filtros que el listado, en el body (ver fetchInventoryPage()).
  const pedir = page => axios.post(apiUrls.backofficeInventory, { ...params, page }, {
    cancelToken: source.token,
    timeout: AVAILABILITY_TIMEOUT_MS,
  }).then(({ data }) => {
    if (miId === exportId) exportHechas.value += 1
    return data
  })

  try {
    // La primera página dice el total real (pudo cambiar desde la última carga).
    const primera = await pedir(1)
    const total   = Number(primera.total) || 0
    const paginas = Math.max(1, Math.ceil(total / EXPORT_PAGE))
    exportPaginas.value = paginas

    const porPagina = new Array(paginas)
    porPagina[0] = primera.accommodations || []

    let siguiente = 2
    const trabajador = async () => {
      while (siguiente <= paginas) {
        const page = siguiente++
        const data = await pedir(page)
        porPagina[page - 1] = data.accommodations || []
      }
    }
    await Promise.all(Array.from({ length: Math.min(EXPORT_CONCURRENCY, paginas - 1) }, trabajador))
    if (miId !== exportId) return

    // Por si una fila cae entre dos páginas mientras alguien edita la base.
    const vistos = new Set()
    const rows = []
    porPagina.forEach(lista => mapRows(lista, hostMap).forEach(r => {
      if (vistos.has(r.id)) return
      vistos.add(r.id)
      rows.push(r)
    }))

    if (!rows.length) {
      addToast('No hay resultados para exportar', 'info')
      return
    }

    // La hoja de filtros se arma con la foto del momento del clic.
    filasFiltros[2] = ['Resultados', rows.length]
    descargarExcel(rows, { filasFiltros, conDisp, nombre, keys })
    addToast(`${formatInt(rows.length)} alojamiento${rows.length !== 1 ? 's' : ''} exportado${rows.length !== 1 ? 's' : ''} a Excel ✓`, 'success')
  } catch (err) {
    if (axios.isCancel(err) || miId !== exportId) return
    console.error('[BackOffice] Error exportando:', err)
    addToast(
      err && err.code === 'ECONNABORTED'
        ? 'La exportación tardó demasiado. Acota los filtros y vuelve a intentarlo.'
        : 'No se pudo exportar el listado',
      'error',
    )
  } finally {
    if (miId === exportId) {
      exportando.value = false
      exportAbort = null
    }
  }
}

// ── Popup para elegir columnas ───────────────────────────────────────────────
// Se abre desde "Exportar Excel" (modo filtro: todo lo filtrado) y desde la
// barra de selección (modo seleccion: solo las filas marcadas). La elección se
// guarda en este navegador; "Exportar todo" la ignora sin borrarla.
const EXPORT_COLS_LS = 'bo_export_columnas'

const exportModal = ref({ show: false, modo: 'filtro' })

// Columnas ofrecidas: las de la tabla (Precio incluye su moneda) + el link.
// Disponibilidad solo con fechas aplicadas (sin rango sería todo "-").
const opcionesExport = computed(() => [
  ...COLUMNS
    .filter(c => c.key !== 'disponible' || isApplied.value)
    .map(c => ({ key: c.key, label: c.key === 'precio' ? 'Precio (y moneda)' : c.label })),
  { key: 'link', label: 'Link a la ficha' },
])

function colsExportGuardadas () {
  try {
    const arr = JSON.parse(localStorage.getItem(EXPORT_COLS_LS) || 'null')
    if (Array.isArray(arr)) return new Set(arr.filter(k => k === 'link' || COL_BY_KEY[k]))
  } catch (e) { /* valor roto: se usan todas */ }
  return null
}

const exportColsSel = ref(new Set())

const exportModalResumen = computed(() => {
  if (exportModal.value.modo === 'seleccion') {
    const n = selected.value.size
    return `Se exportarán las ${formatInt(n)} fila${n !== 1 ? 's' : ''} seleccionada${n !== 1 ? 's' : ''}. Elige las columnas o exporta todo.`
  }
  const n = formatInt(serverTotal.value)
  return hayFiltros.value
    ? `Se exportarán los ${n} resultados del filtro actual. Elige las columnas o exporta todo.`
    : `Se exportará todo el listado de ${ETIQUETA_SECCION[estadoActual.value] || 'la sección'} (${n}). Elige las columnas o exporta todo.`
})

function abrirExportModal (modo) {
  if (modo === 'filtro' && (exportando.value || !serverTotal.value)) return
  const guardadas = colsExportGuardadas()
  exportColsSel.value = new Set(
    opcionesExport.value.map(o => o.key).filter(k => !guardadas || guardadas.has(k))
  )
  exportModal.value = { show: true, modo }
  nextTick(() => {
    const el = document.querySelector('.bo-exportmodal input[type="checkbox"]')
    if (el) el.focus()
  })
}

function cerrarExportModal () {
  exportModal.value = { ...exportModal.value, show: false }
}

// Set reasignado: Vue 2.7 no observa mutaciones de Set.
function toggleColExport (key) {
  const next = new Set(exportColsSel.value)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  exportColsSel.value = next
}

function marcarTodasExport (on) {
  exportColsSel.value = on ? new Set(opcionesExport.value.map(o => o.key)) : new Set()
}

function confirmarExport (todo) {
  const ofrecidas = opcionesExport.value.map(o => o.key)
  let keys = null
  if (!todo) {
    if (!exportColsSel.value.size) return
    // Se guarda lo marcado y también lo que no se ofreció ahora (Disponibilidad
    // sin fechas) según estaba, para no perder esa preferencia.
    const previas = colsExportGuardadas()
    const guardar = [...exportColsSel.value]
    if (ofrecidas.indexOf('disponible') === -1 && (!previas || previas.has('disponible'))) guardar.push('disponible')
    try { localStorage.setItem(EXPORT_COLS_LS, JSON.stringify(guardar)) } catch (e) { /* sin espacio: no importa */ }
    // Todas marcadas equivale a exportar todo.
    if (ofrecidas.some(k => !exportColsSel.value.has(k))) keys = new Set(exportColsSel.value)
  }
  const modo = exportModal.value.modo
  cerrarExportModal()
  if (modo === 'seleccion') exportSelected(keys)
  else exportarExcel(keys)
}

function cancelarExport () {
  exportId++
  if (exportAbort) exportAbort.cancel('exportación cancelada')
  exportAbort = null
  exportando.value = false
  addToast('Exportación cancelada', 'info', 2500)
}

// Exportar desde la barra de selección: solo las filas marcadas.
function exportSelected (keys = null) {
  const rows = selectedRows.value
  if (!rows.length) return
  descargarExcel(rows, { nombre: `backoffice-${estadoActual.value}-seleccion`, keys })
  addToast(`${rows.length} alojamiento${rows.length !== 1 ? 's' : ''} exportado${rows.length !== 1 ? 's' : ''} a Excel ✓`, 'success')
}

// ─────────────────────────────────────────────────────────────────────────────
// LA VISTA EN LA URL
// La barra de direcciones refleja lo que se está viendo (sección, búsqueda,
// fechas, "solo disponibles", filtros de columna y orden): al recargar se
// vuelve a la misma vista, y siguen funcionando los links que llegan desde
// otras pantallas (?host= desde Reservas, ?alojamiento= desde la ficha).
// Ya no hay botón "Compartir": con muchos valores marcados el link no entraba.
//
//   seccion=sin_aprobar | eliminados   (activos no se escribe: es el defecto)
//   q=<texto>
//   desde=YYYY-MM-DD&hasta=YYYY-MM-DD&disp=0|1
//   orden=<columna>&dir=asc|desc
//   f_<columna>=<valor>  (repetido, un parámetro por valor: los valores pueden
//                         traer comas y barras, como las direcciones)
//   moneda=<código>      (solo con filtro de precio: los tramos son de esa moneda)
//
// Siguen funcionando los links viejos ?alojamiento=<id> y ?host=<idHost>.
// ─────────────────────────────────────────────────────────────────────────────
const FECHA_RE = /^\d{4}-\d{2}-\d{2}$/

// Moneda de los tramos del link abierto, mientras no se toque el filtro de precio.
const monedaPrecioLink = ref(null)

function monedaPrecioFiltro () {
  return monedaPrecioLink.value || displayCurrency.value
}

// Se activa al terminar de leer la URL: antes, escribirla la vaciaría.
let urlSincronizada = false

function queryDeVista ({ sinFiltrosColumna = false } = {}) {
  const p = new URLSearchParams()
  if (estadoActual.value !== 'activos') p.set('seccion', estadoActual.value)

  const texto = searchQuery.value.trim()
  if (texto) p.set('q', texto)

  if (isApplied.value) {
    p.set('desde', appliedRange.value.start)
    p.set('hasta', appliedRange.value.end)
    p.set('disp', onlyAvailable.value ? '1' : '0')
  }

  // En el orden de las columnas, para que dos vistas iguales den el mismo link.
  COLUMNS.forEach(c => {
    if (sinFiltrosColumna) return
    const sel = columnFilters.value[c.key]
    if (!sel || !sel.size) return
    ;[...sel].sort(compareOptions).forEach(v => p.append(`f_${c.key}`, v))
  })
  if (columnFilters.value.precio && !sinFiltrosColumna) p.set('moneda', monedaPrecioFiltro())

  if (sort.value.key) {
    p.set('orden', sort.value.key)
    p.set('dir', sort.value.dir === 'desc' ? 'desc' : 'asc')
  }
  return p
}

// Tope de la query string de la vista. Con cientos de valores marcados (p. ej.
// direcciones) la URL pasaba de 50 KB: los servidores la rechazan (414) y
// Analytics, que manda la URL de la página en cada hit, fallaba con 411.
// Pasado el tope, la URL deja fuera los filtros de columna.
const MAX_QS_VISTA = 4000

function queryDeVistaAcotada () {
  const completa = queryDeVista().toString()
  if (completa.length <= MAX_QS_VISTA) return { qs: completa, recortada: false }
  return { qs: queryDeVista({ sinFiltrosColumna: true }).toString(), recortada: true }
}

// replaceState y no router.replace: no agrega entradas al historial (cada
// tecla del buscador sería un "atrás") ni hace pasar la página por el router.
function sincronizarUrl () {
  if (!urlSincronizada || activeView.value === 'data') return
  const { qs } = queryDeVistaAcotada()
  const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`
  const actual = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (next !== actual) window.history.replaceState(window.history.state, '', next)
}

watch(() => `${filtrosFirma.value}|${estadoActual.value}|${monedaPrecioLink.value}|${activeView.value}`, sincronizarUrl)

/**
 * Lee la URL y deja el estado listo ANTES de la primera consulta, así sale una
 * sola, ya filtrada. Los filtros de host se guardan como texto y se resuelven a
 * ids recién al consultar (inventoryParams), cuando los hosts ya llegaron.
 * Todo se valida: un link manipulado no puede meter columnas ni fechas raras.
 */
function leerVistaDeUrl () {
  const query = new URLSearchParams(window.location.search)
  const VISTA_POR_SECCION = { activos: 'dashboard', sin_aprobar: 'sin_aprobar', eliminados: 'eliminados' }

  const vista = VISTA_POR_SECCION[query.get('seccion')]
  if (vista) {
    activeView.value   = vista
    estadoActual.value = ESTADO_POR_VISTA[vista]
  }

  const texto = (query.get('q') || '').slice(0, 200)
  if (texto.trim()) {
    searchInput.value = texto
    searchQuery.value = texto
  }

  // Fechas: solo en Panel (el botón de disponibilidad solo existe ahí).
  const desde = query.get('desde')
  const hasta = query.get('hasta')
  if (estadoActual.value === 'activos' && FECHA_RE.test(desde || '') && FECHA_RE.test(hasta || '') && hasta > desde) {
    dateRange.value    = { start: desde, end: hasta }
    appliedRange.value = { start: desde, end: hasta }
    onlyAvailable.value = query.get('disp') !== '0'
  }

  const filtros = {}
  COLUMNS.forEach(c => {
    if (c.filterable === false) return
    if (c.key === 'disponible' && !appliedRange.value.start) return
    const valores = query.getAll(`f_${c.key}`).filter(v => v !== '').slice(0, 2000)
    if (valores.length) filtros[c.key] = new Set(valores)
  })

  // Links viejos: desde la ficha (?alojamiento=) y desde Reservas (?host=).
  const alojamiento = query.get('alojamiento')
  if (alojamiento && /^\d+$/.test(alojamiento) && !filtros.id) filtros.id = new Set([alojamiento])
  const host = query.get('host')
  if (host && /^\d+$/.test(host) && !filtros.idHost) filtros.idHost = new Set([host])

  columnFilters.value = filtros

  const moneda = (query.get('moneda') || '').toUpperCase()
  if (filtros.precio && /^[A-Z]{3}$/.test(moneda)) monedaPrecioLink.value = moneda

  const orden = query.get('orden')
  if (orden && COL_BY_KEY[orden]) {
    sort.value = { key: orden, dir: query.get('dir') === 'desc' ? 'desc' : 'asc' }
  }

  // ¿Vino algo para mostrar? (para el aviso de "abriste una vista compartida")
  return !!(texto.trim() || appliedRange.value.start || Object.keys(filtros).length || sort.value.key ||
    query.get('seccion'))
}

function copySelectedLinks () {
  const rows = selectedRows.value
  if (!rows.length) return
  const links = rows.map(a => `${siteUrl}/alojamiento/${a.id}`).join('\n')
  if (!navigator.clipboard) {
    addToast('Clipboard no disponible en este navegador', 'error')
    return
  }
  navigator.clipboard.writeText(links)
    .then(() => addToast(`${rows.length} enlace${rows.length !== 1 ? 's' : ''} copiado${rows.length !== 1 ? 's' : ''} ✓`, 'success'))
    .catch(() => addToast('No se pudieron copiar los enlaces', 'error'))
}

// Reservado para la futura integración de mensajería: por ahora no hace nada.
function contactSelected () {}

// ─────────────────────────────────────────────────────────────────────────────
// MÉTODOS – Modal Push / Email
// ─────────────────────────────────────────────────────────────────────────────
// Abre el modal con los correos (sin repetir) de los anfitriones seleccionados.
function openEmailModal () {
  const emails = [...new Set(
    selectedRows.value
      .map(a => a.host_email || hostEmail(a))
      .filter(e => e && e !== '-' && String(e).trim() !== '')
  )]
  if (!emails.length) {
    addToast('No hay emails válidos en la selección', 'error')
    return
  }
  pushEmail.value = {
    subject: '',
    body: '',
    recipients: emails,
    recipientInput: '',
    sending: false,
    sent: false,
    sendError: false,
  }
  emailModal.value = true
  document.body.style.overflow = 'hidden'
}

function closeEmailModal () {
  emailModal.value = false
  document.body.style.overflow = ''
}

// Vista previa del email: primero se escapa el HTML y después se aplican las
// transformaciones de markdown básicas.
const parsedPreview = computed(() => {
  const raw = pushEmail.value.body
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  return raw
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/_(.*?)_/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, '<code>$1</code>')
    .replace(/\n/g, '<br>')
})

function wrapText (before, after) {
  const ta = editorRef.value
  if (!ta) return
  const { selectionStart: s, selectionEnd: e, value } = ta
  pushEmail.value.body = value.slice(0, s) + before + value.slice(s, e) + after + value.slice(e)
  nextTick(() => { ta.focus(); ta.setSelectionRange(s + before.length, e + before.length) })
}

function insertBullet () {
  const ta = editorRef.value
  if (!ta) return
  const { selectionStart: s, value } = ta
  const prefix = s > 0 && value[s - 1] !== '\n' ? '\n• ' : '• '
  pushEmail.value.body = value.slice(0, s) + prefix + value.slice(s)
  nextTick(() => { ta.focus(); ta.setSelectionRange(s + prefix.length, s + prefix.length) })
}

function addRecipient () {
  const email = pushEmail.value.recipientInput.trim().replace(/,$/, '')
  if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !pushEmail.value.recipients.includes(email)) {
    pushEmail.value.recipients.push(email)
  }
  pushEmail.value.recipientInput = ''
}

function removeRecipient (r) {
  pushEmail.value.recipients = pushEmail.value.recipients.filter(x => x !== r)
}

async function sendPushEmail () {
  if (!pushEmail.value.recipients.length) {
    addToast('Debes agregar al menos un destinatario', 'error')
    return
  }
  if (!pushEmail.value.subject || pushEmail.value.subject.trim() === '') {
    addToast('Debes agregar un asunto', 'error')
    return
  }
  if (!pushEmail.value.body || pushEmail.value.body.trim() === '') {
    addToast('Debes agregar un mensaje', 'error')
    return
  }

  pushEmail.value.sending = true
  pushEmail.value.sent = false
  pushEmail.value.sendError = false

  try {
    const payload = {
      recipients: pushEmail.value.recipients,
      subject: pushEmail.value.subject.trim(),
      body: pushEmail.value.body.trim(),
    }

    addToast('Enviando emails...', 'info', 2000)

    const response = await axios.post(apiUrls.backofficeSendBulkEmail, payload)

    if (response.data && response.data.success) {
      const { summary } = response.data
      pushEmail.value.sent = true
      pushEmail.value.sending = false

      if (summary.errors > 0) {
        addToast(`Emails enviados: ${summary.success} exitosos, ${summary.errors} fallidos`, 'info', 5000)
      } else {
        addToast(`${summary.success} email${summary.success !== 1 ? 's' : ''} enviado${summary.success !== 1 ? 's' : ''} correctamente ✓`, 'success', 4000)
      }

      // Cerrar el modal y limpiar la selección un instante después.
      setTimeout(() => {
        closeEmailModal()
        pushEmail.value.subject = ''
        pushEmail.value.body = ''
        pushEmail.value.recipients = []
        pushEmail.value.sent = false
        selected.value = new Set()
      }, 1500)
    } else {
      throw new Error(response.data.message || 'Error desconocido')
    }
  } catch (error) {
    pushEmail.value.sendError = true
    pushEmail.value.sending = false
    const errorMsg = (error.response && error.response.data && error.response.data.message) || error.message || 'Error al enviar emails'
    addToast(errorMsg, 'error', 6000)
    console.error('[BackOffice] Error enviando emails:', error)
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// MÉTODOS – Scroll infinito
// ─────────────────────────────────────────────────────────────────────────────
// Carga la siguiente tanda cuando el centinela del final entra en pantalla y
// vuelve a comprobarlo, por si la tanda nueva tampoco llena el viewport.
function maybeLoadMore () {
  const el = sentinelRef.value
  if (!el || loadingPage.value || !hasMore.value) return
  const rect = el.getBoundingClientRect()
  if (rect.top > window.innerHeight + 300) return

  // Antes esto revelaba otras 100 filas de un array que ya estaba entero en
  // memoria; ahora pide la página siguiente. Se vuelve a comprobar al terminar,
  // por si la página nueva tampoco llena el viewport.
  fetchInventoryPage().then(ok => { if (ok) nextTick(maybeLoadMore) })
}

let scrollTicking = false
// En captura, para enterarse también del scroll horizontal de la tabla.
function onAnyScroll (e) {
  const t = e && e.target
  const insideMenu = t && t.closest && t.closest('.bo-colmenu')
  if (menu.value.col && !insideMenu) closeColumnMenu()
  if (scrollTicking) return
  scrollTicking = true
  requestAnimationFrame(() => {
    scrollTicking = false
    maybeLoadMore()
  })
}

function onWindowResize () {
  // Cubre tanto el redimensionado de la ventana como el zoom del navegador.
  viewportWidth.value = window.innerWidth
  if (menu.value.col) closeColumnMenu()
  stopEdgeScroll()
  hoveredCol.value = null
  maybeLoadMore()
  nextTick(measureFrozenCols)
}

function onKeydown (e) {
  if (e.key !== 'Escape') return
  if (menu.value.col) closeColumnMenu()
  else if (exportModal.value.show) cerrarExportModal()
  else if (availModal.value) closeAvailModal()
  else if (emailModal.value && !pushEmail.value.sending) closeEmailModal()
}

// ─────────────────────────────────────────────────────────────────────────────
// LIFECYCLE
// ─────────────────────────────────────────────────────────────────────────────
onMounted(async () => {
  window.addEventListener('scroll', onAnyScroll, true)
  window.addEventListener('resize', onWindowResize)
  window.addEventListener('keydown', onKeydown)

  nextTick(measureFrozenCols)

  // Vista desde la URL: link compartido (filtros, fechas, búsqueda, orden) o
  // deep-link viejo desde la ficha (?seccion=&alojamiento=) o Reservas (?host=).
  // Se aplica ANTES de la primera carga para que salga una sola consulta.
  const vinoDeLink = leerVistaDeUrl()
  urlSincronizada = true
  sincronizarUrl()

  await loadApiData()
  nextTick(measureFrozenCols)
  // Cargar países solo si el usuario puede editar el perfil de hosts
  if (canEditHost.value) loadCountries()

  if (vinoDeLink && monedaPrecioLink.value && monedaPrecioLink.value !== displayCurrency.value) {
    addToast(`El filtro de precio del link está en ${monedaPrecioLink.value}`, 'info', 5000)
  }

  nextTick(maybeLoadMore)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onAnyScroll, true)
  window.removeEventListener('resize', onWindowResize)
  window.removeEventListener('keydown', onKeydown)
  clearTimeout(searchTimer)
  stopEdgeScroll()

  // Cortar la consulta de disponibilidad y su cronómetro: si se sale del
  // BackOffice a mitad de un cálculo, el intervalo seguía corriendo.
  inventoryRequestId++
  if (availabilityAbort) availabilityAbort.cancel('componente desmontado')
  exportId++
  if (exportAbort) exportAbort.cancel('componente desmontado')
  clearInterval(loadingTimer)
  loadingTimer = null
})

</script>

<style scoped>
/* Mono de la marca (misma familia que HomeNuevo) para los microlabels. */
@import url('https://fonts.googleapis.com/css2?family=Geist+Mono:wght@400;500&display=swap');

/* ────────────────────────────────────────────────────────
   VARIABLES CSS
──────────────────────────────────────────────────────── */
.bo {
  --c-primary:   #F97316;
  --c-secondary: #EA580C;
  --c-bg:        #F9FAFB;
  --c-card:      #FFFFFF;
  --c-text:      #111827;
  --c-muted:     #6B7280;
  --c-border:    #E5E7EB;
  --c-success:   #10B981;
  --c-warning:   #F59E0B;
  --c-danger:    #EF4444;
  --radius-sm:   8px;
  --radius:      12px;
  --radius-lg:   16px;
  --shadow:      0 1px 3px rgba(0,0,0,.07), 0 2px 8px rgba(0,0,0,.05);
  --shadow-md:   0 4px 14px rgba(0,0,0,.10), 0 2px 6px rgba(0,0,0,.06);
  --shadow-lg:   0 12px 32px rgba(0,0,0,.14);
  --transition:  0.2s ease;
  min-height: 100vh;
  background: transparent; /* fondo de marca del sitio (HyFFondo) */
  color: var(--c-text);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  /* Red de contención: nada del panel debe empujar la página a lo ancho. */
  overflow-x: clip;
}
/* Con border-box los anchos en % nunca se desbordan al sumarles padding: es la
   base para que el layout aguante el zoom y las pantallas chicas. */
.bo *,
.bo *::before,
.bo *::after { box-sizing: border-box; }

/* La tabla queda fuera: los anchos de COLUMNS están calibrados con la caja de
   contenido y cambiarlos angostaría todas las columnas. */
.bo-grid__table col,
.bo-grid__table th,
.bo-grid__table td { box-sizing: content-box; }

/* ────────────────────────────────────────────────────────
   NAVEGACIÓN DE VISTAS (Panel / Descargar datos)
──────────────────────────────────────────────────────── */
.bo-viewnav {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  margin-bottom: 20px;
  background: var(--c-bg);
  border: 1.5px solid var(--c-border);
  border-radius: var(--radius);
}
.bo-viewnav__tab {
  padding: 6px 14px;
  border: none;
  background: transparent;
  color: var(--c-muted);
  font-size: .85rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: all var(--transition);
  white-space: nowrap;
}
.bo-viewnav__tab:hover { color: var(--c-primary); }
.bo-viewnav__tab--active {
  background: var(--c-card);
  color: var(--c-primary);
  box-shadow: var(--shadow);
}

/* ────────────────────────────────────────────────────────
   MAIN
──────────────────────────────────────────────────────── */
/* El main ocupa todo el ancho para que el listado también lo haga; la cabecera
   se centra dentro de .bo-container. Antes esto se resolvía con un full-bleed
   de 100vw que, con la barra de scroll de Windows, desbordaba la página. */
.bo-main {
  width: 100%;
  padding: 24px clamp(12px, 1.8vw, 24px);
}
.bo-container {
  max-width: 1280px;
  margin-inline: auto;
}

/* ────────────────────────────────────────────────────────
   FILTRO POR FECHAS (disponibilidad)
   Tipografía y trazos tomados de HomeNuevo: microlabels en
   mono, bordes hairline, pills y acento naranja.
──────────────────────────────────────────────────────── */
/* Tokens del bloque de disponibilidad (botón + popup) */
.bo-toolbar,
.bo-availmodal {
  --avail-line:   #E7E5DF;
  --avail-ink:    #0A0A0B;
  --avail-dim:    #6E6E76;
  --avail-soft:   #FFF1E5;
  --avail-accent: #FC7010;
  --avail-accent-text: #B84800;
}

/* ── Botón que abre el popup ── */
.bo-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 22px;
}
.bo-availbtn {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  max-width: 100%;
  padding: 10px 18px;
  border: 0.5px solid #DBD9D2;
  border-radius: 999px;
  background: var(--c-card);
  color: var(--avail-ink);
  font-family: inherit;
  font-size: .87rem;
  font-weight: 500;
  letter-spacing: -.01em;
  cursor: pointer;
  white-space: nowrap;
  transition: all .16s ease;
  box-shadow: 0 1px 2px rgba(10,10,11,.04);
}
.bo-availbtn__label { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.bo-availbtn:hover {
  border-color: var(--avail-ink);
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(10,10,11,.10);
}
.bo-availbtn svg { color: var(--avail-dim); flex-shrink: 0; }
.bo-availbtn:hover svg { color: var(--avail-accent); }

/* Con rango aplicado el botón queda encendido y muestra el resumen */
.bo-availbtn--on {
  border-color: #FFD4AE;
  background: var(--avail-soft);
  color: var(--avail-accent-text);
}
.bo-availbtn--on svg,
.bo-availbtn--on:hover svg { color: var(--avail-accent); }
.bo-availbtn--on:hover { border-color: var(--avail-accent); }
.bo-availbtn__range {
  font-family: 'Geist Mono', ui-monospace, 'JetBrains Mono', monospace;
  font-size: 11px;
  letter-spacing: .02em;
  padding-left: 9px;
  border-left: 0.5px solid rgba(184,72,0,.25);
}
.bo-availbtn__count {
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(16,185,129,.12);
  color: #047857;
  font-family: 'Geist Mono', ui-monospace, 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 500;
}
.bo-availbtn__clear {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0.5px solid #DBD9D2;
  border-radius: 50%;
  background: transparent;
  color: var(--c-muted);
  font-size: 1.15rem;
  line-height: 1;
  cursor: pointer;
  transition: all .16s ease;
}
.bo-availbtn__clear:hover { border-color: var(--c-danger); color: var(--c-danger); }

/* Aviso de "hay unidades sin evaluar". Va en ámbar y no en el naranja de la
   marca: no es un filtro activo más, es algo que quedó a medias y hay que
   resolver. Es un botón porque la salida es una sola: reaplicar. */
.bo-availbtn__redo {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 13px;
  border: 0.5px solid #E8C89A;
  border-radius: 999px;
  background: rgba(217,151,32,.10);
  color: #8A5A00;
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: all .16s ease;
}
.bo-availbtn__redo:hover:not(:disabled) { border-color: #D99720; background: rgba(217,151,32,.18); }
.bo-availbtn__redo:disabled { opacity: .5; cursor: default; }

/* ── Buscador global (mismo lenguaje que el botón de disponibilidad) ── */
.bo-search {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  max-width: 420px;
  flex: 1 1 260px;
  padding: 9px 14px;
  border: 0.5px solid #DBD9D2;
  border-radius: 999px;
  background: var(--c-card);
  box-shadow: 0 1px 2px rgba(10,10,11,.04);
  transition: all .16s ease;
}
.bo-search:hover { border-color: var(--avail-ink); }
.bo-search:focus-within {
  border-color: var(--avail-accent);
  box-shadow: 0 6px 16px rgba(10,10,11,.10);
}
.bo-search svg { color: var(--avail-dim); flex-shrink: 0; }
.bo-search:focus-within svg,
.bo-search--on svg { color: var(--avail-accent); }
.bo-search--on { border-color: #FFD4AE; background: var(--avail-soft); }
.bo-search__input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  color: var(--avail-ink);
  font-family: inherit;
  font-size: .87rem;
  font-weight: 500;
  letter-spacing: -.01em;
}
.bo-search__input::placeholder { color: var(--avail-dim); font-weight: 400; }
.bo-search__count {
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(252,112,16,.14);
  color: var(--avail-accent-text);
  font-family: 'Geist Mono', ui-monospace, 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
}
.bo-search__clear {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--c-muted);
  font-size: 1.05rem;
  line-height: 1;
  cursor: pointer;
  transition: all .16s ease;
}
.bo-search__clear:hover { background: rgba(10,10,11,.06); color: var(--avail-ink); }

/* ── Exportar Excel: a la derecha de la barra ── */
.bo-tools {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}
.bo-toolbtn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 0.5px solid #DBD9D2;
  border-radius: 999px;
  background: var(--c-card);
  color: var(--avail-ink);
  font-family: inherit;
  font-size: .87rem;
  font-weight: 500;
  letter-spacing: -.01em;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(10,10,11,.04);
  transition: all .16s ease;
}
.bo-toolbtn svg { color: var(--avail-dim); flex-shrink: 0; }
.bo-toolbtn:hover:not(:disabled) {
  border-color: var(--avail-ink);
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(10,10,11,.10);
}
.bo-toolbtn:hover:not(:disabled) svg { color: var(--avail-accent); }
.bo-toolbtn:focus-visible { outline: 2px solid var(--avail-accent); outline-offset: 2px; }
.bo-toolbtn:disabled { opacity: .55; cursor: default; }
.bo-toolbtn--busy:disabled {
  opacity: 1;
  border-color: #FFD4AE;
  background: var(--avail-soft);
  color: var(--avail-accent-text);
}
.bo-toolbtn__count {
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(10,10,11,.06);
  color: var(--avail-dim);
  font-family: 'Geist Mono', ui-monospace, 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 500;
}

/* ── Popup de columnas a exportar (sobre el armazón de .bo-availmodal) ── */
.bo-exportmodal { max-width: 620px; }
.bo-exportmodal .bo-availmodal__body { max-height: calc(76vh - 130px); overflow-y: auto; }
.bo-exportmodal__intro { margin: 0 0 14px; color: var(--avail-dim); font-size: .86rem; line-height: 1.45; }
.bo-exportmodal__bar { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 14px; margin-bottom: 12px; }
.bo-exportmodal__count { margin-right: auto; font-size: .8rem; font-weight: 600; color: var(--avail-ink); }
.bo-exportmodal__link {
  padding: 0; border: 0; background: none; cursor: pointer;
  color: var(--avail-accent-text); font-family: inherit; font-size: .8rem; font-weight: 500;
}
.bo-exportmodal__link:hover { text-decoration: underline; }
.bo-exportmodal__link:focus-visible { outline: 2px solid var(--avail-accent); outline-offset: 2px; border-radius: 4px; }
.bo-exportmodal__cols {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 8px;
  margin: 0; padding: 0; border: 0; min-width: 0;
}
.bo-exportmodal__col {
  display: flex; align-items: center; gap: 9px; margin: 0; padding: 9px 12px;
  border: 0.5px solid var(--avail-line); border-radius: 10px; cursor: pointer;
  font-size: .84rem; color: var(--avail-ink); transition: all .14s ease;
}
.bo-exportmodal__col:hover { border-color: var(--avail-ink); }
.bo-exportmodal__col--on { border-color: #FFD4AE; background: var(--avail-soft); }
.bo-exportmodal__col input { width: 15px; height: 15px; margin: 0; flex-shrink: 0; accent-color: var(--avail-accent); }
.bo-exportmodal__col:focus-within { outline: 2px solid var(--avail-accent); outline-offset: 1px; }
.bo-sr-only {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
}
@media (max-width: 640px) {
  .bo-exportmodal__cols { grid-template-columns: 1fr 1fr; }
  .bo-exportmodal .bo-availmodal__footer { flex-wrap: wrap; }
  .bo-exportmodal .bo-avail__btn { flex: 1 1 auto; }
}

/* ── Popup ── */
.bo-avail-overlay { align-items: flex-start; padding-top: 12vh; backdrop-filter: blur(3px); }
.bo-availmodal {
  width: 100%;
  max-width: 520px;
  background: var(--c-card);
  border: 0.5px solid var(--avail-line);
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(10,10,11,.22);
  overflow: hidden;
}
.bo-availmodal__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px 14px 20px;
  border-bottom: 0.5px solid var(--avail-line);
}
.bo-availmodal__head .bo-modal__close { margin-left: auto; }
.bo-availmodal__body { padding: 20px; }
.bo-availmodal__state {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 16px;
}
.bo-availmodal__state .bo-avail__chip { margin-left: 0; }
.bo-availmodal__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 0.5px solid var(--avail-line);
  background: #FAFAF9;
}

.bo-avail__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: 'Geist Mono', ui-monospace, 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: .06em;
  text-transform: uppercase;
  color: var(--avail-accent-text);
}
.bo-avail__eyebrow::before {
  content: '';
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--avail-accent);
  box-shadow: 0 0 8px rgba(252,112,16,.6);
}

/* Chip de estado (sin aplicar / calculando / resultado) */
.bo-avail__chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 4px 11px;
  border-radius: 999px;
  border: 0.5px solid transparent;
  font-family: 'Geist Mono', ui-monospace, 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: .02em;
  white-space: nowrap;
}
.bo-avail__chip--ok      { background: rgba(16,185,129,.10); border-color: rgba(16,185,129,.30); color: #047857; }
.bo-avail__chip--pending { background: var(--avail-soft);    border-color: #FFD4AE;             color: var(--avail-accent-text); }
.bo-avail__chip--wait    { background: #F4F3F0;              border-color: var(--avail-line);   color: var(--avail-dim); }

.bo-avail__row {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 10px 12px;
}
.bo-avail__field { display: flex; flex-direction: column; gap: 6px; }
.bo-avail__label {
  font-family: 'Geist Mono', ui-monospace, 'JetBrains Mono', monospace;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: .06em;
  text-transform: uppercase;
  color: var(--avail-dim);
}
.bo-avail__input {
  min-width: 158px;
  padding: 10px 13px;
  border: 0.5px solid #DBD9D2;
  border-radius: 10px;
  background: #FAFAF9;
  font-family: inherit;
  font-size: .88rem;
  font-weight: 500;
  color: var(--avail-ink);
  transition: border-color var(--transition), background var(--transition), box-shadow var(--transition);
}
.bo-avail__input:hover  { background: #fff; border-color: #C7C4BC; }
.bo-avail__input:focus  {
  outline: none;
  background: #fff;
  border-color: var(--avail-accent);
  box-shadow: 0 0 0 3px rgba(252,112,16,.12);
}
.bo-avail__arrow {
  padding-bottom: 12px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 13px;
  color: var(--text-faint, #A0A0A8);
}

/* Acciones: pill oscura para aplicar, hairline para limpiar */
.bo-avail__btn {
  padding: 10px 20px;
  border-radius: 999px;
  border: 0.5px solid transparent;
  font-size: .85rem;
  font-weight: 500;
  letter-spacing: -.01em;
  cursor: pointer;
  white-space: nowrap;
  transition: all .16s ease;
}
.bo-avail__btn--apply {
  background: var(--avail-ink);
  color: #fff;
}
.bo-avail__btn--apply:hover:not(:disabled) {
  background: var(--avail-accent);
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(252,112,16,.28);
}
.bo-avail__btn--ghost {
  background: transparent;
  border-color: #DBD9D2;
  color: var(--avail-dim);
}
.bo-avail__btn--ghost:hover:not(:disabled) { border-color: var(--avail-ink); color: var(--avail-ink); }
.bo-avail__btn:disabled { opacity: .4; cursor: not-allowed; transform: none; box-shadow: none; }

.bo-avail__toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 4px;
  padding: 9px 14px 9px 12px;
  border: 0.5px solid #DBD9D2;
  border-radius: 999px;
  background: #FAFAF9;
  font-size: .82rem;
  font-weight: 500;
  color: var(--avail-ink);
  cursor: pointer;
  user-select: none;
  transition: all .16s ease;
}
.bo-avail__toggle:hover { border-color: #C7C4BC; background: #fff; }
.bo-avail__toggle input { accent-color: var(--avail-accent); cursor: pointer; }

.bo-avail__note {
  margin: 13px 0 0;
  padding-top: 12px;
  border-top: 0.5px solid var(--avail-line);
  font-size: .8rem;
  line-height: 1.45;
  color: var(--avail-dim);
}
.bo-avail__warn { color: var(--avail-accent-text); font-weight: 500; }

/* ────────────────────────────────────────────────────────
   RESUMEN (resultados / hosts / precio promedio / rango)
──────────────────────────────────────────────────────── */
.bo-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px clamp(16px, 2.4vw, 32px);
  margin-bottom: 26px;
}
.bo-stat { min-width: 0; }
.bo-stat__label {
  font-size: clamp(.82rem, .74rem + .3vw, .95rem);
  color: var(--c-muted);
  margin: 0 0 6px;
  font-weight: 500;
}
/* Los importes escalan con el ancho disponible: al hacer zoom se achican en vez
   de desbordar la tarjeta. */
.bo-stat__value {
  font-size: clamp(1.5rem, 1rem + 1.9vw, 2.1rem);
  font-weight: 800;
  color: var(--c-text);
  margin: 0;
  line-height: 1.05;
  letter-spacing: -.02em;
  overflow-wrap: anywhere;
}
.bo-stat__value--ok { color: var(--c-success); }
/* Importes largos (CLP 1.250.000): un punto menos de cuerpo para que no corte */
.bo-stat__value--money { font-size: clamp(1.2rem, .85rem + 1.5vw, 1.75rem); letter-spacing: -.015em; }

/* ────────────────────────────────────────────────────────
   LISTADO DE ALOJAMIENTOS
──────────────────────────────────────────────────────── */
/* Queda fuera de .bo-container, así usa todo el ancho del main. */
.bo-grid {
  width: 100%;
  max-width: 100%;
}
/* Deja aire al final para que la barra de acciones no tape la última fila. */
.bo-grid--with-bar { padding-bottom: 84px; }

.bo-grid__scroll {
  background: var(--c-card);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-lg);
  overflow-x: auto;
  overflow-y: hidden;
  /* El scroll horizontal de la tabla no arrastra la página detrás. */
  overscroll-behavior-x: contain;
  -webkit-overflow-scrolling: touch;
}
.bo-grid__scroll::-webkit-scrollbar        { height: 9px; }
.bo-grid__scroll::-webkit-scrollbar-thumb  { background: #E5E1DA; border-radius: 8px; border: 2px solid transparent; background-clip: padding-box; }
.bo-grid__scroll::-webkit-scrollbar-thumb:hover { background: #D6D2CA; }
.bo-grid__scroll::-webkit-scrollbar-track  { background: transparent; }

.bo-grid__table {
  width: 100%;
  /* separate (y no collapse) porque con collapse los bordes los pinta la tabla y
     las celdas congeladas se quedan sin línea al scrollear. Con spacing 0 el
     resultado visual es el mismo. */
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  font-size: .87rem;
}
.bo-grid__table thead { background: var(--c-card); }
.bo-grid__table th {
  padding: 13px 16px;
  text-align: left;
  font-size: .74rem;
  font-weight: 700;
  color: var(--c-muted);
  text-transform: uppercase;
  letter-spacing: .05em;
  border-bottom: 1.5px solid var(--c-border);
  background: var(--c-card);
  white-space: nowrap;
}
.bo-grid__th--num       { text-align: left; }
.bo-grid__th-inner      { display: inline-flex; align-items: center; gap: 6px; max-width: 100%; }
.bo-grid__th-label      { overflow: hidden; text-overflow: ellipsis; }
.bo-grid__sortmark      { color: var(--c-primary); font-size: .8rem; }

.bo-funnel {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 5px;
  background: transparent;
  color: #9CA3AF;
  cursor: pointer;
  flex-shrink: 0;
  transition: all var(--transition);
}
.bo-funnel:hover  { background: var(--c-bg); color: var(--c-text); }
.bo-funnel--on    { color: var(--c-primary); background: #FFF7ED; }

.bo-grid__table .bo-grid__check { width: 48px; padding-left: 12px; padding-right: 8px; }
.bo-grid__check input { cursor: pointer; }

/* ── Columnas congeladas a la izquierda ──
   Con el scroll horizontal el checkbox y el ID quedan siempre a la vista, así
   no se pierde la referencia de la fila. Los desplazamientos vienen de
   measureFrozenCols(), que mide el ancho real de las cabeceras. */
.bo-grid__check,
.bo-grid__th--id,
.bo-grid__td--id {
  position: sticky;
  z-index: 2;
  background: var(--c-card);
}
.bo-grid__check { left: 0; }
.bo-grid__th--id,
.bo-grid__td--id {
  left: var(--bo-frozen-1, 48px);
  box-shadow: 1px 0 0 var(--c-border);
}
/* Las celdas fijas no son transparentes: tienen que repintar el fondo de la fila. */
.bo-grid__row:hover .bo-grid__check,
.bo-grid__row:hover .bo-grid__td--id            { background: #FAFAF9; }
.bo-grid__row--selected .bo-grid__check,
.bo-grid__row--selected .bo-grid__td--id        { background: #EFF6FF; }
.bo-grid__row--selected:hover .bo-grid__check,
.bo-grid__row--selected:hover .bo-grid__td--id  { background: #E5EEFC; }

/* De tablet para arriba sobra ancho: se congelan también ID Host y Host. */
@media (min-width: 1024px) {
  .bo-grid__th--id,
  .bo-grid__td--id { box-shadow: none; }
  .bo-grid__th--idHost,
  .bo-grid__td--idHost,
  .bo-grid__th--host,
  .bo-grid__td--host {
    position: sticky;
    z-index: 2;
    background: var(--c-card);
  }
  .bo-grid__th--idHost,
  .bo-grid__td--idHost { left: var(--bo-frozen-2, 128px); }
  .bo-grid__th--host,
  .bo-grid__td--host {
    left: var(--bo-frozen-3, 218px);
    box-shadow: 1px 0 0 var(--c-border);
  }
  .bo-grid__row:hover .bo-grid__td--idHost,
  .bo-grid__row:hover .bo-grid__td--host           { background: #FAFAF9; }
  .bo-grid__row--selected .bo-grid__td--idHost,
  .bo-grid__row--selected .bo-grid__td--host       { background: #EFF6FF; }
  .bo-grid__row--selected:hover .bo-grid__td--idHost,
  .bo-grid__row--selected:hover .bo-grid__td--host { background: #E5EEFC; }
}

.bo-grid__table td {
  padding: 12px 16px;
  border-bottom: 1px solid var(--c-border);
  color: var(--c-text);
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bo-grid__row              { cursor: pointer; transition: background var(--transition); }
.bo-grid__row:hover        { background: #FAFAF9; }
.bo-grid__row--selected    { background: #EFF6FF; }
.bo-grid__row--selected:hover { background: #E5EEFC; }

.bo-grid__td--nombre,
.bo-grid__td--host         { font-weight: 600; }
.bo-grid__td--email,
.bo-grid__td--telefono     { color: var(--c-muted); font-size: .82rem; }
.bo-grid__td--segmento,
.bo-grid__td--whats        { text-align: center; }
.bo-grid__muted            { color: var(--c-muted); }

/* Columnas de texto largo: se ensanchan al pasar el mouse (ver colWidth()). */
.bo-grid__td--expanded     { background: #FEFCE8; }
.bo-grid__row--selected .bo-grid__td--expanded { background: #E8EEF6; }

.bo-grid__link {
  color: #2563EB;
  text-decoration: none;
  font-weight: 700;
}
.bo-grid__link:hover { text-decoration: underline; }

.bo-grid__hostbtn {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: 700;
  color: #2563EB;
  cursor: pointer;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}
.bo-grid__hostbtn:hover:not(:disabled) { text-decoration: underline; }
.bo-grid__hostbtn:disabled { color: var(--c-muted); cursor: default; }

.bo-grid__empty {
  text-align: center;
  color: var(--c-muted);
  padding: 40px;
  font-style: italic;
}
.bo-grid__sentinel {
  text-align: center;
  color: var(--c-muted);
  font-size: .8rem;
  padding: 18px 0 8px;
  min-height: 20px;
}

/* ────────────────────────────────────────────────────────
   MENÚ DE COLUMNA (ordenar + filtrar por valores)
──────────────────────────────────────────────────────── */
/* Fondo oscurecido: sin él el menú se confunde con la tabla que tiene detrás.
   Más suave que el de los modales porque acá conviene seguir viendo la columna
   sobre la que se está filtrando. */
.bo-colmenu-backdrop {
  position: fixed;
  inset: 0;
  z-index: 300;
  background: rgba(10, 10, 11, .38);
}
/* Como hoja inferior tapa media pantalla: ahí se oscurece igual que un modal. */
@media (max-width: 640px) {
  .bo-colmenu-backdrop { background: rgba(10, 10, 11, .5); backdrop-filter: blur(2px); }
}
/* El alto y el ancho los calcula openColumnMenu() según el espacio libre: el
   menú abre hacia abajo o hacia arriba, pero nunca se sale del viewport. */
.bo-colmenu {
  position: fixed;
  z-index: 301;
  width: 304px;
  max-width: calc(100vw - 16px);
  max-height: calc(100vh - 16px);
  display: flex;
  flex-direction: column;
  background: var(--c-card);
  border: 1px solid var(--c-border);
  border-radius: var(--radius);
  box-shadow: 0 12px 32px rgba(0,0,0,.16);
  padding: 8px 0;
  font-size: .85rem;
}

/* En celular es una hoja inferior a ancho completo. */
.bo-colmenu--sheet {
  top: auto;
  bottom: 0;
  left: 0;
  right: 0;
  width: auto;
  max-width: none;
  max-height: 82vh;
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  padding-bottom: max(8px, env(safe-area-inset-bottom));
}
.bo-colmenu__sheet-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 4px;
  padding: 4px 12px 10px 16px;
  border-bottom: 1px solid var(--c-border);
  font-size: .9rem;
  font-weight: 700;
  color: var(--c-text);
}
.bo-colmenu__sheet-close {
  border: none;
  background: none;
  color: var(--c-muted);
  font-size: 1.6rem;
  line-height: 1;
  width: 36px;
  height: 36px;
  cursor: pointer;
  border-radius: var(--radius-sm);
}
.bo-colmenu__sheet-close:active { background: var(--c-bg); }
.bo-colmenu--sheet .bo-colmenu__sort { padding: 13px 16px; }
.bo-colmenu--sheet .bo-colmenu__opt  { padding: 11px 16px; }
.bo-colmenu--sheet .bo-colmenu__btn  { flex: 1 1 0; padding: 12px 16px; }
.bo-colmenu--sheet .bo-colmenu__list { max-height: none; }
.bo-colmenu__sort {
  display: block;
  width: 100%;
  text-align: left;
  padding: 9px 16px;
  border: none;
  background: none;
  color: var(--c-text);
  font-size: .85rem;
  cursor: pointer;
}
.bo-colmenu__sort:hover { background: var(--c-bg); }
.bo-colmenu__sep   { height: 1px; background: var(--c-border); margin: 6px 0; }
.bo-colmenu__title { margin: 4px 16px 6px; font-size: .85rem; font-weight: 600; color: var(--c-text); }

.bo-colmenu__toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 0 16px 8px;
}
.bo-colmenu__link {
  background: none;
  border: none;
  padding: 0;
  color: #1A73E8;
  font-size: .78rem;
  cursor: pointer;
  text-decoration: underline;
}
.bo-colmenu__link:hover { color: #1557B0; }
.bo-colmenu__shown { margin-left: auto; font-size: .75rem; color: var(--c-muted); }

.bo-colmenu__search {
  position: relative;
  padding: 0 16px 8px;
  color: var(--c-muted);
}
.bo-colmenu__search svg {
  position: absolute;
  right: 26px;
  top: 9px;
  pointer-events: none;
}
.bo-colmenu__input {
  width: 100%;
  padding: 7px 32px 7px 10px;
  border: 1px solid var(--c-border);
  border-radius: var(--radius-sm);
  font-size: .82rem;
  color: var(--c-text);
  outline: none;
  box-sizing: border-box;
}
.bo-colmenu__input:focus { border-color: var(--c-primary); box-shadow: 0 0 0 3px rgba(249,115,22,.12); }

.bo-colmenu__list {
  flex: 1 1 auto;
  /* Sin esto, un flex item no baja de su contenido y el menú se desborda. */
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  max-height: 260px;
  padding: 2px 0;
  border-top: 1px solid var(--c-border);
}
.bo-colmenu__list::-webkit-scrollbar        { width: 8px; }
.bo-colmenu__list::-webkit-scrollbar-thumb  { background: #E5E1DA; border-radius: 8px; border: 2px solid transparent; background-clip: padding-box; }
.bo-colmenu__opt {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 16px;
  cursor: pointer;
}
.bo-colmenu__opt:hover { background: var(--c-bg); }
.bo-colmenu__opt input { cursor: pointer; flex-shrink: 0; }
.bo-colmenu__opt-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: .82rem;
  color: var(--c-text);
}
.bo-colmenu__nores { margin: 10px 16px; font-size: .78rem; color: var(--c-muted); }

/* Sólo la lista se encoge; el resto del menú mantiene su alto. */
.bo-colmenu > *:not(.bo-colmenu__list) { flex-shrink: 0; }

.bo-colmenu__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 16px 2px;
  border-top: 1px solid var(--c-border);
}
.bo-colmenu__btn {
  padding: 7px 16px;
  border: 1px solid var(--c-border);
  border-radius: var(--radius-sm);
  background: var(--c-card);
  color: var(--c-text);
  font-size: .82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition);
}
.bo-colmenu__btn:hover { background: var(--c-bg); }
.bo-colmenu__btn--primary {
  background: var(--c-primary);
  border-color: var(--c-primary);
  color: #fff;
}
.bo-colmenu__btn--primary:hover { background: var(--c-secondary); border-color: var(--c-secondary); }

/* ────────────────────────────────────────────────────────
   BARRA DE ACCIONES (selección)
──────────────────────────────────────────────────────── */
.bo-actionbar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
  padding: 14px 24px;
  background: #DBE8FB;
  border-top: 1px solid #C3D7F5;
  box-shadow: 0 -4px 16px rgba(0,0,0,.08);
}
.bo-actionbar__count {
  font-size: .9rem;
  font-weight: 600;
  color: #1D4ED8;
}
.bo-actionbar__btns { display: flex; gap: 10px; flex-wrap: wrap; margin-left: auto; margin-right: auto; }
.bo-actionbar__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  border: 1px solid #B6CDF0;
  border-radius: var(--radius);
  background: var(--c-card);
  color: var(--c-text);
  font-size: .88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition);
}
.bo-actionbar__btn:hover { border-color: #1D4ED8; color: #1D4ED8; transform: translateY(-1px); }

/* ────────────────────────────────────────────────────────
   PILLS / BADGES
──────────────────────────────────────────────────────── */
.bo-pill            { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 20px; font-size: .72rem; font-weight: 700; text-transform: capitalize; }
.bo-pill--activo    { background: #ECFDF5; color: var(--c-success); }
.bo-pill--lento     { background: #FFFBEB; color: var(--c-warning); }
.bo-pill--inactivo  { background: #FEF2F2; color: var(--c-danger); }

.bo-ical            { font-size: .78rem; font-weight: 600; }
.bo-ical--yes       { color: var(--c-success); }
.bo-ical--no        { color: var(--c-danger); }

/* ── Pill de la celda Disponibilidad ── */
.bo-avpill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: .74rem;
  font-weight: 700;
  line-height: 1;
}
.bo-avpill--yes { background: #ECFDF5; color: #047857; }
.bo-avpill--no  { background: #FEF2F2; color: #B91C1C; }

/* Dato de Homiii sin confirmar o de una foto vieja: el borde punteado avisa
   que ese "Disponible" no está respaldado por un sondeo reciente. */
.bo-avpill--stale {
  outline: 1px dashed #D97706;
  outline-offset: -1px;
}

/* ── Proveedor de la unidad ── */
.bo-provpill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: .72rem;
  font-weight: 700;
  line-height: 1;
}
.bo-provpill--propio  { background: #F1F5F9; color: #475569; }
.bo-provpill--spacest { background: #EFF6FF; color: #1D4ED8; }
.bo-provpill--homiii  { background: #FFF7ED; color: #C2410C; }

/* ── Badge B2B / B2C (segmentación comercial) ── */
.bo-seg {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 52px;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: .72rem;
  font-weight: 800;
  letter-spacing: .8px;
  text-transform: uppercase;
  color: #fff;
  line-height: 1;
}
.bo-seg--b2b { background: #F97316; }
.bo-seg--b2c { background: #111827; }

/* ── Logo de WhatsApp: gris si el host no está en un grupo ── */
.bo-whats        { display: inline-flex; color: #9CA3AF; }
.bo-whats--on    { color: #25D366; }

/* ────────────────────────────────────────────────────────
   TOASTS
──────────────────────────────────────────────────────── */
.bo-toasts {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
}
.bo-toast {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 18px;
  border-radius: var(--radius);
  background: var(--c-text);
  color: #fff;
  font-size: .85rem;
  font-weight: 500;
  box-shadow: var(--shadow-md);
  pointer-events: all;
  min-width: 220px;
  max-width: 340px;
}
.bo-toast--success  { background: #064E3B; }
.bo-toast--error    { background: #7F1D1D; }
.bo-toast--info     { background: #1E3A5F; }
.bo-toast__icon     { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; background: rgba(255,255,255,.15); flex-shrink: 0; font-size: .78rem; }

/* ────────────────────────────────────────────────────────
   ANIMACIONES & TRANSICIONES
──────────────────────────────────────────────────────── */
@keyframes bo-card-in {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* slide-up */
.bo-slide-up-enter-active { transition: all .25s ease; }
.bo-slide-up-leave-active { transition: all .2s ease; }
.bo-slide-up-enter-from,
.bo-slide-up-leave-to     { opacity: 0; transform: translateY(100%); }

/* toast */
.bo-toast-enter-active { transition: all .35s cubic-bezier(.34,1.56,.64,1); }
.bo-toast-leave-active { transition: all .25s ease; }
.bo-toast-enter-from,
.bo-toast-leave-to     { opacity: 0; transform: translateX(60px) scale(.85); }

/* fade */
.bo-fade-enter-active,
.bo-fade-leave-active  { transition: opacity .22s ease; }
/* Vue 2 usa -enter; -enter-from es de Vue 3 y nunca llegaba a aplicarse, así que
   los overlays entraban de golpe y sólo se desvanecían al cerrar. */
.bo-fade-enter,
.bo-fade-enter-from,
.bo-fade-leave-to      { opacity: 0; }

/* ────────────────────────────────────────────────────────
   RESPONSIVE
──────────────────────────────────────────────────────── */
/* Los cortes valen igual para pantallas chicas y para zoom del navegador:
   ampliar al 200% en un monitor de 1920 equivale a un viewport de 960. */
@media (max-width: 1024px) {
  .bo-main            { padding: 16px 12px; }
  .bo-toolbar         { margin-bottom: 16px; }
  .bo-stats           { margin-bottom: 18px; }
}

@media (max-width: 760px) {
  .bo-viewnav         { display: flex; width: 100%; margin-bottom: 14px; }
  .bo-viewnav__tab    { flex: 1 1 0; padding: 9px 12px; }
  .bo-stats           { grid-template-columns: repeat(auto-fit, minmax(128px, 1fr)); gap: 10px 16px; }
}

@media (max-width: 640px) {
  .bo-main            { padding: 12px 10px; }
  .bo-toolbar         { gap: 8px; }
  /* Buscador a lo ancho: es la acción más usada en celular. */
  .bo-search          { flex: 1 1 100%; max-width: none; }
  .bo-availbtn        { flex: 1 1 auto; justify-content: center; }
  .bo-tools           { flex: 1 1 100%; margin-left: 0; }
  .bo-toolbtn         { flex: 1 1 0; justify-content: center; padding: 10px 12px; }

  .bo-grid__table th  { padding: 11px 12px; }
  .bo-grid__table td  { padding: 10px 12px; }

  /* La barra de acciones apila etiqueta y botones en dos columnas. */
  .bo-actionbar       { padding: 10px 12px calc(10px + env(safe-area-inset-bottom)); gap: 8px; }
  .bo-actionbar__btns { margin: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 8px; width: 100%; }
  .bo-actionbar__btn  { justify-content: center; padding: 10px 6px; font-size: .8rem; }
  /* Con la barra en dos filas hace falta más aire bajo la tabla. */
  .bo-grid--with-bar  { padding-bottom: 168px; }

  .bo-toasts          { bottom: 12px; right: 12px; left: 12px; }
  .bo-toast           { max-width: 100%; min-width: 0; }
}

/* Los toasts suben para no quedar tapados por la barra de selección. */
.bo-toasts--raised { bottom: 96px; }
@media (max-width: 640px) {
  .bo-toasts--raised { bottom: 172px; }
}

/* Sin hover (touch): los objetivos táctiles necesitan más superficie. */
@media (hover: none) {
  .bo-funnel            { width: 30px; height: 30px; }
  .bo-grid__check input { width: 18px; height: 18px; }
  .bo-host-acc-card:hover { transform: none; }
}

/* ────────────────────────────────────────────────────────
   MODAL
──────────────────────────────────────────────────────── */
.bo-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: clamp(8px, 2.5vw, 20px);
}

.bo-modal {
  background: var(--c-card);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  max-width: 600px;
  width: 100%;
  /* 100% = alto del overlay menos su padding: nunca se sale de la pantalla. */
  max-height: min(80vh, 100%);
  display: flex;
  flex-direction: column;
  animation: bo-modal-in .3s ease;
}

@keyframes bo-modal-in {
  from { opacity: 0; transform: scale(.95) translateY(-20px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}

.bo-modal__close {
  background: none;
  border: none;
  font-size: 1.8rem;
  line-height: 1;
  color: var(--c-muted);
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all var(--transition);
}

.bo-modal__close:hover {
  background: var(--c-bg);
  color: var(--c-text);
}

.bo-modal--wide { max-width: 900px; max-height: min(88vh, 100%); }

.bo-modal__header {
  padding: 18px 24px;
  border-bottom: 1px solid var(--c-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.bo-modal__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--c-text);
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}
.bo-modal__subtitle {
  font-size: .82rem;
  font-weight: 500;
  color: var(--c-muted);
}
.bo-modal__body {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
}
.bo-modal__footer {
  padding: 14px 24px;
  border-top: 1px solid var(--c-border);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
}

/* ────────────────────────────────────────────────────────
   PANEL PUSH / EMAIL (dentro del modal)
──────────────────────────────────────────────────────── */
.bo-email-panel {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}
.bo-email-panel__editor,
.bo-email-panel__preview { display: flex; flex-direction: column; gap: 16px; }

.bo-field  { display: flex; flex-direction: column; gap: 6px; }
.bo-label  { font-size: .78rem; font-weight: 600; color: var(--c-muted); text-transform: uppercase; letter-spacing: .04em; }

.bo-input {
  width: 100%;
  box-sizing: border-box;
  padding: 9px 13px;
  border: 1.5px solid var(--c-border);
  border-radius: var(--radius-sm);
  font-size: .88rem;
  font-family: inherit;
  color: var(--c-text);
  outline: none;
  transition: border-color var(--transition), box-shadow var(--transition);
}
.bo-input:focus { border-color: var(--c-primary); box-shadow: 0 0 0 3px rgba(249,115,22,.12); }

.bo-rich-toolbar  { display: flex; gap: 6px; }
.bo-rich-btn      { padding: 5px 10px; border: 1.5px solid var(--c-border); border-radius: 6px; background: var(--c-card); cursor: pointer; font-size: .82rem; color: var(--c-text); transition: all var(--transition); }
.bo-rich-btn:hover{ border-color: var(--c-primary); color: var(--c-primary); background: #FFF7ED; }

.bo-textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 13px;
  border: 1.5px solid var(--c-border);
  border-radius: var(--radius-sm);
  font-size: .88rem;
  font-family: inherit;
  color: var(--c-text);
  resize: vertical;
  outline: none;
  transition: border-color var(--transition), box-shadow var(--transition);
  line-height: 1.6;
}
.bo-textarea:focus { border-color: var(--c-primary); box-shadow: 0 0 0 3px rgba(249,115,22,.12); }

.bo-recipients {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  padding: 8px;
  border: 1.5px solid var(--c-border);
  border-radius: var(--radius-sm);
  min-height: 46px;
  max-height: 132px;
  overflow-y: auto;
  transition: border-color var(--transition);
}
.bo-recipients:focus-within      { border-color: var(--c-primary); box-shadow: 0 0 0 3px rgba(249,115,22,.12); }
.bo-recipients__input            { border: none; outline: none; font-size: .85rem; color: var(--c-text); flex: 1; min-width: 180px; background: transparent; }
.bo-recipient-chip               { display: inline-flex; align-items: center; gap: 5px; background: #FFF7ED; border: 1px solid #FED7AA; color: var(--c-secondary); border-radius: 20px; padding: 3px 10px 3px 12px; font-size: .78rem; font-weight: 500; }
.bo-recipient-chip__remove       { background: none; border: none; cursor: pointer; color: var(--c-secondary); padding: 0; line-height: 1; font-size: 1rem; opacity: .6; transition: opacity var(--transition); }
.bo-recipient-chip__remove:hover { opacity: 1; }

.bo-preview-title          { font-size: .85rem; font-weight: 700; color: var(--c-muted); text-transform: uppercase; letter-spacing: .05em; margin: 0; }
.bo-email-preview          { border: 1px solid var(--c-border); border-radius: var(--radius-sm); overflow: hidden; flex: 1; display: flex; flex-direction: column; }
.bo-email-preview__subject { background: var(--c-bg); border-bottom: 1px solid var(--c-border); padding: 12px 16px; font-weight: 600; font-size: .9rem; color: var(--c-text); }
.bo-email-preview__body    { padding: 16px; font-size: .88rem; line-height: 1.7; color: var(--c-text); flex: 1; overflow-y: auto; min-height: 120px; }
.bo-email-preview__footer  { background: var(--c-bg); border-top: 1px solid var(--c-border); padding: 8px 16px; color: var(--c-muted); font-size: .75rem; word-break: break-all; max-height: 60px; overflow-y: auto; }
.bo-placeholder            { color: var(--c-muted); font-style: italic; }

.bo-inline-success  { color: var(--c-success); font-size: .85rem; font-weight: 600; margin-right: auto; }
.bo-inline-error    { color: var(--c-danger);  font-size: .85rem; font-weight: 600; margin-right: auto; }

.bo-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 20px;
  border: 1.5px solid transparent;
  border-radius: var(--radius);
  font-size: .85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition);
  white-space: nowrap;
}
.bo-btn--primary                     { background: var(--c-primary); color: #fff; border-color: var(--c-primary); }
.bo-btn--primary:hover:not(:disabled){ background: var(--c-secondary); border-color: var(--c-secondary); }
.bo-btn--ghost                       { background: transparent; color: var(--c-muted); border-color: var(--c-border); }
.bo-btn--ghost:hover:not(:disabled)  { border-color: var(--c-primary); color: var(--c-primary); background: #FFF7ED; }
.bo-btn:disabled                     { opacity: .45; cursor: not-allowed; }

.bo-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: bo-spin .7s linear infinite;
}
/* ─────────────────────────────────────────
   OVERLAY DE CARGA (disponibilidad)
───────────────────────────────────────── */
.bo-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(17, 24, 39, .55);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  /* Por encima de los modales (.bo-modal-overlay usa 9999). */
  z-index: 10050;
}

.bo-loading-card {
  width: min(360px, 100%);
  background: var(--c-card);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  padding: 28px 24px 20px;
  text-align: center;
}

/* Anillo grande: se ve girar aunque el hilo esté ocupado unos milisegundos. */
.bo-loading-ring {
  display: block;
  width: 38px;
  height: 38px;
  margin: 0 auto 16px;
  border: 3px solid rgba(249, 115, 22, .2);
  border-top-color: var(--c-primary);
  border-radius: 50%;
  animation: bo-spin .7s linear infinite;
}

.bo-loading-title {
  margin: 0 0 4px;
  font-size: .98rem;
  font-weight: 700;
  color: var(--c-text);
}

.bo-loading-stage {
  margin: 0 0 14px;
  font-size: .84rem;
  color: var(--c-muted);
  min-height: 1.2em;
}

.bo-loading-bar {
  height: 5px;
  border-radius: 999px;
  background: var(--c-border);
  overflow: hidden;
}

.bo-loading-bar__fill {
  height: 100%;
  width: 0;
  border-radius: 999px;
  background: var(--c-primary);
  transition: width .2s ease;
}

/* Mientras se espera al servidor no hay porcentaje que mostrar: la barra
   recorre sola para que se note que la pantalla no está trabada. */
.bo-loading-bar--indef .bo-loading-bar__fill {
  width: 35%;
  transition: none;
  animation: bo-loading-slide 1.1s ease-in-out infinite;
}

@keyframes bo-loading-slide {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(300%); }
}

.bo-loading-hint {
  margin: 12px 0 0;
  font-size: .76rem;
  line-height: 1.45;
  color: var(--c-muted);
}

.bo-loading-cancel {
  margin-top: 14px;
  padding: 7px 18px;
  border: 1px solid var(--c-border);
  border-radius: var(--radius-sm);
  background: transparent;
  font-size: .82rem;
  font-weight: 600;
  color: var(--c-muted);
  cursor: pointer;
  transition: background .15s, color .15s, border-color .15s;
}

.bo-loading-cancel:hover {
  background: #FEF2F2;
  border-color: var(--c-danger);
  color: var(--c-danger);
}

/* Variante para fondos claros (barra de fechas) */
.bo-spinner--dark {
  border-color: rgba(249,115,22,.25);
  border-top-color: var(--c-primary);
}
@keyframes bo-spin { to { transform: rotate(360deg); } }

@media (max-width: 760px) {
  .bo-email-panel { grid-template-columns: 1fr; }
  .bo-avail-overlay    { align-items: flex-end; padding: 0; }
  .bo-availmodal       { max-width: 100%; max-height: 92vh; overflow-y: auto; border-radius: 18px 18px 0 0; }
  .bo-availmodal__body { padding: 16px; }
  .bo-avail__arrow     { display: none; }
  .bo-avail__field     { flex: 1 1 140px; }
  .bo-avail__input     { min-width: 0; width: 100%; }
  .bo-avail__btn       { flex: 1 1 auto; }
  .bo-avail__toggle    { margin-left: 0; }

  .bo-modal__header    { padding: 14px 16px; }
  .bo-modal__body      { padding: 16px; }
  .bo-modal__footer    { padding: 12px 16px; }
  .bo-email-panel__preview { min-height: 0; }
}

@media (max-width: 560px) {
  /* Botones del pie a lo ancho: en pantallas chicas se tocan mejor. */
  .bo-modal__footer    { flex-direction: column-reverse; align-items: stretch; gap: 8px; }
  .bo-modal__footer .bo-btn { width: 100%; }
  .bo-inline-success,
  .bo-inline-error     { margin: 0; text-align: center; }
  .bo-rich-toolbar     { flex-wrap: wrap; }
  .bo-recipients__input { min-width: 120px; }
}

/* ────────────────────────────────────────────────────────
   HOST PROFILE MODAL
──────────────────────────────────────────────────────── */
.bo-host-overlay {
  align-items: center;
  backdrop-filter: blur(3px);
  padding: 8px;
}

/* Se mide contra el overlay (que ya descuenta su padding) en vez de contra vw/vh:
   así no lo afecta la barra de scroll ni la barra de direcciones del celular. */
.bo-host-modal {
  background: var(--c-card);
  border-radius: var(--radius-lg);
  box-shadow: 0 24px 64px rgba(0,0,0,.18), 0 4px 16px rgba(0,0,0,.08);
  max-width: 1380px;
  width: 100%;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  animation: bo-modal-in .3s cubic-bezier(.34,1.3,.64,1);
  overflow: hidden;
}

/* Header */
.bo-host-modal__header {
  padding: clamp(12px, 2vw, 20px) clamp(14px, 2.4vw, 28px);
  border-bottom: 1px solid var(--c-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
  background: linear-gradient(135deg, #FFF7ED 0%, #fff 60%);
}
.bo-host-modal__header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}
.bo-host-modal__header-left > div { min-width: 0; }
.bo-host-modal__title {
  margin: 0 0 6px;
  overflow-wrap: anywhere;
  font-size: clamp(1rem, .9rem + .5vw, 1.15rem);
  font-weight: 700;
  color: var(--c-text);
  line-height: 1.2;
}
.bo-host-modal__header-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.bo-host-modal__id-badge {
  font-size: .75rem;
  font-weight: 600;
  color: var(--c-muted);
  background: var(--c-bg);
  border: 1px solid var(--c-border);
  border-radius: 20px;
  padding: 2px 10px;
}

/* Avatar */
.bo-host-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--c-primary), var(--c-secondary));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 14px rgba(249,115,22,.35);
}
.bo-host-avatar__initials {
  font-size: 1.3rem;
  font-weight: 800;
  color: #fff;
  letter-spacing: .02em;
}

/* Body layout */
.bo-host-modal__body {
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

/* Left panel */
.bo-host-modal__left {
  /* 360 = los 320 de contenido de antes + el padding lateral (ahora border-box). */
  width: 360px;
  min-width: 360px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px 20px;
  border-right: 1px solid var(--c-border);
  background: var(--c-bg);
  display: flex;
  flex-direction: column;
  gap: 20px;
  scrollbar-width: thin;
  scrollbar-color: var(--c-border) transparent;
}
.bo-host-modal__left::-webkit-scrollbar { width: 5px; }
.bo-host-modal__left::-webkit-scrollbar-track { background: transparent; }
.bo-host-modal__left::-webkit-scrollbar-thumb { background: var(--c-border); border-radius: 10px; }
.bo-host-modal__left::-webkit-scrollbar-thumb:hover { background: var(--c-muted); }
.bo-host-modal__section-label {
  font-size: .84rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: var(--c-text);
  margin: 0 0 6px;
}

/* Host info card */
.bo-host-card__info {
  background: var(--c-card);
  border: 1px solid var(--c-border);
  border-radius: var(--radius);
  overflow: hidden;
}
.bo-host-card__row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 16px;
  border-bottom: 1px solid var(--c-border);
  gap: 12px;
  transition: background var(--transition);
}
.bo-host-card__row:last-child { border-bottom: none; }
.bo-host-card__row:hover { background: #FFF7ED; }
.bo-host-card__label {
  font-size: .82rem;
  font-weight: 700;
  color: var(--c-muted);
  text-transform: uppercase;
  letter-spacing: .04em;
  flex-shrink: 0;
}
.bo-host-card__value {
  font-size: .98rem;
  color: var(--c-text);
  font-weight: 700;
  text-align: right;
  word-break: break-word;
}
.bo-host-card__value--email {
  color: #2563EB;
  font-size: .92rem;
}
.bo-host-card__value--count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--c-primary);
  color: #fff;
  border-radius: 12px;
  padding: 2px 12px;
  font-size: .92rem;
  font-weight: 800;
}

/* Stat grid */
.bo-host-stat-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.bo-host-stat {
  background: var(--c-card);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-sm);
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-align: center;
  transition: border-color var(--transition), transform var(--transition);
}
.bo-host-stat:hover { border-color: var(--c-primary); transform: translateY(-2px); }
.bo-host-stat__num {
  font-size: 1.3rem;
  font-weight: 800;
  color: var(--c-success);
  line-height: 1;
}
.bo-host-stat__num--warn { color: var(--c-danger); }
.bo-host-stat__label {
  font-size: .65rem;
  font-weight: 600;
  color: var(--c-muted);
  text-transform: uppercase;
  letter-spacing: .03em;
  line-height: 1.3;
}

/* Puntuación del anfitrión (estrellas) */
.bo-host-rating {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
}
.bo-host-rating__star {
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  font-size: 1.7rem;
  line-height: 1;
  color: #E5E1DA;
  transition: color var(--transition), transform var(--transition);
}
.bo-host-rating__star:hover { transform: scale(1.12); }
.bo-host-rating__star--on   { color: #F59E0B; }
.bo-host-rating__star:disabled { cursor: default; opacity: .7; }
.bo-host-rating__value {
  margin-left: 8px;
  font-size: .8rem;
  font-weight: 700;
  color: var(--c-muted);
}
.bo-host-rating__value--empty { font-weight: 500; font-style: italic; }
/* Toggle grupo de WhatsApp: gris = sin grupo, verde = en grupo */
.bo-host-wa {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}
.bo-host-wa__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: none;
  background: transparent;
  color: #9CA3AF;
  cursor: pointer;
  border-radius: 8px;
  transition: color var(--transition), transform var(--transition), background var(--transition);
}
.bo-host-wa__btn:hover:not(:disabled) { transform: scale(1.1); background: #F3F4F6; }
.bo-host-wa__btn--on      { color: #25D366; }
.bo-host-wa__btn:disabled { cursor: default; opacity: .7; }
.bo-host-wa__label {
  font-size: .82rem;
  font-weight: 600;
  color: var(--c-muted);
}

/* Right panel */
.bo-host-modal__right {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px 28px;
  scrollbar-width: thin;
  scrollbar-color: var(--c-border) transparent;
}
.bo-host-modal__right::-webkit-scrollbar { width: 5px; }
.bo-host-modal__right::-webkit-scrollbar-track { background: transparent; }
.bo-host-modal__right::-webkit-scrollbar-thumb { background: var(--c-border); border-radius: 10px; }
.bo-host-modal__right::-webkit-scrollbar-thumb:hover { background: var(--c-muted); }
.bo-host-modal__count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--c-primary);
  color: #fff;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: .72rem;
  font-weight: 700;
  margin-left: 8px;
  vertical-align: middle;
}
.bo-host-modal__empty {
  text-align: center;
  color: var(--c-muted);
  padding: 60px 20px;
  font-style: italic;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Accommodation list */
.bo-host-acc-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.bo-host-acc-card {
  display: flex;
  border: 1px solid var(--c-border);
  border-radius: var(--radius);
  overflow: hidden;
  background: var(--c-card);
  transition: box-shadow var(--transition), border-color var(--transition), transform var(--transition);
  animation: bo-card-in .35s ease both;
}
.bo-host-acc-card:hover {
  box-shadow: var(--shadow-md);
  border-color: var(--c-primary);
  transform: translateX(4px);
}

/* Image */
.bo-host-acc-card__img-wrap {
  width: 220px;
  min-width: 220px;
  height: 150px;
  overflow: hidden;
  flex-shrink: 0;
  background: var(--c-bg);
  position: relative;
}
.bo-host-acc-card__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform .5s ease;
}
.bo-host-acc-card:hover .bo-host-acc-card__img { transform: scale(1.07); }
.bo-host-acc-card__img-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--c-muted);
  background: linear-gradient(135deg, #F9FAFB, #F3F4F6);
}
.bo-host-acc-card__quality-dot {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,.2);
}
.bo-host-acc-card__quality-dot--bueno    { background: var(--c-success); }
.bo-host-acc-card__quality-dot--promedio { background: var(--c-warning); }
.bo-host-acc-card__quality-dot--malo     { background: var(--c-danger); }

/* Card info */
.bo-host-acc-card__info {
  flex: 1;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  min-width: 0;
}
.bo-host-acc-card__name {
  font-size: 1rem;
  font-weight: 700;
  color: var(--c-text);
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  transition: color var(--transition);
}
.bo-host-acc-card__name:hover { color: var(--c-primary); }

/* Chips */
.bo-host-acc-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  align-items: center;
}
.bo-chip {
  display: inline-flex;
  padding: 3px 10px;
  background: #FFF7ED;
  color: var(--c-secondary);
  border: 1px solid #FED7AA;
  border-radius: 20px;
  font-size: .78rem;
  font-weight: 600;
  text-transform: capitalize;
}
.bo-chip--gray {
  background: var(--c-bg);
  color: var(--c-muted);
  border-color: var(--c-border);
}

/* Price */
.bo-host-acc-card__price {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}
.bo-host-acc-card__price-main {
  font-size: 1.15rem;
  font-weight: 800;
  color: var(--c-primary);
}
.bo-host-acc-card__price-period {
  font-size: .78rem;
  font-weight: 500;
  color: var(--c-muted);
}
.bo-host-acc-card__price-day {
  font-size: .83rem;
  color: var(--c-muted);
  font-weight: 500;
}

/* Features */
.bo-host-acc-card__features {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.bo-host-acc-card__feat {
  font-size: .9rem;
  font-weight: 600;
  color: var(--c-text);
}

/* Aside: ID grande + botón Ver */
.bo-host-acc-card__aside {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 20px;
  border-left: 1px solid var(--c-border);
  background: var(--c-bg);
  min-width: 90px;
  flex-shrink: 0;
}
.bo-host-acc-card__aside-id {
  font-size: 1.35rem;
  font-weight: 800;
  color: #2563EB;
  letter-spacing: -.01em;
  line-height: 1;
}
.bo-host-acc-card__ver-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px 18px;
  background: var(--c-primary);
  color: #fff;
  border-radius: var(--radius-sm);
  font-size: .82rem;
  font-weight: 700;
  text-decoration: none;
  transition: background var(--transition), transform var(--transition), box-shadow var(--transition);
  letter-spacing: .02em;
}
.bo-host-acc-card__ver-btn:hover {
  background: var(--c-secondary);
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(249,115,22,.35);
}

/* ── Aprobar / eliminar (secciones Sin aprobar y Eliminados) ── */
.bo-host-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 0 0 14px;
  padding: 10px 12px;
  background: var(--c-bg);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-sm);
}
.bo-host-actions__label {
  flex: 1 1 auto;
  font-size: .82rem;
  font-weight: 600;
  color: var(--c-muted);
}
.bo-host-actions__btn,
.bo-host-acc-card__act {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1.5px solid transparent;
  border-radius: var(--radius-sm);
  font-weight: 700;
  cursor: pointer;
  transition: background var(--transition), color var(--transition), opacity var(--transition);
}
.bo-host-actions__btn { padding: 7px 14px; font-size: .82rem; color: #fff; }
.bo-host-acc-card__act { min-width: 78px; padding: 5px 10px; font-size: .76rem; background: #fff; }
.bo-host-actions__btn--approve { background: var(--c-success); }
.bo-host-actions__btn--approve:hover:not(:disabled) { background: #059669; }
.bo-host-actions__btn--delete  { background: var(--c-danger); }
.bo-host-actions__btn--delete:hover:not(:disabled)  { background: #DC2626; }
.bo-host-acc-card__act--approve { color: #047857; border-color: var(--c-success); }
.bo-host-acc-card__act--approve:hover:not(:disabled) { background: var(--c-success); color: #fff; }
.bo-host-acc-card__act--delete  { color: #B91C1C; border-color: var(--c-danger); }
.bo-host-acc-card__act--delete:hover:not(:disabled)  { background: var(--c-danger); color: #fff; }
.bo-host-actions__btn:disabled,
.bo-host-acc-card__act:disabled { opacity: .55; cursor: not-allowed; }
.bo-host-actions__btn:focus-visible,
.bo-host-acc-card__act:focus-visible { outline: 2px solid #2563EB; outline-offset: 2px; }

/* Responsive */
@media (max-width: 900px) {
  /* Una sola columna y un único scroll: dos paneles con scroll propio, apilados,
     son imposibles de usar con el dedo. */
  .bo-host-modal__body {
    flex-direction: column;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
  .bo-host-modal__left,
  .bo-host-modal__right {
    width: 100%;
    min-width: 0;
    flex: 0 0 auto;
    overflow: visible;
  }
  .bo-host-modal__left {
    padding: 18px 16px;
    border-right: none;
    border-bottom: 1px solid var(--c-border);
  }
  .bo-host-modal__right { padding: 20px 16px; }
}

@media (max-width: 620px) {
  /* La tarjeta de alojamiento pasa a vertical: en fila la foto y el aside
     dejaban el texto en una columna de 100px. */
  .bo-host-acc-card           { flex-direction: column; }
  .bo-host-acc-card:hover     { transform: none; }
  .bo-host-acc-card__img-wrap { width: 100%; min-width: 0; height: 170px; }
  .bo-host-acc-card__aside {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    min-width: 0;
    padding: 10px 16px;
    border-left: none;
    border-top: 1px solid var(--c-border);
  }
  .bo-host-stat-grid { gap: 8px; }
  .bo-host-stat      { padding: 10px 6px; }
}

@media (max-width: 480px) {
  .bo-host-overlay { padding: 0; }
  .bo-host-modal   { border-radius: 0; }
  .bo-host-avatar  { width: 46px; height: 46px; }
  .bo-host-avatar__initials { font-size: 1.1rem; }
  .bo-host-card__row {
    /* Etiqueta arriba y valor abajo: en línea el valor quedaba partido. */
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  .bo-host-card__value { text-align: left; }
}

/* ────────────────────────────────────────────────────────
   BANK DATA
──────────────────────────────────────────────────────── */
.bo-host-card__value--mono {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: .76rem;
  letter-spacing: .02em;
  color: #374151;
  background: #F3F4F6;
  padding: 1px 6px;
  border-radius: 4px;
}
.bo-host-bank-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--c-bg);
  border: 1px dashed var(--c-border);
  border-radius: var(--radius-sm);
  padding: 12px 14px;
  color: var(--c-muted);
  font-size: .8rem;
  font-style: italic;
}

/* ────────────────────────────────────────────────────────
   EDICIÓN DEL HOST (perfil / bancarios) — solo superadmins
──────────────────────────────────────────────────────── */
.bo-host-modal__section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}
.bo-host-modal__section-head .bo-host-modal__section-label {
  margin: 0;
}
.bo-host-edit-btn {
  border: 1px solid var(--c-border);
  background: #fff;
  color: var(--c-primary);
  font-size: .72rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background .15s, border-color .15s;
}
.bo-host-edit-btn:hover {
  background: var(--c-bg);
  border-color: var(--c-primary);
}

.bo-host-edit-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: var(--c-bg);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-sm);
  padding: 12px 14px;
  margin-bottom: 8px;
}
.bo-edit-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}
.bo-edit-row--2 {
  grid-template-columns: 1fr 1fr;
}
@media (max-width: 560px) {
  .bo-edit-row--2 { grid-template-columns: 1fr; }
}
.bo-edit-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.bo-edit-label {
  font-size: .68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: var(--c-muted);
}
.bo-edit-input {
  width: 100%;
  border: 1px solid var(--c-border);
  border-radius: var(--radius-sm);
  padding: 7px 10px;
  font-size: .82rem;
  color: var(--c-text);
  background: #fff;
  outline: none;
  transition: border-color .15s;
}
.bo-edit-input:focus {
  border-color: var(--c-primary);
}
.bo-edit-input:disabled {
  background: var(--c-bg);
  color: var(--c-muted);
  cursor: not-allowed;
}
.bo-edit-textarea {
  resize: vertical;
  min-height: 52px;
  font-family: inherit;
}
.bo-edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 2px;
}
.bo-edit-btn {
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  padding: 7px 16px;
  font-size: .8rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity .15s, background .15s;
}
.bo-edit-btn:disabled {
  opacity: .6;
  cursor: not-allowed;
}
.bo-edit-btn--ghost {
  background: #fff;
  border-color: var(--c-border);
  color: var(--c-text);
}
.bo-edit-btn--ghost:hover:not(:disabled) {
  background: var(--c-bg);
}
.bo-edit-btn--primary {
  background: var(--c-primary);
  color: #fff;
}
.bo-edit-btn--primary:hover:not(:disabled) {
  background: var(--c-secondary);
}

/* ────────────────────────────────────────────────────────
   GALLERY SLIDER
──────────────────────────────────────────────────────── */
.bo-gallery-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(0,0,0,.55);
  color: #fff;
  border: none;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  transition: background var(--transition), transform var(--transition);
  padding: 0;
}
.bo-gallery-btn:hover { background: rgba(0,0,0,.78); }
.bo-gallery-btn--prev { left: 5px; }
.bo-gallery-btn--next { right: 5px; }
.bo-gallery-btn:active { transform: translateY(-50%) scale(.9); }

.bo-gallery-counter {
  position: absolute;
  bottom: 6px;
  right: 7px;
  background: rgba(0,0,0,.55);
  color: #fff;
  font-size: .65rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 10px;
  z-index: 2;
  letter-spacing: .03em;
}

.bo-gallery-dots {
  position: absolute;
  bottom: 6px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 4px;
  z-index: 2;
}
.bo-gallery-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: rgba(255,255,255,.5);
  cursor: pointer;
  transition: background var(--transition), transform var(--transition);
}
.bo-gallery-dot--active {
  background: #fff;
  transform: scale(1.4);
}

/* Image fade transition */
.bo-img-fade-enter-active,
.bo-img-fade-leave-active { transition: opacity .22s ease; }
.bo-img-fade-enter-from,
.bo-img-fade-leave-to     { opacity: 0; }

</style>
